/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { PureComponent } from "react";
import Styled from "instructor/styles/dashboard";
import CustomDropDown from "instructor/components/dashboard-select";
import CustomDropDownMultiple from "instructor/components/dashboard-select-multiple";
import BarChart from "instructor/components/dashboard-bar-chart";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import TeamDynamicsPopup from "instructor/components/team-dynamics-popup";
import StudentStatsPopup from "instructor/components/student-stats-popup";
import { Loader } from "react-loaders";
import { getRequest, batchRequest, downloadRequest, postRequest } from "api/ApiService";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import moment from "moment";
import { CSVLink } from "react-csv";
import { Link } from "react-router";
import styled from 'styled-components';
import Auth from '../../routes/Auth';
import groupBy from 'lodash/groupBy';
import SkillsBadgePopup, { getSkillName as sharedGetSkillName } from "../../components/skills-badge/SkillsBadgePopup";
import InstructorSurveyReadyNotification from "../../components/instructor-notifications/InstructorSurveyReadyNotification";

import { isSurveyFinished } from '../../api/Surveys';
import { pickCourseAndGame } from '../../api/AppGeneralInfoService';

import ErrorAlert from '../../components/alerts/error';
import ErrorInlineAlert from '../../components/alerts/errorInline';

// Leaderboard table styles are intentionally kept exported on Styled
// for re-use once Top/Bottom Performers rows are wired to real data.
const { Engagement, Performance, TeamDynamics } = Styled;

const TEAM_GRADING_API = `${process.env.REACT_APP_API_URL}app/team-grading/`;
const SURVEY_DATA_API = `${process.env.REACT_APP_API_URL}app/survey-data/`;
const BULK_SCORING_API = `${process.env.REACT_APP_API_URL}v2/forms/surveys/%SURVEY_ID%/bulk-submission-scoring/`;
const CALC_SURVEY_SCORE_API = `${process.env.REACT_APP_API_URL}v2/forms/surveys/%SURVEY_ID%/calc-score/`;

const doNothing = e => {
  if (e) {
    e.preventDefault();
  }
};

const MODAL_TYPE_WORKED_VERY_WELL = 1;
const MODAL_TYPE_WORKED_WELL = 2;
const MODAL_TYPE_DID_NOT_WORK_WELL = 3;

// Student Spotlight → Most Valuable panel: label shown next to each
// student's name (also drives the Skills Badge popup's category pill).
// Keyed by the `category` field on the merged mostValuablePlayers rows,
// which is set by fetchMostLeastValuable when it unions the three BE
// cohorts (most_valuable / well_rounded / market_masters).
const MVP_LINK_LABEL = {
  most_valuable: 'Most Valuable',
  well_rounded: 'Well Rounded',
  market_masters: 'Market Master',
};
const MVP_BADGE_LABEL = {
  most_valuable: 'Most Valuable Teammate',
  well_rounded: 'Well Rounded Teammate',
  market_masters: 'Market Master',
};

// Review Badge Recipients popup: per-category display config.
// - accent: 4px top bar + icon fill
// - podium: rank-1/2/3 fills for the rank circle (dark to light);
//   ranks 4+ fall through to the neutral gray in the row itself.
const BADGE_CATEGORY_ORDER = ['most_valuable', 'market_masters', 'well_rounded'];
const BADGE_CATEGORY_META = {
  most_valuable: {
    label: 'Most Valuable',
    accent: '#D18A2E',
    podium: ['#8B5A2B', '#D6A059', '#E9C08A'],
  },
  market_masters: {
    label: 'Market Master',
    accent: '#3AA7AC',
    podium: ['#276C6F', '#4EBCC1', '#A3DCDF'],
  },
  well_rounded: {
    label: 'Well Rounded',
    accent: '#6E5AA6',
    podium: ['#4B3B7A', '#7B62B0', '#B49FD3'],
  },
};

// Simple ribbon medal glyph used inside the round column icon. Kept
// as a constant so the JSX below doesn't repeat the path each time.
const BADGE_MEDAL_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="10" r="5" />
    <path d="M8.21 13.89L7 22l5-3 5 3-1.21-8.11" />
  </svg>
);

const DOWNLOAD_MAX_ATTEMPTS = 10;
const DOWNLOAD_ATTEMPT_DELAY = 1000;
const DOWNLOAD_ERROR = 'The system cannot process request. Try again later.';

const teamDynamicsModalTitles = {
  [MODAL_TYPE_WORKED_VERY_WELL]: "Worked Very Well Together",
  [MODAL_TYPE_WORKED_WELL]: "Worked Well Together",
  [MODAL_TYPE_DID_NOT_WORK_WELL]: "Did Not Work Well Together"
};

const teamDynamicsField = {
  [MODAL_TYPE_WORKED_VERY_WELL]: "team_worked_well_together",
  [MODAL_TYPE_WORKED_WELL]: "team_worked_together",
  [MODAL_TYPE_DID_NOT_WORK_WELL]: "team_did_not_worked_together"
};

// Static catalog of Performance Feedback list items shown in the
// dashboard's "Performance Feedback" block. Each entry pairs an API
// feedback `type` string (or array of types for pages that listen to
// more than one) with:
//   - displayName: friendly theme label shown in the list. Multiple
//     entries may share the same displayName, in which case they are
//     consolidated into one list row (union of teams, lowest id).
//   - hoverMessage: long-form tooltip text shown on hover.
//   - path: detail page the list row links to.
//   - category: used to gate items by which round targets are active.
//
// displayName and hoverMessage may be functions of the round number
// (used by Share Price, whose label changes from "Revenue Challenges"
// in rounds 1-2 to "Profit Challenges" in round 3+).
// Hover copy for each Critical Thinking Insights group. The
// "These teams" prefix gets swapped to "X out of Y teams" at render
// time, so author the rest of the sentence to read naturally after
// that substitution.
const PERF_STMF_MSG = "These teams analyzed each decision in depth but struggled in synthesizing, making pricing, promotion, and production decisions in silos instead of seeing how they collectively impact financial targets (e.g. revenues, gross margins).";
const PERF_MDID_MSG = "These teams decided on instinct rather than evidence. The market data was available (e.g customer preferences, willingness to pay), but they didn't read or leverage it to drive their decision making.";
const PERF_RIPOR_MSG = "These teams generated revenue but couldn't turn it into profit, falling short on ROE, ROA, or EPS targets.";

const PERF_FEEDBACK_ITEMS = [
  // 1) Seeing the Trees, Missing the Forest (revenue + gross-margin
  //    failures rolled into one theme, plus the "low inventory"
  //    page which is also a symptom of siloed planning between
  //    production and revenue expectations).
  { type: "Expected revenue low",       category: "revenues",     displayName: "Seeing the Trees, Missing the Forest", hoverMessage: PERF_STMF_MSG, path: "/instructor/performance-feedback/revenues/products-selling-price" },
  { type: "Expected revenue low",       category: "revenues",     displayName: "Seeing the Trees, Missing the Forest", hoverMessage: PERF_STMF_MSG, path: "/instructor/performance-feedback/revenues/customers-willingness-to-pay" },
  { type: "Gross margin fail",          category: "grossMargins", displayName: "Seeing the Trees, Missing the Forest", hoverMessage: PERF_STMF_MSG, path: "/instructor/performance-feedback/gross-margins/selling-price-too-low" },
  { type: "Negative gross margins",     category: "grossMargins", displayName: "Seeing the Trees, Missing the Forest", hoverMessage: PERF_STMF_MSG, path: "/instructor/performance-feedback/gross-margins/running-non-profit" },
  { type: "Low inventory",              category: "marketShare",  displayName: "Seeing the Trees, Missing the Forest", hoverMessage: PERF_STMF_MSG, path: "/instructor/performance-feedback/market-share/insufficient-capacity" },

  // 2) Making Decisions in the Dark (was "Customer Preferences
  //    Overlooked").
  { type: "Price range failed",         category: "marketShare",  displayName: "Making Decisions in the Dark", hoverMessage: PERF_MDID_MSG, path: "/instructor/performance-feedback/market-share/outside-price-range" },
  { type: "Minimum requirement failed", category: "marketShare",  displayName: "Making Decisions in the Dark", hoverMessage: PERF_MDID_MSG, path: "/instructor/performance-feedback/market-share/product-customer-wanted" },

  // 3) Low Awareness (unchanged).
  { type: "Awareness",                  category: "marketShare",  displayName: "Low Awareness", hoverMessage: "These teams are underinvesting in the channels their target customers actually use.", path: "/instructor/performance-feedback/market-share/product-promotion" },

  // 4) Product Market Fit Challenges (unchanged).
  { type: "Product market fit",         category: "marketShare",  displayName: "Product Market Fit Challenges", hoverMessage: "These teams have a marketing mix that meet low demands.", path: "/instructor/performance-feedback/market-share/product-met-interest" },

  // "Decision Submission Failed" used to live here (types "Did not
  // submit" and "Insufficient capital" under category "submission")
  // but was removed because the Decision Submission Status KPI at
  // the top of the dashboard already surfaces the same information
  // with a direct link to the detail page. Keeping it on the Critical
  // Thinking list duplicated the signal.

  // 5) Revenue In, Profit Out of Reach (was "Profit Shortfalls").
  //    Shared "profitTrio" category gates the entire theme — it
  //    only appears when ANY of roe / roa / earnings_per_share is
  //    a target for the round.
  { type: "Negative profits",           category: "profitTrio",   displayName: "Revenue In, Profit Out of Reach", hoverMessage: PERF_RIPOR_MSG, path: "/instructor/performance-feedback/net-loss" },
  { type: "Roe fail",                   category: "profitTrio",   displayName: "Revenue In, Profit Out of Reach", hoverMessage: PERF_RIPOR_MSG, path: "/instructor/performance-feedback/roe/enough-profits" },
  { type: "Roa fail",                   category: "profitTrio",   displayName: "Revenue In, Profit Out of Reach", hoverMessage: PERF_RIPOR_MSG, path: "/instructor/performance-feedback/roa/enough-profits" },
  { type: "EPS fail",                   category: "profitTrio",   displayName: "Revenue In, Profit Out of Reach", hoverMessage: PERF_RIPOR_MSG, path: "/instructor/performance-feedback/eps/enough-profits" },
];

// Fixed display order for the Critical Thinking Insights list.
// Replaces the prior team-count-desc / feedback-id-asc sort.
const PERF_FEEDBACK_DISPLAY_ORDER = [
  "Seeing the Trees, Missing the Forest",
  "Making Decisions in the Dark",
  "Low Awareness",
  "Product Market Fit Challenges",
  "Revenue In, Profit Out of Reach",
];

// Per-group path override: when a group has multiple member pages,
// we want the row link to prefer the first path in this list that
// has data (teams > 0), falling back through the list in order. If
// none of the listed paths have data, the row uses the catalog's
// default path (first entry whose feedback row had teams).
const PERF_FEEDBACK_PATH_PRIORITY = {
  "Making Decisions in the Dark": [
    "/instructor/performance-feedback/market-share/product-customer-wanted",
    "/instructor/performance-feedback/market-share/outside-price-range",
  ],
};

// eslint-disable-next-line no-unused-vars
const CSVDownload = styled(CSVLink)`
  &:active,
  &:visited,
  &:link {
    font-family: 'Mukta Vaani', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 23px;
    text-decoration: underline;
    color: #fff;
  }

  &:hover {
    text-decoration: none;
  }
`;

// Badges consent tour styling. Backdrop dims everything below the
// TopBar so logout stays clickable; popup card renders on top with
// the consent message + MVP block + Approve/Decline actions.
/* Tour backdrop dims the whole page (below topbar) and blocks
   clicks — except the Student Spotlight card, which is lifted
   above the backdrop via a z-index bump so the instructor can
   click Skills Certificate links there. */
const BadgesTourBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
`;

/* Fixed centered card so the Student Spotlight column behind it
   can stay dimmed with the rest of the page (the earlier design
   lifted the card above the backdrop and anchored the popup to it;
   the review-and-approve redesign drops that lift). Body scrolls;
   footer with action buttons stays pinned. */
const BadgesTourPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(520px, calc(100vw - 40px));
  max-height: calc(100vh - 120px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  z-index: 1700;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* Review-and-approve checklist popup. Opens on top of the tour
   popup (higher z-index) with its own dimmer between the two. Sized
   ~2.5x the tour popup so the checklist has room to breathe, but
   capped to the viewport so smaller laptop screens don't overflow. */
const BadgesReviewBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1800;
`;

const BadgesReviewPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(1280px, calc(100vw - 40px));
  max-height: calc(100vh - 60px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  z-index: 1900;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BadgesReviewHeader = styled.div`
  padding: 24px 32px 16px;
  border-bottom: 1px solid #ECEDEF;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
`;

const BadgesReviewHeaderLeft = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const BadgesReviewEyebrow = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
  margin: 0 0 6px;
`;

const BadgesReviewTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 26px;
  line-height: 32px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const BadgesReviewSubtitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.7);
  margin: 8px 0 0;
  max-width: 640px;
`;

const BadgesReviewStats = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
`;

const BadgesReviewStatCard = styled.div`
  background: #F5F7FA;
  border: 1px solid #E1E6EE;
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 118px;
`;

const BadgesReviewStatValue = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 26px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
`;

const BadgesReviewStatLabel = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 2px;
`;

const BadgesReviewBody = styled.div`
  padding: 20px 32px 8px;
  overflow-y: auto;
  flex: 1 1 auto;
`;

const BadgesReviewColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
`;

// Single per-category column. Top border is a 4px accent bar in the
// category color; the rest of the card is standard. Body has its
// own scroll so the popup only scrolls when the whole card is
// taller than the viewport.
const BadgesReviewColumn = styled.div`
  background: #ffffff;
  border: 1px solid #E1E6EE;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  max-height: calc(100vh - 320px);
`;

const BadgesReviewColumnAccent = styled.div`
  height: 4px;
  background: ${p => p.color || '#E1E6EE'};
`;

const BadgesReviewColumnHeader = styled.div`
  padding: 14px 16px 12px;
`;

const BadgesReviewColumnHeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BadgesReviewColumnIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.color || '#E1E6EE'};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const BadgesReviewColumnTitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const BadgesReviewColumnTopPct = styled.span`
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.55);
  margin-left: 8px;
`;

const BadgesReviewColumnClearAll = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: #3A507B;
  cursor: pointer;
  padding: 4px 6px;

  &:hover {
    text-decoration: underline;
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    color: rgba(0, 27, 59, 0.35);
    cursor: default;
    text-decoration: none;
  }
`;

const BadgesReviewColumnSubtitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 6px;
  padding-left: 42px;
`;

const BadgesReviewColumnTableHeader = styled.div`
  display: grid;
  grid-template-columns: 92px 56px 1fr;
  gap: 8px;
  padding: 8px 16px;
  background: #F5F7FA;
  border-top: 1px solid #E1E6EE;
  border-bottom: 1px solid #E1E6EE;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
`;

const BadgesReviewColumnScroll = styled.div`
  overflow-y: auto;
  flex: 1 1 auto;
`;

const BadgesReviewNewRow = styled.label`
  display: grid;
  grid-template-columns: 92px 56px 1fr;
  gap: 8px;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #F1F3F6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #F5F7FA;
  }
`;

const BadgesReviewRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BadgesReviewCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
`;

const BadgesReviewRankCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${p => p.color || '#DDE1E8'};
  color: ${p => p.textColor || 'rgba(0, 27, 59, 0.7)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
`;

const BadgesReviewName = styled.a`
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
  text-decoration: none;
  cursor: pointer;
  min-width: 0;

  &:hover {
    color: #3A507B;
    text-decoration: underline;
  }
`;

const BadgesReviewColumnEmpty = styled.div`
  padding: 24px 16px;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.55);
  text-align: center;
`;

const BadgesReviewFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #ECEDEF;
  background: #ffffff;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
`;

const BadgesReviewFooterLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const BadgesReviewCount = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
`;

const BadgesReviewFooterNote = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.55);
  margin-top: 2px;
`;

const BadgesReviewActions = styled.div`
  display: flex;
  gap: 12px;
`;

const BadgesReviewBackButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 10px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover {
    background: #f5f6f7;
  }

  &:disabled {
    cursor: default;
    color: rgba(0, 27, 59, 0.4);
  }
`;

const BadgesReviewConfirmButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 22px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: #248339;
    border-color: #248339;
  }

  &:disabled {
    background: #a4d9b1;
    border-color: #a4d9b1;
    cursor: default;
  }
`;

const BadgesTourHeader = styled.div`
  padding: 24px 32px 12px;
  border-bottom: 1px solid #ECEDEF;
`;

const BadgesTourBody = styled.div`
  padding: 20px 32px;
  overflow-y: auto;
  flex: 1 1 auto;
`;

const BadgesTourFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #ECEDEF;
  background: #ffffff;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
`;

const BadgesTourClose = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: rgba(0, 27, 59, 0.6);
  padding: 4px 8px;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    cursor: default;
    color: rgba(0, 27, 59, 0.3);
  }
`;

const BadgesTourTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 16px;
`;

const BadgesTourConsentText = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  white-space: pre-wrap;
  margin: 0 0 20px;
`;

/* Visually differentiates the sample-email portion of the consent
   message (starts at "Subject:") from the note written for the
   instructor above it. Light grey block with subtle border. */
const BadgesTourEmailBlock = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  white-space: pre-wrap;
  background: #F5F6F7;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 16px 0 0;
`;

/* "View the email message" — inline link inside the instructor
   note. Click opens the email preview modal. */
const BadgesTourInlineLink = styled.a`
  color: #2DA548;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #248339;
  }
`;

/* Email preview modal — nested on top of the tour overlay, shows
   the sample email body verbatim. */
const BadgesEmailPreviewBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const BadgesEmailPreviewCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 120px);
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BadgesEmailPreviewHeader = styled.div`
  padding: 20px 28px 12px;
  border-bottom: 1px solid #ECEDEF;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BadgesEmailPreviewTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const BadgesEmailPreviewCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: rgba(0, 27, 59, 0.6);
  padding: 4px 8px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const BadgesEmailPreviewBody = styled.div`
  padding: 20px 28px 28px;
  overflow-y: auto;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  white-space: pre-wrap;
`;

const BadgesTourMvpSection = styled.div`
  background: #FAFBFD;
  border: 1px solid #d5d9dd;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 0 0 20px;
`;

const BadgesTourMvpHeader = styled.h3`
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const BadgesTourMvpRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  font-family: 'Mukta Vaani';
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const BadgesTourMvpRank = styled.span`
  font-weight: 600;
  min-width: 24px;
`;

const BadgesTourCertificateLink = styled.a`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #3A507B;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 8px;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const BadgesTourActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const BadgesTourDeclineButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 10px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover {
    background: #f5f6f7;
  }

  &:disabled {
    cursor: default;
    color: rgba(0, 27, 59, 0.4);
  }
`;

const BadgesTourApproveButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #248339;
    border-color: #248339;
  }

  &:disabled {
    background: #a4d9b1;
    border-color: #a4d9b1;
    cursor: default;
  }
`;

const BadgesTourError = styled.div`
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: #d34c4c;
  margin: 0 0 12px;
`;

const BadgesTourLoader = styled.div`
  font-family: 'Mukta Vaani';
  font-size: 16px;
  color: rgba(0, 27, 59, 0.6);
  padding: 24px 0;
  text-align: center;
`;

const BadgesTourNotSureButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 10px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;
  margin-right: auto;

  &:hover {
    background: #f5f6f7;
  }

  &:disabled {
    cursor: default;
    color: rgba(0, 27, 59, 0.4);
  }
`;

/* Intro popup — auto-appears when badge gates pass. Only exit is
   the Continue button (opens tour) or logging out via the topbar. */

const BadgesIntroBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const BadgesIntroCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 520px;
  padding: 32px 36px;
  text-align: center;
`;

const BadgesIntroTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const BadgesIntroBody = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 24px;
`;

const BadgesIntroContinueButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 28px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #248339;
    border-color: #248339;
  }
`;

/* Student Count KPI subtext + popup. Small link under the big
   student count renders "N students not registered yet" when count
   > 0. Clicking opens the modal with the list + copy-all. */
const NotRegisteredLink = styled.button`
  background: none;
  border: 0;
  padding: 4px 0 0;
  margin: 0;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #5C91F5;
  cursor: pointer;
  text-align: left;

  &:hover { text-decoration: underline; }
  &:disabled {
    color: rgba(0, 27, 59, 0.4);
    cursor: default;
    text-decoration: none;
  }
`;

const NotRegisteredBackdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const NotRegisteredCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const NotRegisteredHeader = styled.div`
  padding: 20px 24px 12px;
  border-bottom: 1px solid #ECEDEF;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const NotRegisteredHeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const NotRegisteredTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 4px;
`;

const NotRegisteredSubtitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
`;

const NotRegisteredClose = styled.button`
  background: none;
  border: 0;
  font-size: 24px;
  line-height: 1;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 2px 6px;
  flex: none;

  &:hover { color: rgba(0, 27, 59, 0.94); }
`;

const NotRegisteredActions = styled.div`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #ECEDEF;
`;

const NotRegisteredCopyButton = styled.button`
  background: #3A507B;
  border: 1px solid #3A507B;
  border-radius: 4px;
  padding: 8px 18px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
  cursor: pointer;

  &:hover { background: #2a3f66; border-color: #2a3f66; }
  &:disabled { background: #a4b2cd; border-color: #a4b2cd; cursor: default; }
`;

const NotRegisteredCopiedBadge = styled.span`
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 13px;
  color: #2DA548;
`;

const NotRegisteredList = styled.div`
  overflow-y: auto;
  flex: 1 1 auto;
`;

const NotRegisteredRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid #F1F3F6;
  font-family: 'Mukta Vaani';
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.85);

  &:last-child { border-bottom: none; }
`;

const NotRegisteredName = styled.span`
  font-weight: 600;
  color: rgba(0, 27, 59, 0.94);
`;

const NotRegisteredEmail = styled.span`
  color: rgba(0, 27, 59, 0.65);
  word-break: break-all;
`;

const NotRegisteredEmpty = styled.div`
  padding: 24px;
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
  text-align: center;
`;

// ----- Decision Submission Status popup styled components -----
// Replaces the earlier single-column list layout with a 2-column
// grid of team cards wrapped in a header/banner/filter/footer frame.

const SubStatusCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SubStatusHeader = styled.div`
  padding: 22px 28px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const SubStatusHeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const SubStatusEyebrow = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
  margin: 0 0 4px;
`;

const SubStatusTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 24px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const SubStatusClose = styled.button`
  background: none;
  border: 0;
  font-size: 24px;
  line-height: 1;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 2px 6px;
  flex: none;

  &:hover { color: rgba(0, 27, 59, 0.94); }
`;

const SubStatusBanner = styled.div`
  margin: 0 24px 16px;
  padding: 16px 20px;
  background: #EDF4FF;
  border: 1px solid #D7E5F9;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const SubStatusBannerIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 27, 59, 0.94);
  color: #ffffff;
  display: grid;
  place-items: center;
  flex: none;

  svg { width: 20px; height: 20px; }
`;

const SubStatusBannerText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const SubStatusBannerTitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const SubStatusBannerSubtitle = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.65);
  margin-top: 2px;
`;

const SubStatusFilterWrap = styled.div`
  margin: 0 24px 12px;
  position: relative;
`;

const SubStatusFilterIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: rgba(0, 27, 59, 0.45);
  pointer-events: none;
  display: inline-flex;
  svg { width: 16px; height: 16px; }
`;

const SubStatusFilterInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px 10px 38px;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  background: #ffffff;

  &::placeholder { color: rgba(0, 27, 59, 0.4); }
  &:focus { outline: none; border-color: #55B4BC; }
`;

const SubStatusGrid = styled.div`
  padding: 0 24px 12px;
  overflow-y: auto;
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
`;

const SubStatusTeamCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #E1E6EE;
  border-radius: 10px;
`;

const SubStatusTeamBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #EEF2F8;
  color: rgba(0, 27, 59, 0.78);
  display: grid;
  place-items: center;
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  flex: none;
`;

const SubStatusTeamName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.94);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubStatusTeamPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EDF4FF;
  border-radius: 999px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #3A507B;
  flex: none;
`;

const SubStatusTeamPillDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5C91F5;
  flex: none;
`;

const SubStatusEmpty = styled.div`
  grid-column: 1 / -1;
  padding: 24px;
  text-align: center;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
`;

const SubStatusFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #ECEDEF;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SubStatusShowing = styled.div`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
`;

const SubStatusCloseButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 6px;
  padding: 8px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover { background: #f5f6f7; }
`;

/* "Share With Students" button — sits on the right side of the
   Most Valuable Teammate header row. Active (clickable, green
   underline) when the instructor hasn't confirmed; inactive
   (grey, no click) after "Badges Sent". Font size / weight
   match the SpotlightSubheader ("Most Valuable Teammate"). */

const ShareWithStudentsButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: #ffffff;
  text-transform: uppercase;
  line-height: 16px;

  &:hover {
    background: #248339;
    border-color: #248339;
  }

  &:disabled {
    cursor: default;
    background: #ffffff;
    border-color: #D5D9DD;
    color: rgba(0, 27, 59, 0.48);
  }
`;

class Dashboard extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      perfFeedbackSearch: "",
      perfFeedbackSearchSubmitted: "",
      perfFeedbackActiveIndex: null,
      collabInsightsSearch: "",
      performanceFeedbacksGame: [],
      gamesTargets: [],
      mostValuablePlayers: [],
      leastValuablePlayers: [],
      mostLeastValuableLoading: false,
      collaborationStrengths: [],
      collaborationWeaknesses: [],
      collaborationILikeTotal: 0,
      collaborationIWishTotal: 0,
      collaborationSkillsLoading: false,
      collaborationSkillsTab: "strengths",
      collabPopupOpen: false,
      // Skills Certificate popup
      certificateOpen: false,
      certificateUserId: null,
      certificateStudentName: '',
      // Which cohort the student came from — drives the pill label
      // ("Most Valuable Teammate" / "Well Rounded Teammate" /
      // "Market Master") in SkillsBadgePopup. Nullable; null falls
      // back to the default label.
      certificateCategory: null,
      certificateData: null,
      certificateLoading: false,
      certificateError: null,
      // Areas of Improvement (Insights) popup
      insightsOpen: false,
      insightsUserId: null,
      insightsStudentName: '',
      insightsData: null,
      insightsLoading: false,
      insightsError: null,
      // Student Spotlight: gate the lists on whether per-survey
      // insights have been generated. null = haven't checked yet,
      // true/false = check completed.
      studentSpotlightHasInsights: null,
      studentSpotlightCheckLoading: false,
      studentSpotlightGenerating: false,
      studentSpotlightGenerateMessage: null,
      studentSpotlightConfirmOpen: false,
      // Student Spotlight team filter: multi-select by team_name.
      // Empty array = show every team in the (section-scoped) response.
      spotlightTeamFilter: [],
      spotlightTeamFilterOpen: false,
      // Insights job polling — populated after POST /insights/generate/
      // returns 202. jobId is stored for debugging; statusUrl is the
      // authoritative pointer used for polling.
      insightsJobId: null,
      insightsStatusUrl: null,
      // Instructor's per-survey badge consent state, from
      // GET /badges/survey/<survey_id>/status/ → my_instructor_state.
      // "pending" | "confirmed" | "declined" | null (not yet fetched).
      // Only meaningful for final-stage surveys.
      myInstructorBadgeState: null,
      // Intro popup ("skills insights are ready to review") auto-appears
      // when all badge gates pass. Continue → tour. Not Sure Yet in the
      // tour flips this flag to suppress the intro popup for the rest
      // of the session (until a refresh/navigation resets state).
      badgesIntroSuppressed: false,
      // Badges consent tour overlay — dims the page except topbar +
      // Student Spotlight MVP block, shows the consent message with
      // Approve / Decline / Not Sure Yet. Opened by the intro popup's
      // Continue button.
      badgesTourOpen: false,
      // Nested modal that shows the sample email that would be sent
      // to students. Opened from a link inside the tour popup body.
      badgesEmailPreviewOpen: false,
      // Sample email body from GET /insights/class/<c>/<s>/
      // (`badges_student_email_preview`). Populated alongside the
      // consent message in openBadgesTour.
      badgesStudentEmailPreview: null,
      badgesConsentMessage: null,
      badgesConsentLoading: false,
      badgesBatchSubmitting: false,
      badgesActionError: null,
      // Class-wide badge recipient count (unique users across
      // most_valuable + well_rounded + market_masters) across every
      // section. The Review and Issue consent text shows class-wide
      // numbers so an instructor who only covers one section still
      // sees the student's placement in the entire course. Fetched
      // alongside the consent message in openBadgesTour.
      classWideBadgeRecipientCount: null,
      // Nested checklist popup opened from the tour popup's primary
      // button. Lists every qualifying student with a checkbox so the
      // instructor can un-select any before POSTing to batch-confirm.
      // badgesSelectedIds is a Set of user_ids; initialized to the
      // full mostValuablePlayers set the moment the popup opens.
      badgesReviewOpen: false,
      badgesSelectedIds: null,
      // Team Lookup modal (KPI row card + ⌘K shortcut)
      teamLookupOpen: false,
      teamLookupStep: 1,
      teamLookupQuery: "",
      teamLookupHighlightIdx: 0,
      teamLookupSelectedTeam: null,
      teamLookupRows: [],
      teamLookupLoading: false,
      teamLookupError: null,
      teamLookupCacheRoundId: null,
      teamLookupCacheTime: 0,
      courses: null,
      games: null,
      rounds: null,
      currentGame: 0,
      currentCourse: 0,
      currentRound: 0,
      setRoundValue: null,
      averageTeamGrade: 0,
      medianTeamGrade: 0,
      standardDeviation: 0,
      teams: null,
      surveys: null,
      allSurveys: null,
      surveysError: null,
      currentSurvey: 0,
      setSurveyValue: null,
      teamScore: null,
      showTeamDynamicsModal: false,
      teamDynamicsModalTitle: "",
      teamDynamicsTeamsList: [],
      teamDynamicsCurrentTeam: 0,
      teamDynamicsHideDropdowns: false,
      players: null,
      teamDynamicsCurrentPlayer: 0,
      setPlayerValue: null,
      reports: {},
      userScoreReports: {},
      countOfCourseMembers: 0,
      // "Not registered yet" affordance under the Student Count KPI
      // tile. count/list are populated from GET /v2/games/coursemembers/
      // ?not-registered&course=<id>. Popup opens when the instructor
      // clicks the small subtext link, so they can email the students
      // who haven't signed up yet.
      notRegisteredCount: 0,
      notRegisteredList: [],
      notRegisteredLoading: false,
      notRegisteredPopupOpen: false,
      notRegisteredCopied: false,
      // Team Decisions Status KPI. count/teamIds come from GET
      // /v2/games/games/<gameId>/rounds/<roundId>/
      // team-decisions-not-submitted/ with teams whose only missing
      // item is "negative_cash" filtered out (BE flags over-aggressive
      // submissions but the instructor doesn't need to chase cash-
      // only cases). Popup renders team names resolved via state.teams.
      pendingDecisionsCount: 0,
      pendingDecisionsTeamIds: [],
      pendingDecisionsLoading: false,
      pendingDecisionsPopupOpen: false,
      // Filter typed into the popup's search box. Reset when the
      // popup opens so each session starts clean.
      pendingDecisionsFilter: '',
      leaderboard: [],
      leaderboardLoading: true,
      leaderboardFileData: [],
      leaderboardFileDownloading: false,
      sortKey: "classRanking",
      sortOrder: "asc",
      isReportsLoading: false,
      isPlayersLoading: false,
      competitions: null,
      showStudentStats: false,
      studentStatsLoading: false,
      engagementDownloadRequest: null,
      engagementDownloadCount: 0,
      engagementDownloadFile: null,
      teamDynamicsDownloadRequest: null,
      teamDynamicsDownloadCount: 0,
      teamDynamicsDownloadFile: null,
      performanceDownloadRequest: null,
      performanceDownloadCount: 0,
      performanceDownloadFile: null,
      showPerformanceDowload: true,
      showError: false,
      hasUserScores: false,
      showStudentStatError: false,
      processResultsFailed: false
    };
  }

  downloadPerformanceFile = () => {
    const { performanceDownloadFile } = this.state;

    downloadRequest(performanceDownloadFile, 'application/csv')
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `performance_${this.currentDate}.csv`;
        link.click();
      })
      .catch(err => {
        console.error(err);
        alert(DOWNLOAD_ERROR);
      });
  }

  downloadPerformanceData = e => {
    const {
      performanceDownloadRequest,
      currentRound,
      performanceDownloadCount,
      performanceDownloadFile,
      rounds,
      currentGame,
      currentSurvey
    } = this.state;

    if (e) {
      e.preventDefault();
    }

    const selectedRound = rounds.find(round => round.id === currentRound);
    if (!selectedRound.is_finished) {
      this.setState({
        showError: true
      });
      return;
    }

    if (performanceDownloadFile) {
      this.downloadPerformanceFile();
      return;
    }

    if (!performanceDownloadRequest) {
      postRequest(`${process.env.REACT_APP_API_URL}v2/games/usergrade/run/`)
        .send({
          game: currentGame,
          ...(currentSurvey && { survey: currentSurvey })
        })
        .type('form')
        .then(res => {
          if (res.statusCode === 200 || res.statusCode === 201) {
            postRequest(`${process.env.REACT_APP_API_URL}v2/reports/performance-csv-add/`)
              .send({
                round: currentRound,
                ...(currentSurvey ? { survey: currentSurvey } : {})
              })
              .type('form')
              .then(res => {
                const { download_file } = res.body;
                const performanceDownloadRequest = download_file.substring(5);

                this.setState({
                  performanceDownloadRequest,
                  performanceDownloadCount: 0,
                  performanceDownloadFile: null,
                });
              })
              .catch(err => {
                console.error(err);
                alert(DOWNLOAD_ERROR);
              });
          }
        })
        .catch(err => {
          console.error(err);
        });
    } else {
      if (performanceDownloadCount === DOWNLOAD_MAX_ATTEMPTS) {
        alert(DOWNLOAD_ERROR);

        this.setState({
          performanceDownloadCount: 0,
        });
        return;
      }

      if (this._pollingPerformance) return;
      this._pollingPerformance = true;

      getRequest(`${process.env.REACT_APP_API_URL}${performanceDownloadRequest}`)
        .then(res => {
          this._pollingPerformance = false;
          const { status, download_link } = res.body;

          this.setState({
            performanceDownloadCount: performanceDownloadCount + 1,
          });

          if (status !== 'Completed') {
            setTimeout(this.downloadPerformanceData, DOWNLOAD_ATTEMPT_DELAY);
          } else {
            this.setState({
              performanceDownloadFile: download_link,
              performanceDownloadCount: 0,
            }, () => {
              this.downloadPerformanceFile();
            });
          }
        })
        .catch(err => {
          console.error(err);
          alert(DOWNLOAD_ERROR);
        });
    }
  }

  downloadTeamDynamicsFile = () => {
    const { teamDynamicsDownloadFile } = this.state;

    downloadRequest(teamDynamicsDownloadFile, 'application/csv')
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `team_dynamics_${this.currentDate}.csv`;
        link.click();
      })
      .catch(err => {
        console.error(err);
        alert(DOWNLOAD_ERROR);
      });
  }

  downloadTeamDynamicsData = e => {
    const {
      teamDynamicsDownloadRequest,
      currentSurvey,
      teamDynamicsDownloadCount,
      teamDynamicsDownloadFile,
    } = this.state;

    if (e) {
      e.preventDefault();
    }

    if (teamDynamicsDownloadFile) {
      this.downloadTeamDynamicsFile();
      return;
    }

    if (!teamDynamicsDownloadRequest) {
      getRequest(`${process.env.REACT_APP_API_URL}v2/forms/add-export-answers-to-csv-task/${currentSurvey}/`)
        .then(res => {
          const { download_file } = res.body;
          const teamDynamicsDownloadRequest = download_file.substring(5);

          this.setState({
            teamDynamicsDownloadRequest,
            teamDynamicsDownloadCount: 0,
            teamDynamicsDownloadFile: null,
          });
        })
        .catch(err => {
          console.error(err);
          alert(DOWNLOAD_ERROR);
        });
    } else {
      if (teamDynamicsDownloadCount === DOWNLOAD_MAX_ATTEMPTS) {
        alert(DOWNLOAD_ERROR);

        this.setState({
          teamDynamicsDownloadCount: 0,
        });
        return;
      }

      if (this._pollingTeamDynamics) return;
      this._pollingTeamDynamics = true;

      getRequest(`${process.env.REACT_APP_API_URL}${teamDynamicsDownloadRequest}`)
        .then(res => {
          this._pollingTeamDynamics = false;
          const { status, download_link } = res.body;

          this.setState({
            teamDynamicsDownloadCount: teamDynamicsDownloadCount + 1,
          });

          if (status !== 'Completed') {
            setTimeout(this.downloadTeamDynamicsData, DOWNLOAD_ATTEMPT_DELAY);
          } else {
            this.setState({
              teamDynamicsDownloadFile: download_link,
              teamDynamicsDownloadCount: 0,
            }, () => {
              this.downloadTeamDynamicsFile();
            });
          }
        })
        .catch(err => {
          console.error(err);
          alert(DOWNLOAD_ERROR);
        });
    }
  }

  downloadEngagementFile = () => {
    const { engagementDownloadFile } = this.state;

    downloadRequest(engagementDownloadFile, 'application/csv')
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `engagement_${this.currentDate}.csv`;
        link.click();
      })
      .catch(err => {
        console.error(err);
        alert(DOWNLOAD_ERROR);
      });
  }

  downloadEngagementData = e => {
    const {
      engagementDownloadRequest,
      currentRound,
      engagementDownloadCount,
      engagementDownloadFile,
    } = this.state;

    if (e) {
      e.preventDefault();
    }

    if (engagementDownloadFile) {
      this.downloadEngagementFile();
      return;
    }

    if (!engagementDownloadRequest) {
      getRequest(`${process.env.REACT_APP_API_URL}v2/engagement/add-export-user-scores-to-csv-task/${currentRound}/`)
        .then(res => {
          const { download_file } = res.body;
          const engagementDownloadRequest = download_file.substring(5);

          this.setState({
            engagementDownloadRequest,
            engagementDownloadCount: 0,
          });
        })
        .catch(err => {
          console.error(err);
          alert(DOWNLOAD_ERROR);
        });
    } else {
      if (engagementDownloadCount === DOWNLOAD_MAX_ATTEMPTS) {
        alert(DOWNLOAD_ERROR);

        this.setState({
          engagementDownloadCount: 0,
        });
        return;
      }

      if (this._pollingEngagement) return;
      this._pollingEngagement = true;

      getRequest(`${process.env.REACT_APP_API_URL}${engagementDownloadRequest}`)
        .then(res => {
          this._pollingEngagement = false;
          const { status, download_link } = res.body;

          this.setState({
            engagementDownloadCount: engagementDownloadCount + 1,
          });

          if (status !== 'Completed') {
            setTimeout(this.downloadEngagementData, DOWNLOAD_ATTEMPT_DELAY);
          } else {
            this.setState({
              engagementDownloadFile: download_link,
              engagementDownloadCount: 0,
            }, () => {
              this.downloadEngagementFile();
            });
          }
        })
        .catch(err => {
          console.error(err);
          alert(DOWNLOAD_ERROR);
        });
    }
  }

  processUserScores = () => {
    const { currentSurvey } = this.state;

    if (currentSurvey > 0) {
      postRequest(BULK_SCORING_API.replace("%SURVEY_ID%", currentSurvey))
        .then(res => {
          console.log(res.body);
        })
        .catch(err => { });
      postRequest(CALC_SURVEY_SCORE_API.replace("%SURVEY_ID%", currentSurvey))
        .then(res => {
          this.fetchTeamScore();
          this.fetchUserScore();
        })
        .catch(err => { });
    }
  }

  showStudentStats = () => {
    this.setState({
      showStudentStats: true,
      studentStatsLoading: true,
    });
  }

  hideStudentStats = () => {
    this.setState({
      showStudentStats: false,
    });
  }

  setStudentsStatsLoading = (value) => {
    this.setState({
      studentStatsLoading: value,
    });
  }

  selectCourse = () => {
    const { courses, games, currentGame } = this.state;
    const game = games.find(i => i.id === currentGame);

    if (game) {
      const { course: currentCourse } = game;
      const course = courses.find(i => i.id === currentCourse);

      if (course) {
        this.setState({ currentCourse: course.id });
      }
    }
  };

  showTeamDynamicsModal = (type) => (e) => {
    e.preventDefault();
    const { teamScore, teams } = this.state;

    this.setState({
      showTeamDynamicsModal: true,
      teamDynamicsModalTitle: teamDynamicsModalTitles[type],
      teamDynamicsTeamsList: teamScore
        ? teams.filter(team =>
          get(teamScore.collaboration, teamDynamicsField[type], []).includes(
            team.id
          )
        )
        : []
    });
  };

  hideTeamDynamicsModal = () => {
    this.setState({
      showTeamDynamicsModal: false,
      teamDynamicsModalTitle: "",
      teamDynamicsTeamsList: [],
      teamDynamicsCurrentTeam: 0,
      teamDynamicsHideDropdowns: false,
      players: null,
      teamDynamicsCurrentPlayer: 0,
      setPlayerValue: null,
      reports: {},
      userScoreReports: {},
    });
  };

  componentDidMount() {
    // Gate on props.game being populated (set by App.js once
    // general-info responds), not on rounds.length — a brand-new
    // course with zero rounds still needs the dashboard to load.
    if (this.props.game && !this._hasFetched) {
      this.fetchData();
    }

    // Global ⌘K (Mac) / Ctrl+K (Win) opens the Team Lookup modal.
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", this.handleGlobalKeydown);
      // Close the Student Spotlight team filter when the instructor
      // clicks anywhere outside the dropdown. Listener runs for the
      // whole page lifetime but is a cheap no-op when the dropdown
      // isn't open.
      document.addEventListener("mousedown", this.handleSpotlightFilterOutsideClick);
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", this.handleGlobalKeydown);
      document.removeEventListener("mousedown", this.handleSpotlightFilterOutsideClick);
    }
    if (this._collabCloseTimer) {
      clearTimeout(this._collabCloseTimer);
      this._collabCloseTimer = null;
    }
    this._stopInsightsPoll();
  }

  // Click-outside handler for the Student Spotlight filter dropdown.
  handleSpotlightFilterOutsideClick = (e) => {
    if (!this.state.spotlightTeamFilterOpen) return;
    const el = this._spotlightFilterRef;
    if (el && e.target && !el.contains(e.target)) {
      this.setState({ spotlightTeamFilterOpen: false });
    }
  };

  setSpotlightFilterRef = (el) => {
    this._spotlightFilterRef = el;
  };

  // Student Spotlight filter + presentation helpers. All derive from
  // the already-fetched mostValuablePlayers / leastValuablePlayers
  // state, which is section-scoped by fetchMostLeastValuable.
  trimSpotlightName = (first, last) => {
    const composed = `${first || ''} ${last || ''}`.trim();
    if (!composed) return '';
    const tokens = composed.split(/\s+/).filter(Boolean);
    if (tokens.length >= 3) {
      return `${tokens[0]} ${tokens[tokens.length - 1]}`;
    }
    return composed;
  };

  spotlightTeamBadge = (name) => {
    const str = String(name || '').trim();
    if (!str) return '—';
    // Show the full team name on the badge. Team codes in this app
    // follow a <section><number> convention (e.g. "AA02", "FA06"), so
    // showing the full code lets instructors tell AA02 and AA03 apart.
    // Longer names get CSS-truncated inside the pill with the full
    // value available via the PerformersCell's hover title.
    return str.toUpperCase();
  };

  // Natural sort by team_name ascending, so "Team 2" lands before
  // "Team 10". Rows without a team_name fall to the bottom.
  sortSpotlightByTeam = (list) => {
    const arr = Array.isArray(list) ? list.slice() : [];
    arr.sort((a, b) => {
      const an = (a && a.team_name) || '';
      const bn = (b && b.team_name) || '';
      if (!an && bn) return 1;
      if (an && !bn) return -1;
      return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
    });
    return arr;
  };

  filterSpotlightByTeam = (list) => {
    const selected = this.state.spotlightTeamFilter || [];
    if (selected.length === 0) return list;
    const set = new Set(selected);
    return (list || []).filter(p => set.has((p && p.team_name) || ''));
  };

  // Unique team names in the current MVP + LVP data, sorted naturally.
  // Drives the filter dropdown options.
  spotlightTeamOptions = () => {
    const mvps = this.state.mostValuablePlayers || [];
    const lvps = this.state.leastValuablePlayers || [];
    const names = new Set();
    [...mvps, ...lvps].forEach(p => {
      if (p && p.team_name) names.add(p.team_name);
    });
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  toggleSpotlightTeamFilter = (teamName) => {
    const current = this.state.spotlightTeamFilter || [];
    const set = new Set(current);
    if (set.has(teamName)) set.delete(teamName);
    else set.add(teamName);
    this.setState({ spotlightTeamFilter: Array.from(set) });
  };

  clearSpotlightTeamFilter = () => {
    this.setState({ spotlightTeamFilter: [] });
  };

  toggleSpotlightTeamFilterDropdown = () => {
    this.setState(prev => ({
      spotlightTeamFilterOpen: !prev.spotlightTeamFilterOpen,
    }));
  };

  renderSpotlightTeamFilter = () => {
    // Only render when there's something to filter — hide the control
    // in the empty / needs-generation states so the header stays clean.
    const options = this.spotlightTeamOptions();
    if (options.length === 0) return null;

    const selected = this.state.spotlightTeamFilter || [];
    const open = this.state.spotlightTeamFilterOpen;
    const selectedSet = new Set(selected);

    let label;
    if (selected.length === 0) label = 'Filter by team';
    else if (selected.length === 1) label = selected[0];
    else label = `${selected.length} teams selected`;

    return (
      <Styled.SpotlightFilter.Wrap innerRef={this.setSpotlightFilterRef}>
        <Styled.SpotlightFilter.Button
          type="button"
          hasSelection={selected.length > 0}
          onClick={this.toggleSpotlightTeamFilterDropdown}
        >
          {label}
          <Styled.SpotlightFilter.Chevron />
        </Styled.SpotlightFilter.Button>
        {open && (
          <Styled.SpotlightFilter.Dropdown>
            {options.map(name => (
              <Styled.SpotlightFilter.Option key={name}>
                <input
                  type="checkbox"
                  checked={selectedSet.has(name)}
                  onChange={() => this.toggleSpotlightTeamFilter(name)}
                />
                {name}
              </Styled.SpotlightFilter.Option>
            ))}
            <Styled.SpotlightFilter.Footer>
              <Styled.SpotlightFilter.Clear
                type="button"
                disabled={selected.length === 0}
                onClick={this.clearSpotlightTeamFilter}
              >
                Clear
              </Styled.SpotlightFilter.Clear>
            </Styled.SpotlightFilter.Footer>
          </Styled.SpotlightFilter.Dropdown>
        )}
      </Styled.SpotlightFilter.Wrap>
    );
  };

  // Collaboration Insights hover popup. The popup card is fixed and
  // centered, so it can overlap the chart area on entry — that flips
  // the cursor target from ChartHoverArea to PopupCard mid-motion and
  // would fire mouseleave on the chart, snapping the popup shut. We
  // debounce the close and let the popup card cancel it on enter so
  // motion between the two zones is treated as a single hover.
  openCollabPopup = () => {
    if (this._collabCloseTimer) {
      clearTimeout(this._collabCloseTimer);
      this._collabCloseTimer = null;
    }
    if (!this.state.collabPopupOpen) {
      this.setState({ collabPopupOpen: true });
    }
  };

  scheduleCollabPopupClose = () => {
    if (this._collabCloseTimer) clearTimeout(this._collabCloseTimer);
    this._collabCloseTimer = setTimeout(() => {
      this._collabCloseTimer = null;
      this.setState({ collabPopupOpen: false });
    }, 150);
  };

  handleGlobalKeydown = e => {
    if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      this.openTeamLookup();
    }
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.game && this.props.game && !this._hasFetched) {
      this.fetchData();
      return;
    }

    // React to the global Year selector (LeftBar) — it pushes the
    // chosen round number into the URL as ?round=N, which App.js
    // forwards down as props.roundInUrl. Without this hook the
    // dashboard would stay on the latest finished round forever and
    // every per-round fetch (targets, performancefeedbacks, leaderboard,
    // engagement) would keep firing for the wrong round.
    const prevRoundInUrl = Number(prevProps.roundInUrl) || 0;
    const nextRoundInUrl = Number(this.props.roundInUrl) || 0;
    if (nextRoundInUrl > 0 && nextRoundInUrl !== prevRoundInUrl) {
      const currentRoundNumber = this.roundNumber;
      if (nextRoundInUrl !== currentRoundNumber) {
        this.selectRound(`Round ${nextRoundInUrl}`);
      }
    }

    // Decision Submission Status: refetch when the active round or
    // game changes. Covers the case where App.js resolves game +
    // currentRound after this component mounts, so the selectGame
    // chain missed firing with both values available.
    const prevGameId = prevProps.game && Number(prevProps.game.id);
    const nextGameId = this.props.game && Number(this.props.game.id);
    const prevCurrentRound = Number(prevProps.currentRound) || 0;
    const nextCurrentRound = Number(this.props.currentRound) || 0;
    const gameChanged = nextGameId && nextGameId !== prevGameId;
    const roundChanged = nextCurrentRound && nextCurrentRound !== prevCurrentRound;
    if (nextGameId && nextCurrentRound && (gameChanged || roundChanged)) {
      this.fetchPendingDecisions();
    }
  }

  fetchData = () => {
    this._hasFetched = true;
    // Use data from general-info (passed via props) instead of separate API calls
    const teams = this.props.teams || [];
    const courses = this.props.courses || [];
    const rounds = this.props.rounds || [];
    const games = this.props.games || [];
    const courseMembers = this.props.courseMembers || [];

    const ids = courses.map(i => i.id);
    const gamesFiltered = games.filter(i => ids.includes(i.course));
    // Student Count KPI: scope to the CURRENT course only, not sum
    // across every course the instructor is tied to. Otherwise a
    // multi-course instructor sees students from all their courses
    // combined instead of the course they're actively viewing.
    //
    // Section-scoped instructors (covers_all_sections === false) get
    // the section_student_count field instead of student_count, so the
    // Class Roster card reflects just their section(s). Lead instructors
    // (true) and legacy payloads (undefined) keep summing student_count.
    //
    // Fallback: when the course has NO sections at all (user.sections
    // empty), use student_count regardless of covers_all_sections.
    // Otherwise a misconfigured instructor flagged
    // covers_all_sections=false on a sectionless course would read
    // section_student_count (always 0) and the tile would blank out.
    const currentCourseId = Number(Auth.getCourseId());
    const scopedRows = courseMembers
      .filter(cm => Number(cm.course_id != null ? cm.course_id : cm.course) === currentCourseId);
    const authUserForRoster = Auth.getUser() || {};
    const hasAnySections = Array.isArray(authUserForRoster.sections)
      && authUserForRoster.sections.length > 0;
    const useSectionCount = hasAnySections
      && scopedRows.some(cm => cm && cm.covers_all_sections === false);
    const studentField = useSectionCount ? 'section_student_count' : 'student_count';
    const countOfCourseMembers = scopedRows
      .reduce((sum, cm) => sum + (Number(cm[studentField]) || 0), 0);

    // Fetch remaining data: survey list. (static/all/ was previously
    // batched here to feed constants into the now-removed
    // PerformancePopup; nothing on the instructor dashboard reads it.)
    getRequest(SURVEY_DATA_API)
      .then(res => {
        let surveys = null;
        let finishedSurveys = [];
        let surveysError = null;
        if (res.statusCode !== 200) {
          surveysError = res.body;
        } else {
          // /app/survey-data/ returns surveys across every course the
          // instructor is tied to. Scope to the current course so the
          // Initial/Final picker, Student Spotlight insights check, and
          // default-survey rule don't mix surveys from foreign courses.
          const rawSurveys = res.body.surveys || [];
          const currentCourseId = Number(Auth.getCourseId());
          surveys = rawSurveys.filter(s => {
            const c = s.course_id != null ? s.course_id : s.course;
            return Number(c) === currentCourseId;
          });
          if (surveys && surveys.length > 0) {
            finishedSurveys = surveys.filter(i => isSurveyFinished(i))
          }
        }

        if (surveysError) {
          console.error('SURVEYS API ERROR', surveysError);
        }

        this.setState(
          {
            teams,
            courses,
            rounds,
            surveys: finishedSurveys,
            allSurveys: surveys,
            surveysError,
            games: gamesFiltered,
            countOfCourseMembers,
          },
          () => {
            const { games, allSurveys: stateAllSurveys } = this.state;

            if (games && games.length > 0) {
              // Prefer the game App.js already picked at login. It's
              // the authoritative choice and it always arrives in the
              // same setState as instructorGames/instructorCourses, so
              // it is guaranteed to be present in `games` by the time
              // this callback runs. Fall back to the shared
              // pickCourseAndGame helper (highest-id course, then
              // highest-id game within that course) if props.game
              // isn't set for any reason, and finally to games[0].
              const propGame = this.props.game;
              const propGameId = propGame && Number(propGame.id);
              const propGameMatch = propGameId
                ? games.find(g => Number(g.id) === propGameId)
                : null;
              const { game: pickedGame } = pickCourseAndGame(courses, games);
              const game = propGameMatch || pickedGame || games[0];

              console.log('[MG:dashboard] game pick:', {
                propsGameId: propGameId,
                courses: courses.map(c => c.id),
                gamesFiltered: games.map(g => ({ id: g.id, course: g.course, name: g.name })),
                pickedGame: pickedGame ? { id: pickedGame.id, course: pickedGame.course, name: pickedGame.name } : null,
                propGameMatch: propGameMatch ? { id: propGameMatch.id, name: propGameMatch.name } : null,
                chosen: game ? { id: game.id, name: game.name } : null,
                stateRounds: (this.state.rounds || []).map(r => ({ id: r.id, number: r.number, game: r.game, is_finished: r.is_finished })),
              });

              this.selectGame(game.name);
            }

            // Fire-and-forget: count of unregistered course members
            // for the Student Count tile subtext. Non-blocking so
            // dashboard rendering isn't held up.
            this.fetchNotRegistered();

            // Default survey selection on page load:
            //   Both Initial + Final exist → Final if its end date has
            //     passed (open+closed), otherwise Initial.
            //   0 or 1 survey → earliest by start date (nothing to
            //     toggle between).
            // currentSurvey is shared state driving multiple sections
            // (Top/Bottom Performers, Collaboration Insights, Team
            // Scores, downloads); shifting the default here shifts
            // all of them until the instructor picks the other survey.
            const evalSurveys = this.findEvalSurveys();
            let defaultSurveyId = null;

            if (evalSurveys.initial && evalSurveys.final) {
              const finalClosed = moment.utc().isAfter(moment(evalSurveys.final.end));
              defaultSurveyId = finalClosed
                ? evalSurveys.final.id
                : evalSurveys.initial.id;
            } else {
              const sortedSurveys = (stateAllSurveys || []).slice().sort((a, b) => {
                const aTime = new Date(a.start || a.start_date || 0).getTime();
                const bTime = new Date(b.start || b.start_date || 0).getTime();
                return aTime - bTime;
              });
              if (sortedSurveys.length > 0) {
                defaultSurveyId = sortedSurveys[0].id;
              }
            }

            if (defaultSurveyId) {
              this.selectSurveyById(defaultSurveyId);
            } else {
              this.fetchMostLeastValuable();
              this.fetchCollaborationSkills();
            }
          }
        );
      })
      .catch(err => { });
  };

  fetchPaginationData = (url, loadingField, dataField, pageSize) => {
    this.setState({
      [loadingField]: true,
      [dataField]: [],
    });

    getRequest(`${url}${url.indexOf('?') > 0 ? '&' : '?'}page_size=1`)
      .then(res => {
        const { count } = res.body;

        if (count > 0) {
          const pages = Math.ceil(count / pageSize);
          const requests = [];

          for (let i = 1; i <= pages; i++) {
            requests.push(`${url}${url.indexOf('?') > 0 ? '&' : '?'}page_size=${pageSize}&page=${i}`);
          }

          batchRequest(requests)
            .then(res => {
              try {
                const data = res.flatMap(payload => payload.body.results);

                this.setState({
                  [loadingField]: false,
                  [dataField]: data,
                });
              } catch (e) {
                console.log(e);
              }
            })
            .catch(err => { });
        } else {
          this.setState({
            [loadingField]: false,
          });
        }
      })
      .catch(err => { });
  }

  fetchTeamScore = () => {
    const { currentSurvey } = this.state;

    if (currentSurvey > 0) {
      const apiUrl = process.env.REACT_APP_API_URL;
      const sq = this.sectionsQueryParam();
      getRequest(`${apiUrl}v2/forms/surveys/${currentSurvey}/team-score/${sq ? `?${sq}` : ""}`)
        .then(res => {
          const data = res.body || {};
          this.setState({
            teamScore: data,
            processResultsFailed: !data.collaboration,
          });
        })
        .catch(err => { });
    }
  };

  fetchUserScore = () => {
    // No longer needed — user scores are fetched via fetchTeamScore using survey-data API
  };

  checkPerformanceDownloadAbility = () => {
    const { currentSurvey, currentRound, rounds } = this.state;
    const userId = Auth.getUserId();
    const round = rounds.find(i => i.id === currentRound);

    if (round) {
      getRequest(`
        ${process.env.REACT_APP_API_URL}v2/games/usergrade/${currentSurvey}/${round.number}/${userId}
      `)
        .then(res => {
          if (res.body) {
            this.setState({
              showPerformanceDowload: true
            });
          }
        })
        .catch(err => { });
    }
  }

  get isLoading() {
    const { courses, games, rounds, teams, surveys, surveysError } = this.state;

    return (
      courses === null ||
      games === null ||
      rounds === null ||
      teams === null ||
      (surveys === null && surveysError === null)
    );
  }

  get rounds() {
    const { currentGame, rounds } = this.state;

    const result = currentGame > 0 && rounds
      ? rounds
        .filter(
          round => round.game === currentGame && round.is_finished === true
        )
        .map(round => `Round ${round.number}`)
      : [];
    if (typeof window !== 'undefined' && !this._loggedRoundsOnce) {
      this._loggedRoundsOnce = true;
      console.log('[MG:dashboard] rounds getter:', {
        currentGame,
        currentGameType: typeof currentGame,
        rawRounds: (rounds || []).map(r => ({
          id: r.id,
          number: r.number,
          game: r.game,
          gameType: typeof r.game,
          is_finished: r.is_finished,
          is_finishedType: typeof r.is_finished,
        })),
        filtered: result,
      });
    }
    return result;
  }

  selectGame = value => {
    const { games } = this.state;

    if (games) {
      const game = games.find(i => i.name === value);
      if (game) {
        this.setState(
          {
            currentGame: game.id,
            currentRound: 0,
            averageTeamGrade: 0,
            medianTeamGrade: 0,
            standardDeviation: 0,
          },
          () => {
            this.selectCourse();
            this.updateCurrentRound();
            // Decision Submission Status targets the live in-progress
            // round, so it has to fire outside the selectRound path
            // (which only runs for finished rounds).
            this.fetchPendingDecisions();
          }
        );
      }
    }
  };

  selectRound = value => {
    const { rounds } = this.state;

    if (rounds && value) {
      let roundNumber = parseInt(value.replace("Round ", ""), 10);
      if (isNaN(roundNumber)) {
        roundNumber = 0;
      }

      if (roundNumber > 0) {
        const round = rounds.find(i => i.number === roundNumber);
        if (round) {
          this.setState(
            {
              currentRound: round.id,
              engagementDownloadRequest: null,
              engagementDownloadCount: 0,
              engagementDownloadFile: null,
              performanceDownloadRequest: null,
              performanceDownloadCount: 0,
              performanceDownloadFile: null,
            },
            () => {
              this.fetchPerformanceMetrics();
              this.fetchPerformanceFeedbacks();
              this.fetchPendingDecisions();
            }
          );
        }
      }
    }
  };

  // Loads the data that drives the dashboard's Performance Feedback
  // block: per-game performance feedback rows (with their `type` +
  // unique `teams` arrays + `id` for tie-break) and the round's
  // targets (used to gate which categories are relevant).
  fetchPerformanceFeedbacks = () => {
    const { currentGame, currentRound, rounds } = this.state;

    // Target round: honor the sidebar's ?round=N first so the
    // Critical Thinking Insights list reflects what the instructor
    // has selected, not updateCurrentRound's latest-closed fallback.
    // Fall back to state.currentRound when roundInUrl is missing or
    // doesn't resolve to a round in the current game.
    const roundInUrl = Number(this.props.roundInUrl) || 0;
    const urlRoundRecord = (roundInUrl > 0 && Array.isArray(rounds))
      ? rounds.find(r => r.number === roundInUrl && r.game === currentGame)
      : null;
    const targetRoundRecord = urlRoundRecord
      || (rounds || []).find(r => r.id === currentRound);
    const targetRoundNumber = targetRoundRecord ? Number(targetRoundRecord.number) : 0;

    if (!currentGame || !targetRoundNumber) return;

    // Mirror the Team Scores guard — performancefeedbacks for a
    // live round reflects a mid-round snapshot that'll shift as
    // decisions roll in, which misleads the Critical Thinking
    // Insights ranking. Blank both arrays so the list renders empty
    // and skip the requests entirely when the round hasn't closed.
    if (!targetRoundRecord || targetRoundRecord.is_finished !== true) {
      this.setState({
        performanceFeedbacksGame: [],
        gamesTargets: [],
      });
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    Promise.all([
      getRequest(`${apiUrl}v2/reports/performancefeedbacks/game/${currentGame}/?round_number=${targetRoundNumber}`),
      getRequest(`${apiUrl}v2/games/targets/?round_number=${targetRoundNumber}&course=${Auth.getCourseId()}`),
    ])
      .then(([feedbacksRes, targetsRes]) => {
        this.setState({
          performanceFeedbacksGame: (feedbacksRes && feedbacksRes.body) || [],
          gamesTargets: (targetsRes && targetsRes.body) || [],
        });
      })
      .catch(() => {});
  };

  // Loads the dashboard's Top + Bottom Performers list. Hits
  // /v2/games/most-least-valuable/<game_id>/ which returns
  // { most_valuable: [{ user_id, ... }], least_valuable: [...] }.
  // Names are resolved by cross-referencing user_id against
  // courseMembers (passed in via the general-info API) so we don't
  // fan out to /users on top of the request the dashboard already
  // makes for course members.
  //
  // To avoid hitting the endpoint on every page load this is gated
  // by an `Update`-only refresh model:
  //   - On mount: only fire if BOTH lists are empty in state.
  //   - On Update click: always refetch (force = true).
  //
  // Order returned by the backend is preserved (no shuffle).
  // Checks whether per-survey insights have been generated for the
  // current survey. The /students/ endpoint returns a paginated set
  // of generated insight rows; if `results.length > 0` we treat the
  // survey as having insights and show the Student Spotlight lists.
  // We request only one row to keep the payload tiny.
  fetchSpotlightInsightsCheck = () => {
    const surveyId = this.state.currentSurvey;
    if (!surveyId) {
      this.setState({
        studentSpotlightHasInsights: null,
        studentSpotlightCheckLoading: false,
        studentSpotlightGenerateMessage: null,
      });
      return;
    }
    this.setState({
      studentSpotlightCheckLoading: true,
      // Don't clear the generate banner here; it's cleared explicitly
      // on a successful survey switch via selectSurveyById.
    });
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/insights/survey/${surveyId}/students/?page=1&page_size=1${sq ? `&${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        const results = Array.isArray(body.results) ? body.results : [];
        this.setState({
          studentSpotlightHasInsights: results.length > 0,
          studentSpotlightCheckLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          studentSpotlightHasInsights: false,
          studentSpotlightCheckLoading: false,
        });
      });
  };

  // Read the instructor's per-survey badge consent state. Drives the
  // "Confirm Badges" CTA + the tour overlay — CTA renders only when
  // state === "pending" (never fetched, never confirmed, never
  // declined). Called on survey switch, after insight generation
  // completes, and after Approve/Decline flips the state server-side.
  fetchInstructorBadgeState = () => {
    const surveyId = this.state.currentSurvey;
    if (!surveyId || !this.isFinalStageSurvey()) {
      this.setState({ myInstructorBadgeState: null });
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/badges/survey/${surveyId}/status/${sq ? `?${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        this.setState({
          myInstructorBadgeState: body.my_instructor_state || null,
        });
      })
      .catch(() => {
        // Endpoint 404s / errors → treat as "unknown". Intro popup
        // stays hidden rather than showing a broken affordance.
        this.setState({ myInstructorBadgeState: null });
      });
  };

  // True when the currently-selected survey's stage is the final /
  // closing one. Uses the same "final" | "second" convention that
  // findEvalSurveys does — either value means the closing survey.
  isFinalStageSurvey = () => {
    const surveys = this.state.allSurveys || [];
    const cur = surveys.find(s => s.id === this.state.currentSurvey);
    if (!cur) return false;
    const stage = String(cur.stage_name || cur.stage || "").toLowerCase();
    return stage === "final" || stage === "second";
  };

  // Open the badges consent tour overlay. Fires two parallel calls:
  //
  //   1. GET /v2/insights/class/<c>/<s>/?sections=...
  //      consent message copy + email preview, scoped to the
  //      instructor's sections (so the email preview reflects a
  //      student the instructor actually teaches).
  //   2. GET /v2/games/most-least-valuable/<s>/   (no sections query)
  //      used only to count the class-wide unique badge recipients
  //      for the "<X> of <Y> students in the entire course" line in
  //      the consent paragraph. Section-scoped MVP state stays as-is
  //      for the Review Badge Recipients checklist that opens next.
  //
  // Popup opens after both calls settle so the counts land correct
  // on first paint. Course id comes from the picked course stashed
  // in state at general-info load time.
  openBadgesTour = () => {
    const surveyId = this.state.currentSurvey;
    const courseId = Number(Auth.getCourseId());
    if (!surveyId || !courseId) return;
    // Guard against re-entrancy if the user double-clicks the button
    // while the first fetch is still in flight.
    if (this.state.badgesConsentLoading) return;
    // Fetch first, THEN open the popup — this avoids the ~1s
    // "Loading consent message…" flash inside the popup. During the
    // wait, the button shows its own loading state so the click still
    // feels responsive.
    this.setState({ badgesConsentLoading: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const consentPromise = getRequest(
      `${apiUrl}v2/insights/class/${courseId}/${surveyId}/${this.sectionsQueryParam() ? `?${this.sectionsQueryParam()}` : ""}`
    )
      .then(res => (res && res.body) || {})
      .catch(() => ({}));
    const classWideCountPromise = this.fetchClassWideBadgeRecipientCount(surveyId);

    Promise.all([consentPromise, classWideCountPromise])
      .then(([consentBody, classWideCount]) => {
        this.setState({
          badgesConsentMessage: consentBody.badges_consent_message || null,
          badgesStudentEmailPreview: consentBody.badges_student_email_preview || null,
          classWideBadgeRecipientCount: classWideCount,
          badgesConsentLoading: false,
          badgesTourOpen: true,
        });
      });
  };

  // Counts unique class-wide badge recipients for the Review and Issue
  // consent paragraph. Fires a second GET to most-least-valuable
  // without the ?sections= filter so even a section-scoped instructor
  // sees the whole course's badge totals. Returns 0 on any failure so
  // the popup still opens with best-effort copy.
  fetchClassWideBadgeRecipientCount = (surveyId) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    return getRequest(`${apiUrl}v2/games/most-least-valuable/${surveyId}/`)
      .then(res => {
        const body = (res && res.body) || {};
        const mostRaw = Array.isArray(body.most_valuable) ? body.most_valuable : [];
        const wellRaw = Array.isArray(body.well_rounded) ? body.well_rounded : [];
        const marketRaw = Array.isArray(body.market_masters) ? body.market_masters : [];
        // Dedupe by user_id — a student can show up in more than one
        // positive cohort but counts as one badge recipient.
        const seen = new Set();
        [...mostRaw, ...wellRaw, ...marketRaw].forEach(entry => {
          const uid = entry && (entry.user_id != null ? entry.user_id : entry.user);
          if (uid != null) seen.add(Number(uid));
        });
        return seen.size;
      })
      .catch(() => 0);
  };

  closeBadgesTour = () => {
    if (this.state.badgesBatchSubmitting) return;
    this.setState({
      badgesTourOpen: false,
      badgesConsentMessage: null,
      badgesStudentEmailPreview: null,
      badgesConsentLoading: false,
      badgesEmailPreviewOpen: false,
      classWideBadgeRecipientCount: null,
    });
  };

  // Intro → Continue and Ready-to-Review both funnel through here.
  openBadgesEmailPreview = () => {
    this.setState({ badgesEmailPreviewOpen: true });
  };

  closeBadgesEmailPreview = () => {
    this.setState({ badgesEmailPreviewOpen: false });
  };

  // Opens the nested checklist popup with every qualifying student
  // pre-selected. The instructor can un-check any before POSTing.
  openBadgesReview = () => {
    if (this.state.badgesBatchSubmitting) return;
    const mvps = this.state.mostValuablePlayers || [];
    const seedIds = new Set(
      mvps
        .map(m => m && m.user_id)
        .filter(id => id != null)
        .map(id => Number(id))
    );
    this.setState({
      badgesReviewOpen: true,
      badgesSelectedIds: seedIds,
      badgesActionError: null,
    });
  };

  // Back button on the review popup: close it and return to the tour
  // popup (still open behind). Selection state is retained so
  // re-opening restores the instructor's picks; if the tour closes
  // for any reason, openBadgesReview reseeds from the current MVP
  // list.
  closeBadgesReview = () => {
    if (this.state.badgesBatchSubmitting) return;
    this.setState({ badgesReviewOpen: false });
  };

  toggleBadgeSelection = (userId) => {
    const id = Number(userId);
    if (Number.isNaN(id)) return;
    const current = this.state.badgesSelectedIds instanceof Set
      ? new Set(this.state.badgesSelectedIds)
      : new Set();
    if (current.has(id)) current.delete(id);
    else current.add(id);
    this.setState({ badgesSelectedIds: current });
  };

  // "Clear all" link on a category column. Un-checks every student
  // in that category; other categories keep their selections. If
  // every row in the column is already un-checked, re-checks them
  // all (acts as a toggle to speed up "de-select everything then
  // add a few back" flows).
  clearBadgeCategory = (category) => {
    const mvps = this.state.mostValuablePlayers || [];
    const ids = mvps
      .filter(m => m && m.category === category && m.user_id != null)
      .map(m => Number(m.user_id));
    if (ids.length === 0) return;
    const current = this.state.badgesSelectedIds instanceof Set
      ? new Set(this.state.badgesSelectedIds)
      : new Set();
    const anySelected = ids.some(id => current.has(id));
    if (anySelected) {
      ids.forEach(id => current.delete(id));
    } else {
      ids.forEach(id => current.add(id));
    }
    this.setState({ badgesSelectedIds: current });
  };

  approveBadges = () => {
    const surveyId = this.state.currentSurvey;
    if (!surveyId || this.state.badgesBatchSubmitting) return;
    const selected = this.state.badgesSelectedIds instanceof Set
      ? Array.from(this.state.badgesSelectedIds)
      : null;
    this.setState({ badgesBatchSubmitting: true, badgesActionError: null });
    const apiUrl = process.env.REACT_APP_API_URL;
    // Omit user_ids entirely to badge every qualifying student (BE
    // treats a missing field as "all"). Send the array as-is when the
    // instructor has explicitly opened the review popup.
    const payload = selected != null ? { user_ids: selected } : {};
    postRequest(`${apiUrl}v2/badges/batch-confirm/${surveyId}/`)
      .send(payload)
      .then(() => {
        this.setState({
          badgesBatchSubmitting: false,
          badgesReviewOpen: false,
          badgesTourOpen: false,
          badgesConsentMessage: null,
          badgesSelectedIds: null,
          myInstructorBadgeState: "confirmed",
        });
      })
      .catch(() => {
        this.setState({
          badgesBatchSubmitting: false,
          badgesActionError: "Couldn't confirm badges. Please try again.",
        });
      });
  };

  declineBadges = () => {
    const surveyId = this.state.currentSurvey;
    if (!surveyId || this.state.badgesBatchSubmitting) return;
    this.setState({ badgesBatchSubmitting: true, badgesActionError: null });
    const apiUrl = process.env.REACT_APP_API_URL;
    postRequest(`${apiUrl}v2/badges/batch-decline/${surveyId}/`)
      .send({})
      .then(() => {
        this.setState({
          badgesBatchSubmitting: false,
          badgesTourOpen: false,
          badgesConsentMessage: null,
          myInstructorBadgeState: "declined",
        });
      })
      .catch(() => {
        this.setState({
          badgesBatchSubmitting: false,
          badgesActionError: "Couldn't decline badges. Please try again.",
        });
      });
  };

  // "Not Sure Yet" — no BE call. Closes the tour AND suppresses the
  // intro popup for the rest of this session. On refresh/navigation
  // the suppress flag resets and the flow re-fires (my_instructor_state
  // is still "pending" on the BE, so gates still pass).
  notSureYetBadges = () => {
    if (this.state.badgesBatchSubmitting) return;
    this.setState({
      badgesTourOpen: false,
      badgesConsentMessage: null,
      badgesConsentLoading: false,
      badgesIntroSuppressed: true,
    });
  };

  // Intro popup → Continue: open the Review and Approve consent
  // popup directly. The two-step tour that used to sit between the
  // intro and the Review popup was removed as unnecessary; this now
  // mirrors what clicking the "Review and Approve Badges" button in
  // the Student Spotlight header does.
  continueBadgesFromIntro = () => {
    this.openBadgesTour();
  };

  // Two-step: the active Generate Insights button opens this confirm
  // popup; the popup's primary action fires the actual POST. Keeps
  // instructors from accidentally consuming the one-shot generation.
  openGenerateConfirm = () => {
    this.setState({ studentSpotlightConfirmOpen: true });
  };

  closeGenerateConfirm = () => {
    this.setState({ studentSpotlightConfirmOpen: false });
  };

  confirmGenerateSpotlightInsights = () => {
    this.setState({ studentSpotlightConfirmOpen: false });
    this.generateSpotlightInsights();
  };

  // Kicks off insight generation for the current survey. Backend
  // returns 202 immediately with a status_url; FE polls that URL
  // until status flips to "done" (typically <5 min).
  generateSpotlightInsights = () => {
    const surveyId = this.state.currentSurvey;
    if (!surveyId || this.state.studentSpotlightGenerating) return;
    this.setState({
      studentSpotlightGenerating: true,
      studentSpotlightGenerateMessage:
        "Insights are being generated, it typically takes up to 5 minutes.",
    });
    const apiUrl = process.env.REACT_APP_API_URL;
    postRequest(`${apiUrl}v2/insights/generate/${surveyId}/`)
      .send({})
      .then(res => {
        const body = (res && res.body) || {};
        const statusUrl = body.status_url;
        if (!statusUrl) {
          // BE didn't return the expected status_url shape — fall back
          // to the old static-notice behavior so the instructor still
          // knows something's in flight.
          this.setState({
            studentSpotlightGenerating: false,
            studentSpotlightGenerateMessage:
              "Insights are being generated. Please check back in a few minutes.",
          });
          return;
        }
        this._insightsPollStartTime = Date.now();
        this.setState({
          insightsJobId: body.job_id || null,
          insightsStatusUrl: statusUrl,
        });
        // Kick off the first poll at 10s (matches roster-upload
        // POLL_FIRST_DELAY_MS). Sub-90s completions typically land on
        // the second or third poll.
        this._insightsPollTimeout = setTimeout(
          () => this.pollInsightsStatus(statusUrl),
          10000,
        );
      })
      .catch(() => {
        this._stopInsightsPoll();
        this.setState({
          studentSpotlightGenerating: false,
          studentSpotlightGenerateMessage:
            "Couldn't kick off insight generation. Please try again.",
        });
      });
  };

  // Tiered poll delays mirror the roster-upload pattern in
  // instructor/utils/roster-upload.js — start tight (10s), back off
  // once the job has been running a while. Cap at 6 min (BE target
  // is <5 min; buffer covers Gemini/autoscaling variance).
  _INSIGHTS_POLL_FIRST_DELAY_MS = 10000;
  _INSIGHTS_POLL_MID_DELAY_MS = 30000;
  _INSIGHTS_POLL_LATE_DELAY_MS = 60000;
  _INSIGHTS_POLL_MID_THRESHOLD_MS = 120000;
  _INSIGHTS_POLL_LATE_THRESHOLD_MS = 240000;
  _INSIGHTS_POLL_MAX_DURATION_MS = 360000;

  _getInsightsPollDelay = (elapsedMs) => {
    if (elapsedMs < this._INSIGHTS_POLL_MID_THRESHOLD_MS) return this._INSIGHTS_POLL_FIRST_DELAY_MS;
    if (elapsedMs < this._INSIGHTS_POLL_LATE_THRESHOLD_MS) return this._INSIGHTS_POLL_MID_DELAY_MS;
    return this._INSIGHTS_POLL_LATE_DELAY_MS;
  };

  _stopInsightsPoll = () => {
    if (this._insightsPollTimeout) {
      clearTimeout(this._insightsPollTimeout);
      this._insightsPollTimeout = null;
    }
    this._insightsPollStartTime = null;
  };

  // Resolve the status_url returned by /insights/generate/ into a
  // fully-qualified URL. REACT_APP_API_URL already includes the /api/
  // prefix, so we strip a leading /api/ to avoid doubling it up.
  _resolveStatusUrl = (statusUrl) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (statusUrl.startsWith('/api/')) {
      return `${apiUrl}${statusUrl.slice(5)}`;
    }
    return `${apiUrl}${statusUrl.replace(/^\//, '')}`;
  };

  pollInsightsStatus = (statusUrl) => {
    const elapsedMs = this._insightsPollStartTime
      ? Date.now() - this._insightsPollStartTime
      : 0;

    if (elapsedMs > this._INSIGHTS_POLL_MAX_DURATION_MS) {
      this._stopInsightsPoll();
      this.setState({
        studentSpotlightGenerating: false,
        studentSpotlightGenerateMessage:
          "Still processing — please refresh the page in a moment to check again.",
      });
      return;
    }

    getRequest(this._resolveStatusUrl(statusUrl))
      .then(res => {
        const body = (res && res.body) || {};
        const status = body.status;

        if (status === 'done') {
          this._stopInsightsPoll();
          this.setState({
            studentSpotlightGenerating: false,
            studentSpotlightGenerateMessage: null,
            insightsJobId: null,
            insightsStatusUrl: null,
          }, () => {
            // Refresh the derived data so the Student Spotlight lists,
            // collaboration bubbles, and existence flag all reflect the
            // freshly-generated insights.
            this.fetchMostLeastValuable(true);
            this.fetchCollaborationSkills(true);
            this.fetchSpotlightInsightsCheck();
            // Also pull the instructor's badge consent state so the
            // Confirm Badges CTA can appear (final surveys only).
            this.fetchInstructorBadgeState();
          });
          return;
        }

        if (status === 'failed') {
          this._stopInsightsPoll();
          this.setState({
            studentSpotlightGenerating: false,
            studentSpotlightGenerateMessage:
              "Insight generation failed. Please try again.",
          });
          return;
        }

        // Still processing — surface live progress in the notice.
        const progress = body.progress_percent || {};
        const studentsPct = Number(progress.students || 0);
        const teamsPct = Number(progress.teams || 0);
        this.setState({
          studentSpotlightGenerateMessage:
            `Insights are being generated, it typically takes up to 5 minutes. (Students ${studentsPct}% • Teams ${teamsPct}%)`,
        });

        const nextDelay = this._getInsightsPollDelay(elapsedMs);
        this._insightsPollTimeout = setTimeout(
          () => this.pollInsightsStatus(statusUrl),
          nextDelay,
        );
      })
      .catch(() => {
        // Transient status failure — try again at the next interval
        // rather than giving up. Persistent failures eventually hit
        // the max-duration cap above.
        const nextDelay = this._getInsightsPollDelay(elapsedMs);
        this._insightsPollTimeout = setTimeout(
          () => this.pollInsightsStatus(statusUrl),
          nextDelay,
        );
      });
  };

  fetchMostLeastValuable = (force = false) => {
    const {
      currentSurvey,
      allSurveys,
      mostValuablePlayers,
      leastValuablePlayers,
    } = this.state;
    // Endpoint is keyed by survey id (despite the /games/ path prefix).
    // Prefer the user-picked survey; if none is selected (e.g. no finished
    // survey yet, so selectSurvey never ran), fall back to the first
    // survey on the game so the block still populates.
    const surveyId = currentSurvey
      || (Array.isArray(allSurveys) && allSurveys.length > 0 ? allSurveys[0].id : 0);
    if (!surveyId) return;
    if (!force
      && Array.isArray(mostValuablePlayers) && mostValuablePlayers.length > 0
      && Array.isArray(leastValuablePlayers) && leastValuablePlayers.length > 0) {
      return;
    }

    this.setState({ mostLeastValuableLoading: true });

    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/games/most-least-valuable/${surveyId}/${sq ? `?${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        // Three "positive" cohorts feed the Most Valuable panel. Each
        // is merged into a single sorted+deduped list where each
        // student appears once under whichever cohort awarded them
        // the highest total_points (per product decision: student
        // shouldn't appear twice in the panel).
        const mostRaw = Array.isArray(body.most_valuable) ? body.most_valuable : [];
        const wellRaw = Array.isArray(body.well_rounded) ? body.well_rounded : [];
        const marketRaw = Array.isArray(body.market_masters) ? body.market_masters : [];
        const leastRaw = Array.isArray(body.least_valuable) ? body.least_valuable : [];

        const shape = (entry, category) => ({
          user_id: entry && (entry.user_id != null ? entry.user_id : entry.user),
          total_points: Number(entry && entry.total_points) || 0,
          first_name: (entry && entry.first_name) || '',
          last_name: (entry && entry.last_name) || '',
          username: (entry && entry.username) || '',
          team_id: entry && entry.team_id != null ? Number(entry.team_id) : null,
          team_name: (entry && entry.team_name) || '',
          category,
        });

        // Union all three positive cohorts, then dedupe by user_id
        // keeping the entry with the highest total_points (that's the
        // cohort we'll label them under). Finally sort desc by points.
        const merged = new Map();
        [
          ...mostRaw.map(e => shape(e, 'most_valuable')),
          ...wellRaw.map(e => shape(e, 'well_rounded')),
          ...marketRaw.map(e => shape(e, 'market_masters')),
        ].forEach(row => {
          if (row.user_id == null) return;
          const key = Number(row.user_id);
          const prev = merged.get(key);
          if (!prev || row.total_points > prev.total_points) {
            merged.set(key, row);
          }
        });
        const mostValuablePlayers = Array.from(merged.values())
          .sort((a, b) => b.total_points - a.total_points);

        this.setState({
          mostValuablePlayers,
          leastValuablePlayers: leastRaw.map(e => shape(e, 'least_valuable')),
          mostLeastValuableLoading: false,
        });
      })
      .catch(() => {
        this.setState({ mostLeastValuableLoading: false });
      });
  };

  // Per-tab bubble palette. body = soft fill, outline = pastel ring,
  // text = saturated accent for the count rendered inside the bubble.
  collabBubbleColors = {
    strengths: { body: "#EAF3EC", outline: "#D1E9D6", text: "#2DA548" },
    weaknesses: { body: "#FAEDED", outline: "#FBD8D8", text: "#EC5E59" },
  };

  // Display-name overrides: backend may return the long form but
  // the dashboard renders the shortened label.
  collabSkillDisplayName = (raw) => {
    if (raw === "Effective Communication") return "Communication";
    return raw;
  };

  // Donut palette for the Team Dynamics chart. Soft green shades for
  // "worked well / worked together" and a soft red for "did not work
  // together" — keyed to the collaboration field names on the BE:
  //   workedVeryWell → team_worked_well_together
  //   workedWell     → team_worked_together
  //   didNotWork     → team_did_not_worked_together
  collabDonutColors = {
    workedVeryWell: "#2DA548",
    workedWell: "#EAF3EC",
    didNotWork: "#FAEDED",
  };

  // Return up to 4 bubbles for the currently-selected tab, sorted
  // largest → smallest by student count so the chart can lay them out
  // left → right in descending order.
  getCollabBubbles = () => {
    const tab = this.state.collaborationSkillsTab === "weaknesses"
      ? "weaknesses"
      : "strengths";
    const source = tab === "weaknesses"
      ? (this.state.collaborationWeaknesses || [])
      : (this.state.collaborationStrengths || []);
    return source
      .slice(0, 4)
      .map(s => ({
        text: this.collabSkillDisplayName(s.skill_name || s.skill || s.name || ""),
        count: Number(s.total_student_count) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  };

  // Total number of teams the survey covers, used in the donut center.
  get collabTeamCount() {
    const { teamScore } = this.state;
    return teamScore ? get(teamScore.collaboration, "total_teams", 0) : 0;
  }

  renderBadgesTourBackdrop = () => {
    if (!this.state.badgesTourOpen) return null;
    return <BadgesTourBackdrop />;
  };

  // "Review and Issue" popup. Renders the summary copy + primary
  // action that opens the nested checklist popup. Copy numbers are
  // class-wide — class-wide unique badge recipients (from the
  // parallel most-least-valuable fetch kicked off in openBadgesTour)
  // over the course's student_count field — so a section-scoped
  // instructor still sees the student's placement across the entire
  // course.
  renderBadgesTour = () => {
    if (!this.state.badgesTourOpen) return null;
    const {
      badgesBatchSubmitting,
      badgesActionError,
    } = this.state;

    // Class-wide numbers so a section-scoped instructor still sees
    // the student's placement across the entire course. mvpCount is
    // from the parallel class-wide fetch kicked off in openBadgesTour;
    // studentCount is read directly from the current course's
    // student_count field (not section_student_count).
    const currentCourseId = Number(Auth.getCourseId());
    const scopedCourseRows = (this.props.courseMembers || [])
      .filter(cm => Number(cm.course_id != null ? cm.course_id : cm.course) === currentCourseId);
    const studentCount = scopedCourseRows.reduce(
      (sum, cm) => sum + (Number(cm.student_count) || 0),
      0,
    );
    const mvpCount = this.state.classWideBadgeRecipientCount != null
      ? Number(this.state.classWideBadgeRecipientCount) || 0
      : 0;
    const percent = studentCount > 0
      ? ((mvpCount / studentCount) * 100).toFixed(1)
      : "0.0";

    return (
      <BadgesTourPopup onClick={e => e.stopPropagation()}>
        <BadgesTourHeader>
            <BadgesTourTitle style={{ margin: 0 }}>Review and Issue</BadgesTourTitle>
          </BadgesTourHeader>

          <BadgesTourBody>
            <BadgesTourConsentText>
              {mvpCount} of the {studentCount} students in the entire course earned a Career Readiness Badge for their work in the Market Games Challenge, placing them in the top {percent}% of the course.
            </BadgesTourConsentText>
            <BadgesTourConsentText style={{ marginBottom: 0 }}>
              To issue the badges, please review the list of students, select those you'd like to receive one, and approve issuance. Once you approve, each selected student will get an email with their badge link, which they can add to LinkedIn, Handshake, or their resume.{' '}
              <BadgesTourInlineLink
                href="#"
                onClick={e => {
                  e.preventDefault();
                  this.openBadgesEmailPreview();
                }}
              >
                View the email message
              </BadgesTourInlineLink>
              .
            </BadgesTourConsentText>
          </BadgesTourBody>

          <BadgesTourFooter>
            {badgesActionError && (
              <BadgesTourError style={{ marginRight: 'auto', marginBottom: 0 }}>
                {badgesActionError}
              </BadgesTourError>
            )}
            <BadgesTourNotSureButton
              type="button"
              onClick={this.notSureYetBadges}
              disabled={badgesBatchSubmitting}
            >
              Not Sure Yet
            </BadgesTourNotSureButton>
            <BadgesTourDeclineButton
              type="button"
              onClick={this.declineBadges}
              disabled={badgesBatchSubmitting}
            >
              {badgesBatchSubmitting ? "Working…" : "Decline"}
            </BadgesTourDeclineButton>
            <BadgesTourApproveButton
              type="button"
              onClick={this.openBadgesReview}
              disabled={badgesBatchSubmitting || mvpCount === 0}
            >
              Review and Issue Badges
            </BadgesTourApproveButton>
          </BadgesTourFooter>
      </BadgesTourPopup>
    );
  };

  // Nested checklist popup. Sits on top of the "Review and Approve"
  // popup with its own dimmer. The body is split into three category
  // columns (Most Valuable / Market Master / Well Rounded), each
  // ranked by total points with its own "Clear all" toggle. Rank 1-3
  // rows get a podium-colored badge circle in the category palette;
  // ranks 4+ fall through to neutral gray. Clicking a name opens the
  // existing certificate preview. Confirm POSTs { user_ids: [selected] }.
  renderBadgesReview = () => {
    if (!this.state.badgesReviewOpen) return null;

    const mvps = this.state.mostValuablePlayers || [];
    const selectedIds = this.state.badgesSelectedIds instanceof Set
      ? this.state.badgesSelectedIds
      : new Set();
    const selectedCount = selectedIds.size;
    const totalStudents = Number(this.state.countOfCourseMembers) || 0;
    const { badgesBatchSubmitting, badgesActionError } = this.state;

    // Filter mostValuablePlayers per category, preserving the
    // total-points order from fetchMostLeastValuable so the rank
    // column shown here matches the BE's ranking.
    const byCategory = BADGE_CATEGORY_ORDER.reduce((acc, cat) => {
      acc[cat] = mvps.filter(m => m && m.category === cat);
      return acc;
    }, {});

    const earnedBadges = mvps.length;
    const topPct = totalStudents > 0
      ? ((earnedBadges / totalStudents) * 100).toFixed(1)
      : "0.0";

    return [
      <BadgesReviewBackdrop key="review-backdrop" />,
      <BadgesReviewPopup key="review-popup" onClick={e => e.stopPropagation()}>
        <BadgesReviewHeader>
          <BadgesReviewHeaderLeft>
            <BadgesReviewEyebrow>Career Readiness Badge</BadgesReviewEyebrow>
            <BadgesReviewTitle>Review Badge Recipients</BadgesReviewTitle>
            <BadgesReviewSubtitle>
              Uncheck any student who shouldn't receive a badge. Click a name to preview their badge. Approved students get their badge link by email.
            </BadgesReviewSubtitle>
          </BadgesReviewHeaderLeft>
          <BadgesReviewStats>
            <BadgesReviewStatCard>
              <BadgesReviewStatValue>{earnedBadges}</BadgesReviewStatValue>
              <BadgesReviewStatLabel>Earned badges</BadgesReviewStatLabel>
            </BadgesReviewStatCard>
            <BadgesReviewStatCard>
              <BadgesReviewStatValue>Top {topPct}%</BadgesReviewStatValue>
              <BadgesReviewStatLabel>of {totalStudents} students</BadgesReviewStatLabel>
            </BadgesReviewStatCard>
          </BadgesReviewStats>
        </BadgesReviewHeader>

        <BadgesReviewBody>
          {mvps.length === 0 && (
            <BadgesTourConsentText style={{ marginBottom: 0 }}>
              No qualifying students to badge on this survey.
            </BadgesTourConsentText>
          )}
          {mvps.length > 0 && (
            <BadgesReviewColumns>
              {BADGE_CATEGORY_ORDER.map(cat => {
                const meta = BADGE_CATEGORY_META[cat];
                const rows = byCategory[cat];
                const rowCount = rows.length;
                const selectedInCat = rows.reduce((n, r) => (
                  r.user_id != null && selectedIds.has(Number(r.user_id)) ? n + 1 : n
                ), 0);
                const catTopPct = totalStudents > 0 && rowCount > 0
                  ? ((rowCount / totalStudents) * 100).toFixed(1)
                  : null;
                return (
                  <BadgesReviewColumn key={`badge-col-${cat}`}>
                    <BadgesReviewColumnAccent color={meta.accent} />
                    <BadgesReviewColumnHeader>
                      <BadgesReviewColumnHeaderTop>
                        <BadgesReviewColumnIcon color={meta.accent}>
                          {BADGE_MEDAL_SVG}
                        </BadgesReviewColumnIcon>
                        <BadgesReviewColumnTitle>
                          {meta.label}
                          {catTopPct != null && (
                            <BadgesReviewColumnTopPct>Top {catTopPct}%</BadgesReviewColumnTopPct>
                          )}
                        </BadgesReviewColumnTitle>
                        <BadgesReviewColumnClearAll
                          type="button"
                          onClick={() => this.clearBadgeCategory(cat)}
                          disabled={badgesBatchSubmitting || rowCount === 0}
                        >
                          Clear all
                        </BadgesReviewColumnClearAll>
                      </BadgesReviewColumnHeaderTop>
                      <BadgesReviewColumnSubtitle>
                        {selectedInCat} of {rowCount} selected · ranked by points
                      </BadgesReviewColumnSubtitle>
                    </BadgesReviewColumnHeader>
                    <BadgesReviewColumnTableHeader>
                      <span>Rank</span>
                      <span>Team</span>
                      <span>Student</span>
                    </BadgesReviewColumnTableHeader>
                    <BadgesReviewColumnScroll>
                      {rowCount === 0 && (
                        <BadgesReviewColumnEmpty>
                          No students in this category.
                        </BadgesReviewColumnEmpty>
                      )}
                      {rows.map((mvp, idx) => {
                        const userId = mvp && mvp.user_id != null ? Number(mvp.user_id) : null;
                        const composed = mvp ? `${mvp.first_name || ''} ${mvp.last_name || ''}`.trim() : '';
                        const name = composed || (mvp && mvp.username) || null;
                        const checked = userId != null && selectedIds.has(userId);
                        const rank = idx + 1;
                        const podiumColor = idx < meta.podium.length ? meta.podium[idx] : null;
                        const circleColor = podiumColor || '#DDE1E8';
                        const textColor = podiumColor ? '#ffffff' : 'rgba(0, 27, 59, 0.7)';
                        return (
                          <BadgesReviewNewRow key={`badge-row-${cat}-${userId || idx}`}>
                            <BadgesReviewRowLeft>
                              <BadgesReviewCheckbox
                                checked={checked}
                                onChange={() => {
                                  if (userId != null) this.toggleBadgeSelection(userId);
                                }}
                                disabled={badgesBatchSubmitting || userId == null}
                              />
                              <BadgesReviewRankCircle color={circleColor} textColor={textColor}>
                                {rank}
                              </BadgesReviewRankCircle>
                            </BadgesReviewRowLeft>
                            <Styled.SpotlightTeamBadge title={(mvp && mvp.team_name) || ''}>
                              {this.spotlightTeamBadge(mvp && mvp.team_name)}
                            </Styled.SpotlightTeamBadge>
                            <BadgesReviewName
                              href="#"
                              onClick={e => {
                                e.preventDefault();
                                if (userId != null) {
                                  this.openCertificate(userId, cat, mvp);
                                }
                              }}
                            >
                              {name || `User ${userId || rank}`}
                            </BadgesReviewName>
                          </BadgesReviewNewRow>
                        );
                      })}
                    </BadgesReviewColumnScroll>
                  </BadgesReviewColumn>
                );
              })}
            </BadgesReviewColumns>
          )}
        </BadgesReviewBody>

        <BadgesReviewFooter>
          <BadgesReviewFooterLeft>
            <BadgesReviewCount>
              {selectedCount} of {mvps.length} selected
            </BadgesReviewCount>
            <BadgesReviewFooterNote>
              Badges are emailed to each selected student as soon as you confirm.
              {badgesActionError && (
                <span style={{ color: '#d34c4c', marginLeft: 8 }}>
                  {badgesActionError}
                </span>
              )}
            </BadgesReviewFooterNote>
          </BadgesReviewFooterLeft>
          <BadgesReviewActions>
            <BadgesReviewBackButton
              type="button"
              onClick={this.closeBadgesReview}
              disabled={badgesBatchSubmitting}
            >
              Back
            </BadgesReviewBackButton>
            <BadgesReviewConfirmButton
              type="button"
              onClick={this.approveBadges}
              disabled={badgesBatchSubmitting || selectedCount === 0}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {badgesBatchSubmitting
                ? "Working…"
                : `Approve & email ${selectedCount} badge${selectedCount === 1 ? '' : 's'}`}
            </BadgesReviewConfirmButton>
          </BadgesReviewActions>
        </BadgesReviewFooter>
      </BadgesReviewPopup>,
    ];
  };

  // Nested email preview modal — shown on top of the tour overlay
  // when the instructor clicks "View the email message". Content is
  // the dedicated `badges_student_email_preview` field from
  // /insights/class/, fetched alongside the consent message when the
  // tour opens.
  // Turn every http(s):// URL in the email preview into a clickable
  // link. Returns an array of strings and <a> elements so it can be
  // dropped into any pre-wrap container without breaking whitespace.
  // React 15 has no Fragment, so an array of keyed children is used.
  renderEmailWithLinks = (text) => {
    const regex = /https?:\/\/[^\s]+/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let n = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(
        <BadgesTourInlineLink
          key={`email-lnk-${n}`}
          href={match[0]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[0]}
        </BadgesTourInlineLink>
      );
      lastIndex = match.index + match[0].length;
      n += 1;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return parts;
  };

  renderBadgesEmailPreview = () => {
    if (!this.state.badgesEmailPreviewOpen) return null;
    let emailText = (this.state.badgesStudentEmailPreview || "").trim();

    // Apply the same student-name / URL substitutions the tour body
    // uses, so the previewed email reflects the actual student data.
    const firstMvp = (this.state.mostValuablePlayers || [])[0];
    if (firstMvp && firstMvp.user_id != null) {
      const firstName = (firstMvp.first_name || '').trim() || null;
      const initial = (firstMvp.first_name || '').charAt(0).toLowerCase();
      const surname = (firstMvp.last_name || '').toLowerCase();
      if (firstName) {
        emailText = emailText.replace(/Hi Jordan,/g, `Hi ${firstName},`);
      }
      if (initial && surname) {
        emailText = emailText.replace(
          /badges\.marketgames\.io\/badge\/[a-zA-Z0-9-]+/g,
          `badges.marketgames.io/badge/${initial}-${surname}-${firstMvp.user_id}`,
        );
      }
    }

    return (
      <BadgesEmailPreviewBackdrop onClick={this.closeBadgesEmailPreview}>
        <BadgesEmailPreviewCard onClick={e => e.stopPropagation()}>
          <BadgesEmailPreviewHeader>
            <BadgesEmailPreviewTitle>Email preview</BadgesEmailPreviewTitle>
            <BadgesEmailPreviewCloseButton
              type="button"
              onClick={this.closeBadgesEmailPreview}
              aria-label="Close"
            >
              &times;
            </BadgesEmailPreviewCloseButton>
          </BadgesEmailPreviewHeader>
          <BadgesEmailPreviewBody>
            {emailText
              ? this.renderEmailWithLinks(emailText)
              : "Loading email preview…"}
          </BadgesEmailPreviewBody>
        </BadgesEmailPreviewCard>
      </BadgesEmailPreviewBackdrop>
    );
  };

  // "Share With Students" button under Most Valuable Teammate.
  //   No render at all: insights aren't generated, or non-final survey.
  //   Active ("Share With Students"): my_instructor_state === "pending" —
  //     click opens the tour (same entry point as intro Continue).
  //   Inactive ("Badges Sent"): my_instructor_state === "confirmed" —
  //     tooltip explains badges were shared.
  // Badge-issuance affordance in the Most Valuable column header. Only
  // instructors with a documented scope (INS or INS2 who are either
  // assigned specific sections or have covers_all_sections=true) can
  // issue badges, so the button is hidden for everyone else. Lead
  // instructors with no section assignments (e.g. walshcm4's data) are
  // allowed to generate insights but intentionally not to issue
  // badges — they have no students in their scope to badge.
  renderShareWithStudentsButton = () => {
    if (
      this.state.studentSpotlightHasInsights !== true
      || !this.isFinalStageSurvey()
      // While the Review and Approve popup is open we already have
      // the Approve / Decline / Not Sure Yet controls there — hide
      // the header button so we don't render duplicate entry points.
      || this.state.badgesTourOpen
      || !this.canIssueBadges()
    ) {
      return null;
    }
    const state = this.state.myInstructorBadgeState;
    if (state !== "pending" && state !== "confirmed") {
      // null (not fetched), "declined", or anything else — don't
      // render the affordance.
      return null;
    }
    const isConfirmed = state === "confirmed";
    // Loading state (isFetching) covers the ~1s window between the
    // instructor clicking the button and the popup actually opening —
    // the popup no longer opens until the consent-message fetch
    // resolves, so we need to give the click somewhere visible to land.
    const isFetching = this.state.badgesConsentLoading;
    const disabled = isConfirmed || isFetching;
    let label = "Review and Issue Badges";
    if (isConfirmed) label = "Badges Sent";
    else if (isFetching) label = "Loading…";
    return (
      <ShareWithStudentsButton
        type="button"
        onClick={disabled ? undefined : this.openBadgesTour}
        disabled={disabled}
        title={isConfirmed ? "The badges were shared with students." : "Review and issue badges to share with students"}
      >
        {label}
      </ShareWithStudentsButton>
    );
  };

  // Badge-issuance eligibility. Lead instructors (INS) and
  // co-instructors (INS2) can issue badges only when they have a
  // documented scope on the course: either assigned specific sections
  // on their user record, or course_members.covers_all_sections=true.
  // Instructor Assistants (IAS) and scope-less instructors are
  // excluded — they are not teaching students they can badge.
  canIssueBadges = () => {
    const authUser = Auth.getUser() || {};
    const cm = (this.props.courseMembers || [])[0];
    const position = authUser.position;
    const isInsOrIns2 = position === "Instructor" || position === "Co-Instructor";
    if (!isInsOrIns2) return false;
    const hasSections = Array.isArray(authUser.sections)
      && authUser.sections.length > 0;
    const coversAll = cm && cm.covers_all_sections === true;
    return hasSections || coversAll;
  };

  // Intro popup — auto-appears above the dashboard when badge gates
  // pass and the instructor hasn't clicked Not Sure Yet this session.
  // Continue opens the Review and Issue consent popup. Only shown to
  // instructors who can actually issue badges (same role + scope rule
  // as the dashboard button) so we don't nudge instructors toward an
  // action they can't take.
  renderBadgesIntro = () => {
    if (
      this.state.studentSpotlightHasInsights !== true
      || !this.isFinalStageSurvey()
      || this.state.myInstructorBadgeState !== "pending"
      || this.state.badgesIntroSuppressed
      || this.state.badgesTourOpen
      || !this.canIssueBadges()
    ) {
      return null;
    }
    return (
      <BadgesIntroBackdrop>
        <BadgesIntroCard onClick={e => e.stopPropagation()}>
          <BadgesIntroTitle>Career ready insights are ready to review</BadgesIntroTitle>
          <BadgesIntroContinueButton
            type="button"
            onClick={this.state.badgesConsentLoading ? undefined : this.continueBadgesFromIntro}
            disabled={this.state.badgesConsentLoading}
          >
            {this.state.badgesConsentLoading ? "Loading…" : "Continue"}
          </BadgesIntroContinueButton>
        </BadgesIntroCard>
      </BadgesIntroBackdrop>
    );
  };

  // Areas of Improvement popup — driven by the same insights API as
  // the certificate, but focused on weaknesses.summary +
  // overall_summary and every weakness evidence quote.
  // Team Lookup modal — the "command palette" the KPI card opens.
  renderTeamLookup = () => {
    const {
      teamLookupOpen,
      teamLookupStep,
      teamLookupQuery,
      teamLookupHighlightIdx,
      teamLookupSelectedTeam,
      teamLookupLoading,
      teamLookupError,
    } = this.state;
    if (!teamLookupOpen) return null;

    const filtered = this.getFilteredLookupRows();
    const total = (this.state.teamLookupRows || []).length;

    const initials = name => {
      const parts = String(name || "").trim().split(/\s+/);
      if (parts.length === 0) return "?";
      const first = parts[0] || "";
      const last = parts.length > 1 ? parts[parts.length - 1] : "";
      return ((first[0] || "") + (last[0] || "")).toUpperCase() || "T";
    };

    const isMac = typeof window !== "undefined"
      && /Mac|iPhone|iPad/.test(window.navigator.platform);
    const cmdLabel = isMac ? "⌘" : "Ctrl";

    return (
      <Styled.TeamLookup.Backdrop onClick={this.closeTeamLookup}>
        <Styled.TeamLookup.Card2
          role="dialog"
          aria-modal="true"
          aria-label="Team lookup dialog"
          onClick={e => e.stopPropagation()}
          onKeyDown={this.handleTeamLookupKeyDown}
          tabIndex={-1}
        >
          {teamLookupStep === 1 && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <Styled.TeamLookup.SearchRow>
                <Styled.TeamLookup.SearchIcon aria-hidden>🔍</Styled.TeamLookup.SearchIcon>
                <Styled.TeamLookup.SearchInput
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  type="text"
                  value={teamLookupQuery}
                  placeholder="Search teams by name or number…"
                  aria-label="Search teams"
                  aria-controls="team-lookup-listbox"
                  aria-activedescendant={
                    filtered[teamLookupHighlightIdx]
                      ? `team-lookup-row-${filtered[teamLookupHighlightIdx].id}`
                      : undefined
                  }
                  onChange={e => this.setTeamLookupQuery(e.target.value)}
                />
                <Styled.TeamLookup.CloseIcon
                  type="button"
                  aria-label="Close"
                  onClick={this.closeTeamLookup}
                >
                  ×
                </Styled.TeamLookup.CloseIcon>
              </Styled.TeamLookup.SearchRow>

              <Styled.TeamLookup.List
                id="team-lookup-listbox"
                role="listbox"
                aria-label="Teams"
              >
                {!teamLookupLoading && !teamLookupError && total > 0 && (
                  <Styled.TeamLookup.SectionHeader>
                    {teamLookupQuery ? "Matches" : "All Teams"}{" "}·{" "}
                    <Styled.TeamLookup.SectionHeaderCount>
                      {teamLookupQuery ? filtered.length : total}
                    </Styled.TeamLookup.SectionHeaderCount>
                  </Styled.TeamLookup.SectionHeader>
                )}
                {teamLookupLoading && (
                  <Styled.TeamLookup.Empty>Loading teams…</Styled.TeamLookup.Empty>
                )}
                {!teamLookupLoading && teamLookupError && (
                  <Styled.TeamLookup.Empty>{teamLookupError}</Styled.TeamLookup.Empty>
                )}
                {!teamLookupLoading && !teamLookupError && filtered.length === 0 && (
                  <Styled.TeamLookup.Empty>
                    No teams match "{teamLookupQuery}"
                  </Styled.TeamLookup.Empty>
                )}
                {!teamLookupLoading && !teamLookupError && filtered.map((team, idx) => (
                  <Styled.TeamLookup.Row
                    key={`team-lookup-row-${team.id}`}
                    id={`team-lookup-row-${team.id}`}
                    role="option"
                    aria-selected={idx === teamLookupHighlightIdx}
                    highlighted={idx === teamLookupHighlightIdx}
                    onMouseEnter={() => this.setState({ teamLookupHighlightIdx: idx })}
                    onClick={() => this.handleTeamLookupSelect(team)}
                  >
                    <Styled.TeamLookup.Avatar>{initials(team.name)}</Styled.TeamLookup.Avatar>
                    <Styled.TeamLookup.RowText>
                      <Styled.TeamLookup.RowName>{team.name}</Styled.TeamLookup.RowName>
                      <Styled.TeamLookup.RowMeta>
                        {team.memberCount != null ? `${team.memberCount} students` : "—"}
                        {" · "}
                        Rank {team.rank} of {total}
                      </Styled.TeamLookup.RowMeta>
                    </Styled.TeamLookup.RowText>
                    <Styled.TeamLookup.RowScore>
                      <Styled.TeamLookup.RowScoreValue value={team.score}>
                        {team.score == null
                          ? "—"
                          : Math.round(team.score * 100)}
                      </Styled.TeamLookup.RowScoreValue>
                      <Styled.TeamLookup.RowScoreLabel>SCORE</Styled.TeamLookup.RowScoreLabel>
                    </Styled.TeamLookup.RowScore>
                  </Styled.TeamLookup.Row>
                ))}
              </Styled.TeamLookup.List>

              <Styled.TeamLookup.Footer>
                <span>
                  <Styled.TeamLookup.HintKey>↑</Styled.TeamLookup.HintKey>
                  <Styled.TeamLookup.HintKey>↓</Styled.TeamLookup.HintKey>
                  navigate &nbsp;
                  <Styled.TeamLookup.HintKey>Enter</Styled.TeamLookup.HintKey>
                  select &nbsp;
                  <Styled.TeamLookup.HintKey>Esc</Styled.TeamLookup.HintKey>
                  close
                </span>
                <span>{cmdLabel} K to reopen</span>
              </Styled.TeamLookup.Footer>
            </div>
          )}

          {teamLookupStep === 2 && teamLookupSelectedTeam && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <Styled.TeamLookup.SelectedBanner>
                <Styled.TeamLookup.Avatar>
                  {initials(teamLookupSelectedTeam.name)}
                </Styled.TeamLookup.Avatar>
                <Styled.TeamLookup.RowText>
                  <Styled.TeamLookup.RowName>{teamLookupSelectedTeam.name}</Styled.TeamLookup.RowName>
                  <Styled.TeamLookup.RowMeta>
                    {teamLookupSelectedTeam.memberCount != null
                      ? `${teamLookupSelectedTeam.memberCount} students`
                      : "—"}
                    {" · "}
                    Rank {teamLookupSelectedTeam.rank} of {total}
                  </Styled.TeamLookup.RowMeta>
                </Styled.TeamLookup.RowText>
                <Styled.TeamLookup.ChangeTeamButton
                  type="button"
                  onClick={this.handleTeamLookupBack}
                >
                  Change team
                </Styled.TeamLookup.ChangeTeamButton>
              </Styled.TeamLookup.SelectedBanner>

              <Styled.TeamLookup.ActionsGrid>
                <Styled.TeamLookup.ActionButton
                  type="button"
                  variant="audit"
                  onClick={this.handleOpenAuditTrail}
                >
                  <Styled.TeamLookup.ActionTitle>Audit Trail</Styled.TeamLookup.ActionTitle>
                  <Styled.TeamLookup.ActionBlurb>
                    Every decision, action, and timestamp from the team this game.
                  </Styled.TeamLookup.ActionBlurb>
                </Styled.TeamLookup.ActionButton>
                <Styled.TeamLookup.ActionButton
                  type="button"
                  variant="report"
                  onClick={this.handleOpenTeamReport}
                >
                  <Styled.TeamLookup.ActionTitle>Team Report</Styled.TeamLookup.ActionTitle>
                  <Styled.TeamLookup.ActionBlurb>
                    Financial results and analysis of the team and their competitors.
                  </Styled.TeamLookup.ActionBlurb>
                </Styled.TeamLookup.ActionButton>
              </Styled.TeamLookup.ActionsGrid>

              <Styled.TeamLookup.Footer>
                <span>
                  <Styled.TeamLookup.HintKey>Esc</Styled.TeamLookup.HintKey>
                  back to search
                </span>
                <span>Opens in a new tab</span>
              </Styled.TeamLookup.Footer>
            </div>
          )}
        </Styled.TeamLookup.Card2>
      </Styled.TeamLookup.Backdrop>
    );
  };

  renderInsights = () => {
    const {
      insightsOpen,
      insightsStudentName,
      insightsData,
      insightsLoading,
      insightsError,
    } = this.state;
    if (!insightsOpen) return null;

    const studentName = insightsStudentName || '';
    const body = insightsData || {};
    const insight = body.insight_json || {};
    const weaknesses = insight.weaknesses || {};
    const weaknessSkills = Array.isArray(weaknesses.skills) ? weaknesses.skills : [];
    const weaknessSummary = weaknesses.summary || null;
    const overallSummary = insight.overall_summary || body.overall_summary || null;

    const personalize = text => this.personalizeText(text, studentName);

    // Flatten every evidence quote from every weakness skill into a
    // single list with the originating skill name attached.
    const evidenceRows = [];
    weaknessSkills.forEach(s => {
      const skill = sharedGetSkillName(s) || "this skill";
      const ev = s.evidence || s.evidences || s.quotes || s.feedback || null;
      const items = Array.isArray(ev)
        ? ev
        : (typeof ev === "string" && ev ? [ev] : []);
      items.forEach(item => {
        const text = typeof item === "string" ? item : (item && (item.text || item.quote)) || "";
        if (text) evidenceRows.push({ skill, quote: personalize(text) });
      });
    });

    return (
      <Styled.Insights.Backdrop onClick={this.closeInsights}>
        <Styled.Insights.Card onClick={e => e.stopPropagation()}>
          <Styled.Insights.CloseButton onClick={this.closeInsights} aria-label="Close">
            ×
          </Styled.Insights.CloseButton>

          <Styled.Insights.Header>
            <Styled.Insights.Eyebrow>Areas of Improvement</Styled.Insights.Eyebrow>
            <Styled.Insights.Title>{studentName}</Styled.Insights.Title>
            <Styled.Insights.Subtitle>
              Summary of peer-identified growth areas
            </Styled.Insights.Subtitle>
          </Styled.Insights.Header>

          <Styled.Insights.Body>
            {insightsLoading && <Styled.Insights.Empty>Loading insights…</Styled.Insights.Empty>}
            {insightsError && <Styled.Insights.Empty>{insightsError}</Styled.Insights.Empty>}

            {!insightsLoading && !insightsError && (
              <div>
                <Styled.Insights.SectionLabel>Weakness Summary</Styled.Insights.SectionLabel>
                <Styled.Insights.Paragraph>
                  {weaknessSummary
                    ? personalize(weaknessSummary)
                    : <Styled.Insights.Empty>No weakness summary provided.</Styled.Insights.Empty>}
                </Styled.Insights.Paragraph>

                <Styled.Insights.SectionLabel>Overall Summary</Styled.Insights.SectionLabel>
                <Styled.Insights.Paragraph>
                  {overallSummary
                    ? personalize(overallSummary)
                    : <Styled.Insights.Empty>No overall summary provided.</Styled.Insights.Empty>}
                </Styled.Insights.Paragraph>

                <Styled.Insights.SectionLabel>Evidence</Styled.Insights.SectionLabel>
                {evidenceRows.length > 0 ? (
                  <Styled.Insights.EvidenceList>
                    {evidenceRows.map((row, i) => (
                      <Styled.Insights.EvidenceItem key={`ins-ev-${i}`}>
                        “{row.quote}”
                        <Styled.Insights.EvidenceSkill>
                          {row.skill}
                        </Styled.Insights.EvidenceSkill>
                      </Styled.Insights.EvidenceItem>
                    ))}
                  </Styled.Insights.EvidenceList>
                ) : (
                  <Styled.Insights.Empty>No specific evidence reported.</Styled.Insights.Empty>
                )}
              </div>
            )}
          </Styled.Insights.Body>
        </Styled.Insights.Card>
      </Styled.Insights.Backdrop>
    );
  };

  renderCertificate = () => {
    const {
      certificateOpen,
      certificateUserId,
      certificateData,
      certificateLoading,
      certificateError,
      certificateCategory,
    } = this.state;
    if (!certificateOpen) return null;

    const studentName = this.state.certificateStudentName || '';
    // Merge the logged-in instructor's last name into the payload so
    // the popup sidebar reads "Professor <LastName>" instead of the
    // bare "Professor" fallback. Do the same for course_institution
    // using data.user.institution from general-info?position=instructor
    // (persisted via Auth.setUserData). The insights response doesn't
    // carry these for instructor viewers, so we inject them client-side
    // from Auth. Preserves whatever the BE returned if it ever starts
    // populating these fields.
    const authUser = Auth.getUser() || {};
    const instructorLastName = (certificateData && certificateData.instructor_last_name)
      || authUser.last_name
      || null;
    const courseInstitution = (certificateData && certificateData.course_institution)
      || authUser.institution
      || null;
    const data = certificateData
      ? Object.assign({}, certificateData, {
        instructor_last_name: instructorLastName,
        course_institution: courseInstitution,
      })
      : { instructor_last_name: instructorLastName, course_institution: courseInstitution };
    const teammateCount = Number(data.team_size) || 0;

    return (
      <SkillsBadgePopup
        open
        data={data}
        loading={certificateLoading}
        error={certificateError}
        studentName={studentName}
        teammateCount={teammateCount}
        categoryLabel={MVP_BADGE_LABEL[certificateCategory] || undefined}
        category={certificateCategory}
        onClose={this.closeCertificate}
        personalize={text => this.personalizeText(text, studentName)}
      />
    );
  };

  renderCollaborationCharts = (variant) => {
    const isLarge = variant === "large";
    const wvw = this.workedVeryWellTogether;
    const dnw = this.didNotWorkWellTogether;
    const ww = this.workedWellTogether;
    const hasDonutData = wvw + dnw + ww > 0;
    const donutData = hasDonutData
      ? [
        { name: "Worked very well together", value: wvw, fill: this.collabDonutColors.workedVeryWell },
        { name: "Worked well together", value: ww, fill: this.collabDonutColors.workedWell },
        { name: "Did not work well together", value: dnw, fill: this.collabDonutColors.didNotWork },
      ]
      : [{ name: "No data yet", value: 1, fill: "#e9ecef" }];

    const donutSize = isLarge ? 240 : 100;
    const teamCount = this.collabTeamCount;

    const bubbles = this.getCollabBubbles();
    const counts = bubbles.map(b => b.count);
    const minCount = counts.length ? Math.min.apply(null, counts) : 0;
    const maxCount = counts.length ? Math.max.apply(null, counts) : 0;
    const range = maxCount - minCount;
    const MIN_SIZE = isLarge ? 79 : 48;
    const MAX_SIZE = isLarge ? 143 : 79;

    const evalSurveys = this.findEvalSurveys();
    const showEvalToggle = !!(evalSurveys.initial && evalSurveys.final);
    const curSurveyId = this.state.currentSurvey;
    const evalToggle = showEvalToggle ? (
      <TeamDynamics.EvalToggleRow>
        Eval:
        <TeamDynamics.EvalToggleOption
          active={curSurveyId === evalSurveys.initial.id}
          onClick={e => {
            e.preventDefault();
            this.selectSurveyById(evalSurveys.initial.id);
          }}
        >
          Initial
        </TeamDynamics.EvalToggleOption>
        <TeamDynamics.EvalToggleSeparator>,</TeamDynamics.EvalToggleSeparator>
        <TeamDynamics.EvalToggleOption
          active={curSurveyId === evalSurveys.final.id}
          onClick={e => {
            e.preventDefault();
            this.selectSurveyById(evalSurveys.final.id);
          }}
        >
          Final
        </TeamDynamics.EvalToggleOption>
      </TeamDynamics.EvalToggleRow>
    ) : null;

    // Pie slice labels: rendered as HTML on top of the SVG so we get
    // reliable positioning and word-wrapping without fighting recharts'
    // label clipping. The label box is positioned by center point at
    // the slice's midAngle and pinned by whichever edge sits toward the
    // pie so long text grows outward.
    const RADIAN = Math.PI / 180;
    const labelOffset = isLarge ? 22 : 12;
    const labelFontSize = isLarge ? 12 : 10;
    const labelLineHeight = isLarge ? 15 : 13;
    const outerRadius = donutSize / 2 - 4;
    const total = donutData.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;
    const sliceLabels = hasDonutData
      ? (() => {
        let cursor = 90;
        return donutData.map((d, i) => {
          const sweep = (d.value / total) * 360;
          const midAngle = cursor - sweep / 2;
          cursor -= sweep;
          const r = outerRadius + labelOffset;
          const x = donutSize / 2 + r * Math.cos(-midAngle * RADIAN);
          const y = donutSize / 2 + r * Math.sin(-midAngle * RADIAN);
          const words = String(d.name || '').split(' ');
          const mid = Math.ceil(words.length / 2);
          const dxFromCenter = x - donutSize / 2;
          let align;
          if (Math.abs(dxFromCenter) < 8) align = 'center';
          else align = dxFromCenter > 0 ? 'left' : 'right';
          return {
            key: `lbl-${i}`,
            line1: words.slice(0, mid).join(' '),
            line2: words.slice(mid).join(' '),
            pct: Math.round(d.value),
            x, y,
            align,
            skip: !d.value,
          };
        });
      })()
      : [];

    return {
      donut: (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {evalToggle}
          <TeamDynamics.PopupChartFrame style={{ flex: 1, height: "auto", marginTop: 16 }}>
            <div style={{ position: 'relative', width: donutSize, height: donutSize }}>
              <PieChart
                width={donutSize}
                height={donutSize}
                margin={{ top: 0, left: 0, bottom: 0, right: 0 }}
              >
                <Tooltip formatter={value => `${Number(value).toFixed(1)}%`} separator=": " />
                <Pie
                  data={donutData}
                  cx={donutSize / 2}
                  cy={donutSize / 2}
                  innerRadius={isLarge ? 70 : 28}
                  outerRadius={outerRadius}
                  paddingAngle={1}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  labelLine={false}
                >
                  {donutData.map((d, i) => (
                    <Cell key={`donut-${i}`} fill={d.fill} />
                  ))}
                </Pie>
              </PieChart>
              {sliceLabels.map(lbl => lbl.skip ? null : (
                <div
                  key={lbl.key}
                  style={{
                    position: 'absolute',
                    left: lbl.align === 'left' ? lbl.x
                      : lbl.align === 'center' ? lbl.x : undefined,
                    right: lbl.align === 'right' ? (donutSize - lbl.x) : undefined,
                    top: lbl.y,
                    transform: lbl.align === 'center'
                      ? 'translate(-50%, -50%)'
                      : 'translateY(-50%)',
                    fontFamily: "'Mukta Vaani', sans-serif",
                    fontSize: labelFontSize,
                    lineHeight: `${labelLineHeight}px`,
                    fontWeight: 600,
                    color: 'rgba(0, 27, 59, 0.85)',
                    textAlign: lbl.align === 'right' ? 'right'
                      : lbl.align === 'center' ? 'center' : 'left',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    paddingLeft: lbl.align === 'left' ? 4 : 0,
                    paddingRight: lbl.align === 'right' ? 4 : 0,
                  }}
                >
                  <div>{lbl.line1}</div>
                  {lbl.line2 ? <div>{lbl.line2}</div> : null}
                  <div style={{ fontWeight: 700 }}>{lbl.pct}%</div>
                </div>
              ))}
            </div>
          </TeamDynamics.PopupChartFrame>
        </div>
      ),
      donutLegend: (
        <TeamDynamics.LegendList>
          <TeamDynamics.LegendItem>
            <TeamDynamics.LegendDot color={this.collabDonutColors.workedVeryWell} />
            <TeamDynamics.LegendName>Worked very well</TeamDynamics.LegendName>
            <TeamDynamics.LegendValue>{Math.round(wvw)}%</TeamDynamics.LegendValue>
          </TeamDynamics.LegendItem>
          <TeamDynamics.LegendItem>
            <TeamDynamics.LegendDot color={this.collabDonutColors.workedWell} />
            <TeamDynamics.LegendName>Worked well</TeamDynamics.LegendName>
            <TeamDynamics.LegendValue>{Math.round(ww)}%</TeamDynamics.LegendValue>
          </TeamDynamics.LegendItem>
          <TeamDynamics.LegendItem>
            <TeamDynamics.LegendDot color={this.collabDonutColors.didNotWork} />
            <TeamDynamics.LegendName>Didn't work well</TeamDynamics.LegendName>
            <TeamDynamics.LegendValue>{Math.round(dnw)}%</TeamDynamics.LegendValue>
          </TeamDynamics.LegendItem>
        </TeamDynamics.LegendList>
      ),
      bubbles: (() => {
        // Hide the entire bubble panel (section header, tabs, and
        // bubbles) when no peer has commented on any skill yet —
        // i.e. sum of total_student_count across both strengths and
        // weaknesses is 0. The donut on the left still renders.
        const totalSkillCount = (
          (this.state.collaborationStrengths || []).concat(this.state.collaborationWeaknesses || [])
        ).reduce((acc, s) => acc + (Number(s && s.total_student_count) || 0), 0);
        if (totalSkillCount === 0) return null;

        const tab = this.state.collaborationSkillsTab === "weaknesses"
          ? "weaknesses"
          : "strengths";
        const scheme = this.collabBubbleColors[tab];
        // Bubble row uses flex `space-evenly` for even visual gaps
        // between bubbles of any size. With N=4, the first bubble's
        // center sits at ~20% of the area — same approximation we
        // used previously — so the header left-align math holds.
        const headerLeftPad = `calc(20% - ${MAX_SIZE / 2}px)`;
        return (
          <TeamDynamics.BubblePanel>
            <TeamDynamics.BubbleSectionHeader style={{ paddingLeft: headerLeftPad }}>
              Students Demonstrating Each Skill
            </TeamDynamics.BubbleSectionHeader>
            <TeamDynamics.BubbleTabs style={{ paddingLeft: headerLeftPad }}>
              <TeamDynamics.BubbleTab
                active={tab === "strengths"}
                onClick={e => {
                  e.preventDefault();
                  this.setState({ collaborationSkillsTab: "strengths" });
                }}
              >
                Strengths
              </TeamDynamics.BubbleTab>
              <TeamDynamics.BubbleTab
                active={tab === "weaknesses"}
                onClick={e => {
                  e.preventDefault();
                  this.setState({ collaborationSkillsTab: "weaknesses" });
                }}
              >
                Areas to Improve
              </TeamDynamics.BubbleTab>
            </TeamDynamics.BubbleTabs>
            <TeamDynamics.PopupChartFrame style={!isLarge ? { height: 130 } : { height: 280 }}>
              <TeamDynamics.BubbleArea style={{ width: "100%", height: "100%", minHeight: 0 }}>
                {bubbles.map((b, i) => {
                  const ratio = range > 0 ? (b.count - minCount) / range : 0.5;
                  const size = Math.round(MIN_SIZE + (MAX_SIZE - MIN_SIZE) * ratio);
                  // Count font dynamic to fit inside the bubble. Digit
                  // width ≈ 0.55em; floor at 9px so it stays legible.
                  const digitCount = String(b.count).length || 1;
                  const widthBased = (size * 0.75) / digitCount / 0.55;
                  const heightBased = size / 2.4;
                  const countFont = Math.max(
                    9,
                    Math.min(24, Math.floor(Math.min(widthBased, heightBased)))
                  );
                  const tooltip = `${b.text} — ${b.count} student${b.count === 1 ? "" : "s"}`;
                  return (
                    <TeamDynamics.BubbleSlot key={`bubble-${variant}-${tab}-${i}`}>
                      <TeamDynamics.BubbleFrame height={MAX_SIZE}>
                        <TeamDynamics.Bubble
                          color={scheme.body}
                          borderColor={scheme.outline}
                          textColor={scheme.text}
                          size={size}
                          title={tooltip}
                        >
                          <TeamDynamics.BubbleLabel fontSize={countFont}>
                            {b.count}
                          </TeamDynamics.BubbleLabel>
                        </TeamDynamics.Bubble>
                      </TeamDynamics.BubbleFrame>
                      <TeamDynamics.BubbleCaption captionWidth={isLarge ? "110px" : "78px"}>
                        <TeamDynamics.BubbleCaptionName title={b.text}>
                          {b.text}
                        </TeamDynamics.BubbleCaptionName>
                      </TeamDynamics.BubbleCaption>
                    </TeamDynamics.BubbleSlot>
                  );
                })}
              </TeamDynamics.BubbleArea>
            </TeamDynamics.PopupChartFrame>
          </TeamDynamics.BubblePanel>
        );
      })(),
      bubbleLegend: null,
    };
  };

  // ---------------------------------------------------------------
  // Skills Certificate popup (Top Performer rows)
  // ---------------------------------------------------------------

  openCertificate = (userId, category, person) => {
    if (!userId) return;
    const name = person
      ? `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.username || ''
      : '';
    this.setState({
      certificateOpen: true,
      certificateUserId: Number(userId),
      certificateStudentName: name,
      certificateCategory: category || null,
      certificateData: null,
      certificateError: null,
      certificateLoading: true,
    });
    const apiUrl = process.env.REACT_APP_API_URL;
    const surveyId = this.state.currentSurvey;
    if (!surveyId) {
      this.setState({
        certificateLoading: false,
        certificateError: "No active survey",
      });
      return;
    }
    getRequest(`${apiUrl}v2/insights/student/${userId}/${surveyId}/`)
      .then(res => {
        this.setState({
          certificateData: (res && res.body) || null,
          certificateLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          certificateLoading: false,
          certificateError: "Unable to load badge",
        });
      });
  };

  closeCertificate = () => {
    this.setState({
      certificateOpen: false,
      certificateUserId: null,
      certificateStudentName: '',
      certificateCategory: null,
      certificateData: null,
      certificateError: null,
      certificateLoading: false,
    });
  };

  // ---------------------------------------------------------------
  // Not-registered-yet affordance under the Student Count KPI tile.
  // Backed by /v2/games/coursemembers/?not-registered&course=<id>.
  // Fetch once per fetchData so the count surfaces on initial render;
  // the modal reads from state when opened (no refetch).
  // ---------------------------------------------------------------
  fetchNotRegistered = () => {
    const courseId = Number(Auth.getCourseId());
    if (!courseId) return;
    this.setState({ notRegisteredLoading: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/games/coursemembers/?not-registered&course=${courseId}${sq ? `&${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        const results = Array.isArray(body.results) ? body.results : [];
        const count = Number(body.count) || results.length;
        this.setState({
          notRegisteredCount: count,
          notRegisteredList: results,
          notRegisteredLoading: false,
        });
      })
      .catch(() => {
        // Non-fatal: just skip the subtext if the endpoint is
        // unavailable.
        this.setState({
          notRegisteredCount: 0,
          notRegisteredList: [],
          notRegisteredLoading: false,
        });
      });
  };

  openNotRegisteredPopup = () => {
    if (this.state.notRegisteredCount === 0) return;
    this.setState({ notRegisteredPopupOpen: true, notRegisteredCopied: false });
  };

  closeNotRegisteredPopup = () => {
    this.setState({ notRegisteredPopupOpen: false, notRegisteredCopied: false });
  };

  copyNotRegisteredEmails = () => {
    const emails = (this.state.notRegisteredList || [])
      .map(s => (s && s.email ? String(s.email).trim() : ""))
      .filter(Boolean)
      .join(", ");
    if (!emails) return;
    const done = () => this.setState({ notRegisteredCopied: true });
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(emails).then(done).catch(() => {});
      return;
    }
    // Fallback for older browsers without the Clipboard API.
    try {
      const ta = document.createElement("textarea");
      ta.value = emails;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch (e) {
      // Silent fail; user can still manually copy from the modal body.
    }
  };

  // ---------------------------------------------------------------
  // Team Decisions Status KPI tile. Lists teams that haven't posted
  // their round decisions yet, excluding teams whose only missing
  // item is "negative_cash" (cash-only misses are a BE check the
  // instructor can't act on in this flow). Popup opens on click of
  // the subtext link.
  // ---------------------------------------------------------------
  fetchPendingDecisions = () => {
    const gameId = Number((this.props.game && this.props.game.id) || this.state.currentGame);
    // Decision Submission Status targets the CURRENT in-progress round.
    // props.currentRound is a round NUMBER already resolved by App.js
    // from data.user.game_round, independent of is_finished — same
    // pattern audit-trail uses. Avoids the this.roundNumber getter,
    // which only resolves against finished rounds.
    const roundNumber = Number(this.props.currentRound) || 0;
    if (!gameId || !roundNumber) {
      this.setState({ pendingDecisionsCount: 0, pendingDecisionsTeamIds: [] });
      return;
    }
    // Dedupe by (gameId, roundNumber). On initial mount, selectGame →
    // selectRound → componentDidUpdate can each schedule this fetch
    // with the same inputs. Skip when nothing changed; refetch when
    // the user moves to a different round or game.
    const key = `${gameId}|${roundNumber}`;
    if (this._pendingDecisionsKey === key) return;
    this._pendingDecisionsKey = key;
    this.setState({ pendingDecisionsLoading: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/games/games/${gameId}/rounds/${roundNumber}/team-decisions-not-submitted/${sq ? `?${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        const teams = Array.isArray(body.teams) ? body.teams : [];
        // Filter out teams whose only missing item is "negative_cash".
        const filtered = teams.filter(t => {
          const missing = Array.isArray(t && t.missing) ? t.missing : [];
          if (missing.length === 0) return false;
          if (missing.length === 1 && missing[0] === 'negative_cash') return false;
          return true;
        });
        const teamIds = filtered.map(t => Number(t.team_id)).filter(Boolean);
        this.setState({
          pendingDecisionsCount: teamIds.length,
          pendingDecisionsTeamIds: teamIds,
          pendingDecisionsLoading: false,
        });
      })
      .catch(() => {
        // Clear the dedup key on failure so the next scheduled call
        // actually retries instead of silently no-oping.
        this._pendingDecisionsKey = null;
        this.setState({
          pendingDecisionsCount: 0,
          pendingDecisionsTeamIds: [],
          pendingDecisionsLoading: false,
        });
      });
  };

  openPendingDecisionsPopup = () => {
    if (this.state.pendingDecisionsCount === 0) return;
    this.setState({ pendingDecisionsPopupOpen: true, pendingDecisionsFilter: '' });
  };

  closePendingDecisionsPopup = () => {
    this.setState({ pendingDecisionsPopupOpen: false, pendingDecisionsFilter: '' });
  };

  onPendingDecisionsFilterChange = (e) => {
    const value = e && e.target ? e.target.value : '';
    this.setState({ pendingDecisionsFilter: value });
  };

  renderPendingDecisionsPopup = () => {
    const {
      pendingDecisionsPopupOpen,
      pendingDecisionsTeamIds,
      pendingDecisionsCount,
      pendingDecisionsFilter,
    } = this.state;
    if (!pendingDecisionsPopupOpen) return null;
    const teams = Array.isArray(this.state.teams) ? this.state.teams : [];
    const resolveTeamName = (teamId) => {
      const match = teams.find(t => t && Number(t.id) === Number(teamId));
      return match && match.name != null ? String(match.name) : String(teamId);
    };
    const ids = Array.isArray(pendingDecisionsTeamIds) ? pendingDecisionsTeamIds : [];
    // Sort: all-numeric names first in ascending numeric order, then
    // alphabetic names A-Z. Mixed names (e.g. "Team 11A") sort with
    // the alphabetic group using locale-aware compare.
    const names = ids.map(id => resolveTeamName(id));
    const numeric = names.filter(n => /^\d+$/.test(n)).sort((a, b) => Number(a) - Number(b));
    const alphabetic = names
      .filter(n => !/^\d+$/.test(n))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const ordered = [...numeric, ...alphabetic];
    const filterRaw = pendingDecisionsFilter || '';
    const filterNorm = filterRaw.trim().toLowerCase();
    const filtered = filterNorm
      ? ordered
          .map((name, idx) => ({ name, idx }))
          .filter(({ name }) => `team ${name}`.toLowerCase().includes(filterNorm))
      : ordered.map((name, idx) => ({ name, idx }));
    const totalCount = ordered.length;
    const shownCount = filtered.length;
    const roundLabel = Number(this.roundNumber) || 0;

    return (
      <NotRegisteredBackdrop onClick={this.closePendingDecisionsPopup}>
        <SubStatusCard onClick={e => e.stopPropagation()}>
          <SubStatusHeader>
            <SubStatusHeaderText>
              <SubStatusEyebrow>Year {roundLabel} · Decisions</SubStatusEyebrow>
              <SubStatusTitle>Submission status</SubStatusTitle>
            </SubStatusHeaderText>
            <SubStatusClose
              type="button"
              onClick={this.closePendingDecisionsPopup}
              aria-label="Close"
            >
              ×
            </SubStatusClose>
          </SubStatusHeader>
          <SubStatusBanner>
            <SubStatusBannerIcon>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </SubStatusBannerIcon>
            <SubStatusBannerText>
              <SubStatusBannerTitle>
                {pendingDecisionsCount} team{pendingDecisionsCount === 1 ? '' : 's'} {pendingDecisionsCount === 1 ? "hasn't" : "haven't"} submitted
              </SubStatusBannerTitle>
              <SubStatusBannerSubtitle>
                Their decisions are still missing for this round.
              </SubStatusBannerSubtitle>
            </SubStatusBannerText>
          </SubStatusBanner>
          <SubStatusFilterWrap>
            <SubStatusFilterIcon>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </SubStatusFilterIcon>
            <SubStatusFilterInput
              type="text"
              placeholder="Filter teams..."
              value={filterRaw}
              onChange={this.onPendingDecisionsFilterChange}
            />
          </SubStatusFilterWrap>
          <SubStatusGrid>
            {totalCount === 0 && (
              <SubStatusEmpty>All teams have submitted this round.</SubStatusEmpty>
            )}
            {totalCount > 0 && shownCount === 0 && (
              <SubStatusEmpty>No teams match your filter.</SubStatusEmpty>
            )}
            {filtered.map(({ name, idx }) => (
              <SubStatusTeamCard key={`pd-${idx}-${name}`}>
                <SubStatusTeamBadge>{idx + 1}</SubStatusTeamBadge>
                <SubStatusTeamName>Team {name}</SubStatusTeamName>
                <SubStatusTeamPill>
                  <SubStatusTeamPillDot />
                  Pending
                </SubStatusTeamPill>
              </SubStatusTeamCard>
            ))}
          </SubStatusGrid>
          <SubStatusFooter>
            <SubStatusShowing>
              Showing {shownCount} of {totalCount}
            </SubStatusShowing>
            <SubStatusCloseButton type="button" onClick={this.closePendingDecisionsPopup}>
              Close
            </SubStatusCloseButton>
          </SubStatusFooter>
        </SubStatusCard>
      </NotRegisteredBackdrop>
    );
  };

  renderNotRegisteredPopup = () => {
    const { notRegisteredPopupOpen, notRegisteredList, notRegisteredCount, notRegisteredCopied } = this.state;
    if (!notRegisteredPopupOpen) return null;
    const list = Array.isArray(notRegisteredList) ? notRegisteredList : [];
    const emailCount = list.filter(s => s && s.email).length;
    return (
      <NotRegisteredBackdrop onClick={this.closeNotRegisteredPopup}>
        <NotRegisteredCard onClick={e => e.stopPropagation()}>
          <NotRegisteredHeader>
            <NotRegisteredHeaderText>
              <NotRegisteredTitle>Students not registered yet</NotRegisteredTitle>
              <NotRegisteredSubtitle>
                {notRegisteredCount} student{notRegisteredCount === 1 ? '' : 's'} haven't signed up for the course.
              </NotRegisteredSubtitle>
            </NotRegisteredHeaderText>
            <NotRegisteredClose
              type="button"
              onClick={this.closeNotRegisteredPopup}
              aria-label="Close"
            >
              ×
            </NotRegisteredClose>
          </NotRegisteredHeader>
          <NotRegisteredActions>
            <NotRegisteredCopyButton
              type="button"
              onClick={this.copyNotRegisteredEmails}
              disabled={emailCount === 0}
            >
              Copy all emails
            </NotRegisteredCopyButton>
            {notRegisteredCopied && (
              <NotRegisteredCopiedBadge>
                Copied {emailCount} email{emailCount === 1 ? '' : 's'} to clipboard
              </NotRegisteredCopiedBadge>
            )}
          </NotRegisteredActions>
          <NotRegisteredList>
            {list.length === 0 && (
              <NotRegisteredEmpty>No students in the not-registered list.</NotRegisteredEmpty>
            )}
            {list.map((s, idx) => {
              const first = (s && s.first_name) || '';
              const last = (s && s.last_name) || '';
              const fullName = `${first} ${last}`.trim() || `Student ${s && s.id ? s.id : idx + 1}`;
              return (
                <NotRegisteredRow key={`nr-${s && s.id ? s.id : idx}`}>
                  <NotRegisteredName>{fullName}</NotRegisteredName>
                  <NotRegisteredEmail>{(s && s.email) || '-'}</NotRegisteredEmail>
                </NotRegisteredRow>
              );
            })}
          </NotRegisteredList>
        </NotRegisteredCard>
      </NotRegisteredBackdrop>
    );
  };

  // ---------------------------------------------------------------
  // Areas of Improvement (Insights) popup — Bottom Performer rows
  // Same backend endpoint as the certificate but the popup focuses
  // on weaknesses.summary, overall_summary, and every weakness
  // evidence quote.
  // ---------------------------------------------------------------

  openInsights = (userId, person) => {
    if (!userId) return;
    const name = person
      ? `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.username || ''
      : '';
    this.setState({
      insightsOpen: true,
      insightsUserId: Number(userId),
      insightsStudentName: name,
      insightsData: null,
      insightsError: null,
      insightsLoading: true,
    });
    const apiUrl = process.env.REACT_APP_API_URL;
    const surveyId = this.state.currentSurvey;
    if (!surveyId) {
      this.setState({
        insightsLoading: false,
        insightsError: "No active survey",
      });
      return;
    }
    getRequest(`${apiUrl}v2/insights/student/${userId}/${surveyId}/`)
      .then(res => {
        this.setState({
          insightsData: (res && res.body) || null,
          insightsLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          insightsLoading: false,
          insightsError: "Unable to load insights",
        });
      });
  };

  closeInsights = () => {
    this.setState({
      insightsOpen: false,
      insightsUserId: null,
      insightsStudentName: '',
      insightsData: null,
      insightsError: null,
      insightsLoading: false,
    });
  };

  // ---------------------------------------------------------------
  // Team Lookup modal — KPI-row "Look up a team" card + ⌘K shortcut
  // ---------------------------------------------------------------

  // Cache window: refetch the team list if the modal opens more than
  // 60s after the last fetch (or if the dashboard round changed).
  TEAM_LOOKUP_CACHE_MS = 60 * 1000;

  openTeamLookup = (prefillTeamId) => {
    const now = Date.now();
    const currentRoundId = this.state.currentRound;
    const cacheStale = !this.state.teamLookupCacheTime
      || now - this.state.teamLookupCacheTime > this.TEAM_LOOKUP_CACHE_MS
      || this.state.teamLookupCacheRoundId !== currentRoundId;

    this.setState({
      teamLookupOpen: true,
      teamLookupStep: 1,
      teamLookupQuery: "",
      teamLookupHighlightIdx: 0,
      teamLookupSelectedTeam: null,
      teamLookupError: null,
    }, () => {
      if (cacheStale) this.fetchTeamLookupRows();
      if (prefillTeamId) {
        const team = (this.state.teamLookupRows || []).find(
          t => Number(t.id) === Number(prefillTeamId)
        );
        if (team) this.handleTeamLookupSelect(team);
      }
    });
  };

  closeTeamLookup = () => {
    this.setState({
      teamLookupOpen: false,
      teamLookupStep: 1,
      teamLookupQuery: "",
      teamLookupHighlightIdx: 0,
      teamLookupSelectedTeam: null,
      teamLookupError: null,
    });
  };

  // Display name for the lookup list: prepend "Team " unless the
  // backend's `name` already starts with "Team" (so we don't render
  // "Team Team Alpha" when the API returns "Team Alpha" directly).
  formatTeamName = (rawName, id) => {
    const name = rawName && String(rawName).trim();
    if (!name) return `Team ${id}`;
    return /^team\s/i.test(name) ? name : `Team ${name}`;
  };

  fetchTeamLookupRows = () => {
    // Prefer the dashboard's currently-selected round; fall back to
    // the latest round in props.rounds (largest round.number) so the
    // modal still surfaces team data on a fresh course where the
    // dashboard hasn't auto-selected a round yet.
    let roundId = this.state.currentRound;
    if (!roundId) {
      const rounds = this.state.rounds || this.props.rounds || [];
      const sorted = rounds.slice().sort((a, b) =>
        Number(b.number) - Number(a.number)
      );
      roundId = sorted.length > 0 ? sorted[0].id : null;
    }
    if (!roundId) {
      // No rounds at all — still let the modal show team names with
      // a "—" score, so the instructor can pick a team.
      const teamsProp = this.props.teams || this.state.teams || [];
      const rows = teamsProp
        .map(t => ({
          id: t.id,
          name: this.formatTeamName(t.name, t.id),
          memberCount: Array.isArray(t.users) ? t.users.length : null,
          score: null,
        }))
        .map((row, idx) => ({ ...row, rank: idx + 1 }));
      this.setState({
        teamLookupRows: rows,
        teamLookupLoading: false,
        teamLookupError: null,
        teamLookupCacheRoundId: null,
        teamLookupCacheTime: Date.now(),
      });
      return;
    }
    this.setState({ teamLookupLoading: true, teamLookupError: null });
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/games/teamgradings/overall/${roundId}/${sq ? `?${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        const apiTeams = Array.isArray(body.teams) ? body.teams : [];
        const teamsProp = this.props.teams || this.state.teams || [];

        // Join the score data with the team prop list (which carries
        // name, member count, competition etc.). Sort by score desc
        // and tag with a 1-based rank within the round.
        //
        // When a sections query is active, the gradings response is
        // already filtered to the instructor's section(s); restrict
        // the row list to just those team ids so the modal doesn't
        // show section-foreign teams with "---" scores. Lead
        // instructors (no sq) still see every team in props.teams,
        // with "---" where the team hasn't been graded yet.
        const sectionTeamIds = sq
          ? new Set(apiTeams.map(g => Number(g.team)))
          : null;
        const rows = teamsProp
          .filter(t => !sectionTeamIds || sectionTeamIds.has(Number(t.id)))
          .map(t => {
            const grading = apiTeams.find(g => Number(g.team) === Number(t.id));
            const score = grading
              ? Number(grading.current_weighted_value) || 0
              : null;
            return {
              id: t.id,
              name: this.formatTeamName(t.name, t.id),
              memberCount: Array.isArray(t.users) ? t.users.length : null,
              score,
            };
          })
          .sort((a, b) => {
            // Teams without a score sink to the bottom; otherwise desc.
            if (a.score == null && b.score == null) return 0;
            if (a.score == null) return 1;
            if (b.score == null) return -1;
            return b.score - a.score;
          })
          .map((row, idx) => ({ ...row, rank: idx + 1 }));

        this.setState({
          teamLookupRows: rows,
          teamLookupLoading: false,
          teamLookupCacheRoundId: roundId,
          teamLookupCacheTime: Date.now(),
        });
      })
      .catch(() => {
        this.setState({
          teamLookupLoading: false,
          teamLookupError: "Unable to load teams",
        });
      });
  };

  // Filter + debounce for the search input. We update the query
  // immediately for input responsiveness, and let the filtered list
  // be derived in render — keeps state simple and matches what
  // PureComponent expects.
  setTeamLookupQuery = (value) => {
    this.setState({ teamLookupQuery: value, teamLookupHighlightIdx: 0 });
  };

  getFilteredLookupRows = () => {
    const q = (this.state.teamLookupQuery || "").trim().toLowerCase();
    const rows = this.state.teamLookupRows || [];
    if (!q) return rows;
    return rows.filter(r => {
      const name = String(r.name || "").toLowerCase();
      const id = String(r.id || "");
      const rank = String(r.rank || "");
      return name.includes(q) || id.includes(q) || rank.includes(q);
    });
  };

  handleTeamLookupKeyDown = e => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (this.state.teamLookupStep === 2) {
        this.handleTeamLookupBack();
      } else {
        this.closeTeamLookup();
      }
      return;
    }
    if (this.state.teamLookupStep !== 1) return;
    const filtered = this.getFilteredLookupRows();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(this.state.teamLookupHighlightIdx + 1, filtered.length - 1);
      this.setState({ teamLookupHighlightIdx: Math.max(0, next) });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(this.state.teamLookupHighlightIdx - 1, 0);
      this.setState({ teamLookupHighlightIdx: next });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const team = filtered[this.state.teamLookupHighlightIdx];
      if (team) this.handleTeamLookupSelect(team);
    }
  };

  handleTeamLookupSelect = team => {
    this.setState({
      teamLookupSelectedTeam: team,
      teamLookupStep: 2,
    });
  };

  handleTeamLookupBack = () => {
    this.setState({
      teamLookupSelectedTeam: null,
      teamLookupStep: 1,
      teamLookupHighlightIdx: 0,
    });
  };

  // Build navigable URLs that carry the picked team across the
  // window.open boundary. _token / _userid / _courseid prime the
  // target tab's session so the page can authorize without forcing
  // the user to log in again.
  // Pick the round number to carry into the audit-trail / team-report
  // tabs. The leftbar "Year" selector is the actual source of truth:
  // it sets ?round=N in the URL, which App.js forwards down as
  // props.roundInUrl. Fall back to state.currentRound's round number,
  // then to the latest round on the game, then 0.
  getTeamLookupRoundNumber = () => {
    const urlRound = Number(this.props.roundInUrl);
    if (urlRound > 0) return urlRound;
    const stateRound = this.roundNumber;
    if (stateRound > 0) return stateRound;
    const rounds = this.state.rounds || this.props.rounds || [];
    if (rounds.length === 0) return 0;
    const latest = rounds.slice().sort(
      (a, b) => Number(b.number) - Number(a.number)
    )[0];
    return Number(latest && latest.number) || 0;
  };

  buildTeamLookupReportUrl = (team) => {
    if (!team) return null;
    const gameId = (this.props.game && this.props.game.id) || Auth.getGameId();
    const round = this.getTeamLookupRoundNumber();
    return `/analysis/team-reports?team_id=${team.id}&round=${round}&game_id=${gameId}&_token=${Auth.getToken()}&_userid=${Auth.getUserId()}&_courseid=${Auth.getCourseId()}`;
  };

  buildTeamLookupAuditUrl = (team) => {
    if (!team) return null;
    const gameId = (this.props.game && this.props.game.id) || Auth.getGameId();
    const round = this.getTeamLookupRoundNumber();
    return `/analysis/audit-trail?team_id=${team.id}&round=${round}&game_id=${gameId}&_token=${Auth.getToken()}&_userid=${Auth.getUserId()}&_courseid=${Auth.getCourseId()}`;
  };

  handleOpenTeamReport = () => {
    const url = this.buildTeamLookupReportUrl(this.state.teamLookupSelectedTeam);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  handleOpenAuditTrail = () => {
    const url = this.buildTeamLookupAuditUrl(this.state.teamLookupSelectedTeam);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  // Replace redaction tokens / generic placeholders with the real
  // student name in any peer-feedback or summary text. Covers:
  //   - "this student" / "the student" / "<student>"
  //   - "[person-1]" / "[person 2]" (LLM-generated redaction)
  //   - "[student-JF]" / "[student-anything]" (bracketed initials/aliases)
  //   - "Student-FB" / "Student JK" (unbracketed initials/aliases)
  personalizeText = (text, name) => {
    if (!name) return String(text || "");
    return String(text || "")
      .replace(/this student/gi, name)
      .replace(/the student/gi, name)
      .replace(/<student>/gi, name)
      .replace(/\[person[\s-]?\d+\]/gi, name)
      .replace(/\[student[^\]]*\]/gi, name)
      .replace(/\bstudent[\s-][a-z0-9]+\b/gi, name);
  };

  // Collaboration Insights bubble chart — fetches the per-survey
  // strengths/weaknesses skill bubbles. Same cache policy as
  // most-least-valuable: only fire if empty OR on Update click.
  fetchCollaborationSkills = (force = false) => {
    const {
      currentSurvey,
      allSurveys,
      collaborationStrengths,
      collaborationWeaknesses,
    } = this.state;
    const surveyId = currentSurvey
      || (Array.isArray(allSurveys) && allSurveys.length > 0 ? allSurveys[0].id : 0);
    if (!surveyId) return;
    if (!force
      && Array.isArray(collaborationStrengths) && collaborationStrengths.length > 0
      && Array.isArray(collaborationWeaknesses) && collaborationWeaknesses.length > 0) {
      return;
    }

    this.setState({ collaborationSkillsLoading: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const sq = this.sectionsQueryParam();
    getRequest(`${apiUrl}v2/insights/survey/${surveyId}/skills/${sq ? `?${sq}` : ""}`)
      .then(res => {
        const body = (res && res.body) || {};
        // Keep the full strength/weakness arrays now (was capped at 4
        // for the bubble chart). The radar pins to a fixed set of 4
        // axes — we look those up by name.
        const strengths = Array.isArray(body.strengths) ? body.strengths : [];
        const weaknesses = Array.isArray(body.weaknesses) ? body.weaknesses : [];
        // Denominators come from the backend so each axis can render
        // skill_count / total-responses. Tolerate a couple plausible
        // field names; fall back to a placeholder 400 until the
        // backend ships the real per-survey totals.
        const iLikeTotal = Number(
          body.i_like_total
          || body.total_i_like_responses
          || (body.response_totals && body.response_totals.like)
          || 400
        );
        const iWishTotal = Number(
          body.i_wish_total
          || body.total_i_wish_responses
          || (body.response_totals && body.response_totals.wish)
          || 400
        );
        this.setState({
          collaborationStrengths: strengths,
          collaborationWeaknesses: weaknesses,
          collaborationILikeTotal: iLikeTotal,
          collaborationIWishTotal: iWishTotal,
          collaborationSkillsLoading: false,
        });
      })
      .catch(() => {
        this.setState({ collaborationSkillsLoading: false });
      });
  };

  selectSurvey = value => {
    const { surveys } = this.state;

    if (surveys) {
      const survey = surveys.find(i => i.name === value);
      if (survey) {
        this.selectSurveyById(survey.id);
      }
    }
  };

  // Direct survey switch by id. Unlike selectSurvey() this doesn't
  // require the survey to be in the finished-only `state.surveys`
  // list, so the Eval toggle can switch to an in-progress survey.
  selectSurveyById = surveyId => {
    if (!surveyId) return;
    // Cancel any in-flight insights poll from the previous survey so
    // a late poll response can't clobber the newly-selected survey's
    // state.
    this._stopInsightsPoll();
    this.setState(
      {
        currentSurvey: surveyId,
        teamDynamicsDownloadRequest: null,
        teamDynamicsDownloadCount: 0,
        teamDynamicsDownloadFile: null,
        // Reset the spotlight existence gate so the loading state
        // shows while we re-check for the newly-picked survey.
        studentSpotlightHasInsights: null,
        studentSpotlightGenerateMessage: null,
        studentSpotlightGenerating: false,
        insightsJobId: null,
        insightsStatusUrl: null,
        myInstructorBadgeState: null,
        // Switching surveys is a fresh context — re-evaluate whether
        // the badges intro popup should show for the new survey.
        badgesIntroSuppressed: false,
        // Clear any class-wide badge recipient count from the prior
        // survey so a stale number doesn't briefly flash if the user
        // reopens the consent popup for the newly picked survey.
        classWideBadgeRecipientCount: null,
      },
      () => {
        this.fetchTeamScore();
        // Top/Bottom Performers and Collaboration Insights are both
        // keyed by survey — force a refresh so the displayed data
        // matches the chosen survey rather than serving cached rows.
        this.fetchMostLeastValuable(true);
        this.fetchCollaborationSkills(true);
        this.fetchSpotlightInsightsCheck();
        this.fetchInstructorBadgeState();
      }
    );
  };

  // Identify the "initial" and "final" surveys for the eval toggle.
  // Trust the stage tag only when it cleanly distinguishes exactly one
  // initial + one final; otherwise fall back to due-date order
  // (earliest end = Initial, latest end = Final). Legacy surveys
  // created before the FE started sending `stage` all default to
  // "initial" on the BE, which would otherwise collide.
  findEvalSurveys = () => {
    const surveys = this.state.allSurveys || [];
    if (surveys.length < 2) return { initial: null, final: null };

    const stageOf = s => String(s.stage_name || s.stage || "").toLowerCase();
    const isFinalStage = stage => stage === "second" || stage === "final";
    const initialTagged = surveys.filter(s => stageOf(s) === "initial");
    const finalTagged = surveys.filter(s => isFinalStage(stageOf(s)));

    if (
      initialTagged.length === 1
      && finalTagged.length === 1
      && initialTagged[0].id !== finalTagged[0].id
    ) {
      return { initial: initialTagged[0], final: finalTagged[0] };
    }

    const sorted = surveys.slice().sort((a, b) => {
      const aTime = new Date(a.end || a.start || a.start_date || 0).getTime();
      const bTime = new Date(b.end || b.start || b.start_date || 0).getTime();
      return aTime - bTime;
    });
    const initial = sorted[0];
    const final = sorted[sorted.length - 1];
    if (initial.id === final.id) return { initial: null, final: null };
    return { initial, final };
  };

  get surveys() {
    const { surveys } = this.state;

    return surveys ? surveys
      .filter(survey => this.isSurveyFinished(survey))
      .map(survey => survey.name) : [];
  }

  isSurveyFinished = survey => {
    const currentDate = moment.utc();

    return !(
      currentDate.isSameOrAfter(moment(survey.start)) &&
      currentDate.isSameOrBefore(moment(survey.end))
    );
  };

  isSurveyReallyFinished = survey => {
    const currentDate = moment.utc();

    return currentDate.isAfter(moment(survey.end));
  }

  fetchPerformanceMetrics = () => {
    const { currentRound, currentGame, rounds } = this.state;

    // Target round for the Team Scores KPI: honor the sidebar's
    // ?round=N first so the tile reflects what the instructor has
    // selected, not whichever round updateCurrentRound landed on
    // (which falls back to the latest closed round when roundInUrl
    // points at a round that isn't in the finished list). Fall back
    // to state.currentRound when roundInUrl is missing or doesn't
    // resolve to a round in the current game.
    const roundInUrl = Number(this.props.roundInUrl) || 0;
    const urlRoundRecord = (roundInUrl > 0 && Array.isArray(rounds))
      ? rounds.find(r => r.number === roundInUrl && r.game === currentGame)
      : null;
    const targetRoundId = urlRoundRecord ? urlRoundRecord.id : currentRound;
    if (!targetRoundId || targetRoundId <= 0) return;

    // Team Scores only reflects CLOSED rounds. For live or
    // not-yet-started rounds the teamgradings/overall endpoint
    // tends to carry over the prior round's grade as a placeholder
    // (same reason the Final Scores page hides live-round columns —
    // commit 04a7e7e0). Blank the state so the tile renders "—"
    // via formatScorePct and skip the request so the console
    // doesn't show a stale/misleading call.
    const targetRoundRecord = urlRoundRecord
      || (rounds || []).find(r => r.id === targetRoundId);
    if (!targetRoundRecord || targetRoundRecord.is_finished !== true) {
      this.setState({
        averageTeamGrade: 0,
        medianTeamGrade: 0,
        standardDeviation: 0,
        leaderboard: [],
        leaderboardLoading: false,
      });
      return;
    }

    // Use consolidated team-grading API
    const sq = this.sectionsQueryParam();
    getRequest(`${TEAM_GRADING_API}?round_id=${targetRoundId}&scope=dashboard${sq ? `&${sq}` : ""}`)
      .then(res => {
        const { overall_stats, gradings } = res.body;

        this.setState({
          averageTeamGrade: overall_stats ? overall_stats.average_team_grade : 0,
          medianTeamGrade: overall_stats ? overall_stats.median_team_grade : 0,
          standardDeviation: overall_stats ? overall_stats.standard_deviation : 0,
          leaderboard: gradings || [],
          leaderboardLoading: false,
        });
      })
      .catch(err => { });
  };

  setRoundValue = callback => {
    this.setState(
      {
        setRoundValue: callback
      },
      () => {
        this.updateCurrentRound();
      }
    );
  };

  setSurveyValue = callback => {
    this.setState(
      {
        setSurveyValue: callback
      },
      () => {
        const { setSurveyValue } = this.state;
        const surveys = this.surveys;

        if (surveys.length > 0) {
          setSurveyValue(surveys[surveys.length - 1]);
        }
      }
    );
  };

  setPlayerValue = callback => {
    this.setState({
      setPlayerValue: callback
    });
  };

  updateCurrentRound = () => {
    const { setRoundValue } = this.state;
    const rounds = this.rounds;

    // With the round-selector dropdown removed from the dashboard,
    // setRoundValue is no longer registered, so route through
    // selectRound directly to make sure currentRound + the dependent
    // fetches (leaderboard, performance, engagement) still fire on
    // initial load.
    if (typeof setRoundValue === "function") {
      if (rounds.length > 0) {
        setRoundValue(rounds[rounds.length - 1]);
      } else {
        setRoundValue(null);
      }
      return;
    }

    if (rounds.length === 0) return;

    // Honor the URL's ?round=N (LeftBar Year selector) when present
    // and it matches one of the finished rounds; otherwise fall back
    // to the latest finished round.
    const roundInUrl = Number(this.props.roundInUrl) || 0;
    const targetLabel = `Round ${roundInUrl}`;
    const preferred = roundInUrl > 0 && rounds.includes(targetLabel)
      ? targetLabel
      : rounds[rounds.length - 1];
    this.selectRound(preferred);
  };

  get roundNumber() {
    const { currentRound, rounds } = this.state;
    const round = rounds.find(i => i.id === currentRound);

    return round ? round.number : 0;
  }

  get totalTeams() {
    const { teamScore } = this.state;

    return teamScore ? get(teamScore.collaboration, "total_teams", 0) : "---";
  }

  // Builds the `sections=...` query param used on dashboard GETs so
  // that section-scoped instructors see only the data for their
  // assigned sections. Rules (per BE/product):
  //   course_members[0].covers_all_sections === true  → sections=all
  //   covers_all_sections === false AND user.sections has entries
  //     → sections=<id1>,<id2>,...
  //   covers_all_sections === false AND user.sections empty/missing
  //     → return "" (BE falls through to default scoping)
  //   covers_all_sections absent (field not yet shipped by BE)
  //     → fall through to the user.sections rule; returns "" when
  //       the user has no sections, preserving current behavior for
  //       instructors who pre-date the field.
  // Caller is responsible for prefixing with "?" or "&" depending on
  // whether the URL already has a query string.
  sectionsQueryParam = () => {
    // user.sections is the authoritative "sections this instructor can
    // see" list. If it's empty, the course either has no sections at
    // all OR the instructor has none assigned — in both cases the
    // dashboard GETs shouldn't carry a sections filter (BE will return
    // the course-wide aggregate, which is what we want).
    //
    // Lead instructors (covers_all_sections === true) also get no
    // sections query. BE's handling of `?sections=all` wasn't
    // returning course-wide aggregates on several endpoints (notably
    // /app/team-grading/), blanking the Team Scores tile and other
    // KPIs. Omitting the query lets BE's default (no filter) kick in,
    // which is semantically what a lead instructor wants anyway.
    const user = Auth.getUser() || {};
    const raw = Array.isArray(user.sections) ? user.sections : [];
    const ids = raw
      .map(s => (s && typeof s === "object" ? s.id : s))
      .filter(id => id != null && id !== "");
    if (ids.length === 0) return "";
    const cm = (this.props.courseMembers || [])[0];
    if (cm && cm.covers_all_sections === true) return "";
    return `sections=${ids.join(",")}`;
  };

  // True when the current instructor can run course-level actions
  // (Generate Insights, approve badges, etc.). Only lead instructors
  // assigned to every section in the course get this. Defaults to
  // true when BE hasn't shipped covers_all_sections yet — avoids
  // accidental lockouts during rollout; once BE ships the field,
  // section-scoped instructors get blocked automatically.
  canGenerateInsights = () => {
    // Who can generate insights:
    //
    // Lead instructor (position === "Instructor") passes under any of
    // three signals that they own the whole course:
    //   1. user.sections is empty — not scoped to any specific
    //      section, treated as full-course lead.
    //   2. course_members[0].covers_all_sections === true — the BE
    //      flag that explicitly flags them as covering every section.
    //   3. course_members[0].section_student_count ===
    //      course_members[0].student_count (and > 0) — their section
    //      scope reaches every student in the course, so they are
    //      effectively assigned to every section even if the
    //      covers_all_sections flag is stale (observed on walshcm4 /
    //      course 266 and Odom-Dull / course 265).
    //
    // Co-Instructor OR Instructor Assistant passes only when
    // course_members[0].covers_all_sections === true. We intentionally
    // do NOT infer their scope from student counts: the one-shot
    // generation shouldn't be fire-able by a section-scoped assistant
    // whose section happens to cover everyone on paper.
    const authUser = Auth.getUser() || {};
    const cm = (this.props.courseMembers || [])[0];

    if (authUser.position === "Instructor") {
      const hasSectionAssignments = Array.isArray(authUser.sections)
        && authUser.sections.length > 0;
      if (!hasSectionAssignments) return true;
      if (cm && cm.covers_all_sections === true) return true;
      const sectionCount = cm ? Number(cm.section_student_count) : 0;
      const totalCount = cm ? Number(cm.student_count) : 0;
      if (totalCount > 0 && sectionCount === totalCount) return true;
      return false;
    }

    const coversAll = cm && cm.covers_all_sections === true;
    if (Auth.isAssistantOrCoInstructor() && coversAll) return true;
    return false;
  };

  get workedVeryWellTogether() {
    const { teamScore } = this.state;

    return teamScore
      ? (get(teamScore.collaboration, "team_worked_well_together", []).length /
        get(teamScore.collaboration, "total_teams", 0)) *
      100
      : 0;
  }

  get workedVeryWellTogetherTeams() {
    const { teamScore } = this.state;

    return teamScore
      ? get(teamScore.collaboration, "team_worked_well_together", []).length : 0;
  }

  get didNotWorkWellTogether() {
    const { teamScore } = this.state;

    return teamScore
      ? (get(teamScore.collaboration, "team_did_not_worked_together", [])
        .length /
        get(teamScore.collaboration, "total_teams", 0)) *
      100
      : 0;
  }

  get didNotWorkWellTogetherTeams() {
    const { teamScore } = this.state;

    return teamScore
      ? get(teamScore.collaboration, "team_did_not_worked_together", []).length : 0;
  }

  get workedWellTogether() {
    const { teamScore } = this.state;

    return teamScore
      ? (get(teamScore.collaboration, "team_worked_together", []).length /
        get(teamScore.collaboration, "total_teams", 0)) *
      100
      : 0;
  }

  get workedWellTogetherTeams() {
    const { teamScore } = this.state;

    return teamScore
      ? get(teamScore.collaboration, "team_worked_together", []).length : 0;
  }

  teamDynamicsSelectTeam = value => {
    this.setState(
      {
        teamDynamicsCurrentTeam: value,
        teamDynamicsCurrentPlayer: 0,
        players: null,
        isPlayersLoading: true
      },
      () => {
        this.fetchPlayers();
      }
    );
  };

  popupSelectPlayer = value => {
    this.setState(
      {
        teamDynamicsCurrentPlayer: parseInt(value, 10),
        reports: {},
        userScoreReports: {},
        isReportsLoading: true
      },
      () => {
        const { surveys, teamDynamicsCurrentPlayer } = this.state;
        const finishedSurveys = surveys ? surveys.filter(survey =>
          this.isSurveyFinished(survey)
        ) : [];

        this.loadReports(finishedSurveys, teamDynamicsCurrentPlayer);
        this.loadUserScoreReports(finishedSurveys, teamDynamicsCurrentPlayer);
      }
    );
  };

  loadUserScoreReports = (surveys, receiver_id) => {
    const requests = surveys.filter(s => this.isSurveyReallyFinished(s)).map(({ id: survey_id }) =>
      getRequest(
        `${process.env.REACT_APP_API_URL}v2/forms/surveys/${survey_id}/user-score/${receiver_id}/`,
        (res) => res.status === 200 || res.status === 404,
      )
    );
    const stages = surveys.reduce(
      (result, { id: survey_id, stage }) => ({
        ...result,
        [survey_id]: stage,
      }),
      {}
    );

    Promise.all(requests)
      .then(responses => {
        const getSurveyIdFromUrl = url => {
          const tmp = url.split('/');

          return tmp[tmp.length - 4];
        };

        return responses.map(res => ({
          body: res.body,
          stage: stages[getSurveyIdFromUrl(res.req.url)],
        }));
      })
      .then(responses => {
        const userScoreReports = responses.reduce(
          (result, { body, stage }) => body.detail && body.detail === 'Not found.' ? result : ({
            ...result,
            [stage]: body,
          }),
          {}
        );

        this.setState({
          userScoreReports,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  loadReports = (surveys, receiver_id) => {
    const requests = surveys.filter(s => this.isSurveyReallyFinished(s)).map(({ id: survey_id }) =>
      getRequest(
        `${process.env.REACT_APP_API_URL}v2/forms/score/${receiver_id}/${survey_id}/`,
        (res) => res.status === 200 || res.status === 404,
      )
    );
    const stages = surveys.reduce(
      (result, { id: survey_id, stage }) => ({
        ...result,
        [survey_id]: stage
      }),
      {}
    );

    Promise.all(requests)
      .then(responses => {
        const getSurveyIdFromUrl = url => {
          const tmp = url.split("/");

          return tmp[tmp.length - 2];
        };

        const prepareResponseBody = res => {
          const body = res.body;
          if (!body.average_user_score) {
            body.average_user_score = 0;
          }

          if (!body.team_average_score) {
            body.team_average_score = 0;
          }

          if (!body.team_median_score) {
            body.team_median_score = 0;
          }

          return body;
        };

        return responses.map(res => ({
          body: prepareResponseBody(res),
          stage: stages[getSurveyIdFromUrl(res.req.url)]
        }));
      })
      .then(responses => {
        const reports = responses.reduce(
          (result, { body, stage }) => body.detail && body.detail === 'Not found.' ? result : ({
            ...result,
            [stage]: body
          }),
          {}
        );

        this.setState({
          reports,
          isReportsLoading: false
        });
      })
      .catch(err => {
        this.setState({
          isReportsLoading: false
        });
      });
  };

  loadReport = async ({ id: survey_id, stage }, receiver_id) => {
    const { body: report } = await getRequest(
      `${process.env.REACT_APP_API_URL}v2/forms/score/${receiver_id}/${survey_id}/`
    );
    const { reports } = this.state;

    this.setState({
      reports: {
        ...reports,
        [stage]: report
      }
    });
  };

  fetchPlayers = () => {
    const { teamDynamicsCurrentTeam } = this.state;

    getRequest(`${process.env.REACT_APP_API_URL}teams/${teamDynamicsCurrentTeam}/players/`)
      .then(res => {
        this.setState({
          players: res.body
            .filter(player => "user" in player)
            .map(player => player.user),
          isPlayersLoading: false
        });
      })
      .catch(err => { });
  };

  changeSortOrder = key => {
    const { sortKey, sortOrder } = this.state;

    let newSortKey = sortKey;
    let newSortOrder = sortOrder;

    if (newSortKey !== key) {
      newSortKey = key;
      newSortOrder = "asc";
    } else {
      newSortOrder = newSortOrder === "asc" ? "desc" : "asc";
    }

    this.setState({
      sortKey: newSortKey,
      sortOrder: newSortOrder
    });
  };

  get leaderboardCSVHeaders() {
    return [
      { label: "Team name", key: "team_name" },
      { label: "Round", key: "round_number" },
      { label: "Round Score", key: "round_score" },
      { label: "Cumulative Score", key: "cumulative_score" },
      { label: "Rank", key: "rank" },
    ];
  }

  get leaderboardCSVRows() {
    const { leaderboard, teams } = this.state;

    return leaderboard.map(row => ({
      team_name: row.team_name || this.getTeamName(teams, Number(row.team)),
      round_number: Number(row.round_number),
      round_score: Number(row.current_weighted_value),
      cumulative_score: Number(row.weighted_value),
      rank: row.ranking,
    }));
  }

  getClassRankings = items => {
    const values = Object.keys(groupBy(items, "weighted_value"));
    const result = {};
    let counter = 0;
    values.forEach(value => {
      counter++;
      result[value] = counter;
    });

    return result;
  };

  getTeamName = (teams, team_id) => {
    const team = teams.find(i => i.id === team_id);

    return team ? team.name : "";
  };

  getDetailsLink = (team_id, round_number, game_id) => {
    return `/analysis/team-reports?team_id=${team_id}&round=${round_number}&game_id=${game_id}&_token=${Auth.getToken()}&_userid=${Auth.getUserId()}&_courseid=${Auth.getCourseId()}`;
  };

  get leaderboardData() {
    const {
      leaderboard,
      currentRound,
      sortKey,
      sortOrder,
      teams,
      rounds,
    } = this.state;
    const round = rounds ? rounds.find(i => i.id === currentRound) : null;
    const roundNumber = round ? round.number : 0;

    const items = leaderboard
      ? leaderboard.filter(i => i.round === currentRound)
      : [];
    const rankings = this.getClassRankings(items);
    const results = items.map(({ weighted_value, ranking, team, team_name, game }, i) => ({
      key: i,
      classRanking: rankings[weighted_value],
      classRankingSort: rankings[weighted_value],
      teamName: team_name || this.getTeamName(teams, team),
      teamNameSort: team_name || this.getTeamName(teams, team),
      competitionRanking: ranking,
      teamPerformance: `${(weighted_value * 100).toFixed(2)}%`,
      teamPerformanceSort: weighted_value,
      detailsLink: this.getDetailsLink(team, roundNumber, game)
    }));


    return orderBy(results, [`${sortKey}Sort`], [sortOrder]);
  }

  get currentDate() {
    const [year, month, day] = new Date()
      .toISOString()
      .split('T')[0]
      .split('-');

    return `${year}-${day}-${month}`;
  }

  // ---------- KPI helpers for the top-row tiles ----------
  // Team Scores tile shows the mean and median of teams' weighted
  // performance for the selected round, computed off leaderboardData.
  // Team Count tile counts the unique team ids on the current round's
  // leaderboard, falling back to the full teams list when no rows
  // have come in yet. Student Count tile reads the consolidated
  // course_members.student_count sum already computed in fetchData.

  get teamScoreStats() {
    const rows = this.leaderboardData || [];
    const values = rows
      .map(r => Number(r.teamPerformanceSort))
      .filter(v => Number.isFinite(v));
    if (values.length === 0) return { mean: null, median: null };

    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const sorted = values.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];

    return { mean, median };
  }

  formatScorePct = value => {
    if (value == null || !Number.isFinite(value)) return "—";
    return `${(value * 100).toFixed(2)}%`;
  };

  get teamCount() {
    // Section-scoped instructors (covers_all_sections === false) read
    // the section-scoped team total from props.sectionTeamsCount (BE
    // top-level data.section_teams_count on general-info). Lead
    // instructors + legacy payloads fall through to the course-wide
    // team-id count below.
    //
    // Fallback: when the course has NO sections at all (user.sections
    // empty), use the course-wide count regardless of
    // covers_all_sections — section_teams_count is meaningless (always
    // 0) if the course was never sectioned.
    const cm = (this.props.courseMembers || [])[0];
    const user = Auth.getUser() || {};
    const hasAnySections = Array.isArray(user.sections) && user.sections.length > 0;
    if (cm && cm.covers_all_sections === false && hasAnySections) {
      return Number(this.props.sectionTeamsCount) || 0;
    }
    // Primary: count teams off the picked game object. games[].teams
    // is BE's authoritative team-id list for this game and is present
    // on general-info responses that include the games plural array
    // (notably IAS scope=dashboard payloads). Fixes the first-mount
    // drift where state.teams could be stale for users whose login
    // stored a different session game_id than the picker landed on.
    //
    // Fallback: count distinct ids in state.teams. Needed when the
    // general-info response omitted the games plural array — INS
    // scope=dashboard currently returns only data.game (singular) +
    // the top-level data.teams array, so state.games is empty and
    // the lookup above misses.
    const { games, currentGame, teams } = this.state;
    const game = (games || []).find(g => Number(g.id) === Number(currentGame));
    if (game && Array.isArray(game.teams)) {
      return new Set(game.teams).size;
    }
    const ids = new Set((teams || [])
      .map(t => t && t.id)
      .filter(id => id != null));
    return ids.size;
  }

  hideError = () => {
    this.setState({
      showError: false
    });
  };

  onStudentStatMouseEnter = () => {
    this.setState({
      showStudentStatError: true
    });
  };

  onStudentStatMouseLeave = () => {
    this.setState({
      showStudentStatError: false
    });
  };

  // ---------- Performance Feedback list helpers ----------
  // Categories map to fields on gamesTargets[0]: a non-zero target value
  // means the category is relevant for the round and its pages are
  // candidates for the top-5 list. Net Loss is gated by RoE/RoA/EPS per
  // product rules.
  isPerfFeedbackCategoryRelevant = (category) => {
    const t = (this.state.gamesTargets || [])[0];
    if (!t) return false;
    const nz = (v) => Number(v || 0) !== 0;
    switch (category) {
      case "marketShare":  return nz(t.market_share);
      case "revenues":     return nz(t.net_revenues);
      case "grossMargins": return nz(t.gross_margin);
      case "sharePrice":   return nz(t.stock_price);
      // "Falling Short on Profit…" theme. All four member pages
      // (net-loss + roe/roa/eps enough-profits) share this category
      // and surface when ANY of roe / roa / earnings_per_share is a
      // target for the round (value > 0). If none of the three are
      // targets, the theme is excluded from the dashboard list.
      case "profitTrio":   return nz(t.roe) || nz(t.roa) || nz(t.earnings_per_share);
      default:             return false;
    }
  };

  // Builds the Performance Feedback list. Pipeline:
  //   1. Index API feedback rows by `type` so each catalog item can
  //      grab its teams + id in O(1).
  //   2. Filter the catalog to categories that are relevant for the
  //      round's targets.
  //   3. Group remaining items by their resolved display name (catalog
  //      pages that share a theme like "Cross-Functional Challenges"
  //      collapse into one list row whose team set is the union of
  //      teams across all member pages' types and whose tie-break id
  //      is the lowest matching id).
  //   4. Drop themes with zero teams.
  //   5. If a team-name search has been submitted, resolve it to a
  //      team id and either:
  //        - keep only themes whose team set includes that team, or
  //        - return { notFound: true, rows: [] } when the name doesn't
  //          match any team — the render branch shows an inline error.
  //   6. Sort by PERF_FEEDBACK_DISPLAY_ORDER (fixed priority list),
  //      then take the top 5. teamCount is no longer used for
  //      ranking — only for the "X out of Y teams" hover prefix.
  //
  // displayName / hoverMessage on a catalog item may be a function of
  // the round number (Share Price changes label by round).
  get topPerformanceFeedbackItems() {
    const { performanceFeedbacksGame, perfFeedbackSearchSubmitted, teams } = this.state;
    const empty = { notFound: false, rows: [] };

    if (!Array.isArray(performanceFeedbacksGame) || performanceFeedbacksGame.length === 0) {
      return empty;
    }

    const round = this.roundNumber || 0;
    const resolve = (v) => (typeof v === "function" ? v(round) : v);

    const byType = {};
    performanceFeedbacksGame.forEach(f => {
      if (f && f.type) byType[f.type] = f;
    });

    // Aggregation rules:
    //   - Within a single catalog entry (which represents one page),
    //     we dedupe teams across all of its types — a team that
    //     appears in both "Auto overproduced" and "Mobile overproduced"
    //     under the shared Overproduced page counts as 1 for that page.
    //   - Across catalog entries that share a displayName (theme), we
    //     SUM those per-page counts — a team that appears on page A
    //     AND page B under the same theme counts as 2.
    //   - The `teamIdsForSearch` set is the union of all teams across
    //     all member pages and is used only by the team-name search
    //     filter to decide whether to keep the theme.
    const groups = new Map();
    PERF_FEEDBACK_ITEMS
      .filter(item => this.isPerfFeedbackCategoryRelevant(item.category))
      .forEach(item => {
        const displayName = resolve(item.displayName);
        const hoverMessage = resolve(item.hoverMessage);
        if (!groups.has(displayName)) {
          groups.set(displayName, {
            displayName,
            hoverMessage,
            path: item.path,
            teamCount: 0,
            teamIdsForSearch: new Set(),
            lowestId: Infinity,
            // Paths within this group that ended up with at least one
            // team. Used below to apply the per-group path priority
            // override so the row links to the page that actually
            // has data when multiple options exist.
            pathsWithTeams: new Set(),
          });
        }
        const group = groups.get(displayName);
        const types = Array.isArray(item.type) ? item.type : [item.type];

        const teamsForThisPage = new Set();
        types.forEach(t => {
          const f = byType[t];
          if (f && Array.isArray(f.teams)) {
            f.teams.forEach(id => {
              teamsForThisPage.add(id);
              group.teamIdsForSearch.add(id);
            });
          }
          if (f && f.id != null && Number(f.id) < group.lowestId) {
            group.lowestId = Number(f.id);
          }
        });

        if (teamsForThisPage.size > 0) {
          group.pathsWithTeams.add(item.path);
        }
        group.teamCount += teamsForThisPage.size;
      });

    // Apply per-group path priority overrides: if any priority path
    // for this group's displayName ended up with data, use the first
    // one in the priority list. Falls back to the catalog default
    // when no priority match is found.
    groups.forEach(g => {
      const priority = PERF_FEEDBACK_PATH_PRIORITY[g.displayName];
      if (!Array.isArray(priority)) return;
      const winner = priority.find(p => g.pathsWithTeams.has(p));
      if (winner) g.path = winner;
    });

    // Total teams for the round, used to fill the "<X> out of <Y>
    // teams" prefix substituted into every hover message. Aligned
    // with the dashboard's Team Count KPI tile.
    const totalTeams = this.teamCount;

    let rows = Array.from(groups.values())
      .map(g => {
        // Unique team count for the theme (union of all member pages'
        // team ids), distinct from `teamCount` which is the sum of
        // per-page counts used for ranking. The hover-text prefix
        // uses this unique count so a team is not double-counted in
        // the explanation sentence.
        const uniqueTeamCount = g.teamIdsForSearch.size;
        const hoverMessage = typeof g.hoverMessage === "string"
          ? g.hoverMessage.replace(
              /These teams/g,
              `${uniqueTeamCount} out of ${totalTeams} teams`,
            )
          : g.hoverMessage;
        return {
          displayName: g.displayName,
          hoverMessage,
          path: g.path,
          teamCount: g.teamCount,
          teamIds: g.teamIdsForSearch,
          feedbackId: g.lowestId === Infinity ? null : g.lowestId,
        };
      })
      .filter(r => r.teamCount > 0);

    // Team-name search (submitted via Enter). Case-insensitive exact
    // match against team.name; whitespace-trimmed. An unmatched name
    // returns notFound:true so the render can show the inline error.
    const searchTerm = (perfFeedbackSearchSubmitted || "").trim().toLowerCase();
    if (searchTerm) {
      const team = (teams || []).find(t =>
        t && typeof t.name === "string" && t.name.trim().toLowerCase() === searchTerm
      );
      if (!team) {
        return { notFound: true, rows: [] };
      }
      const teamId = Number(team.id);
      rows = rows.filter(r =>
        r.teamIds && (r.teamIds.has(teamId) || r.teamIds.has(team.id))
      );
    }

    // Fixed display order: groups appear in PERF_FEEDBACK_DISPLAY_ORDER
    // sequence whenever they have any teams. Anything not in the list
    // (shouldn't happen unless the catalog and order list drift) falls
    // to the bottom by index = list length.
    const orderIndex = displayName => {
      const idx = PERF_FEEDBACK_DISPLAY_ORDER.indexOf(displayName);
      return idx === -1 ? PERF_FEEDBACK_DISPLAY_ORDER.length : idx;
    };
    rows.sort((a, b) => orderIndex(a.displayName) - orderIndex(b.displayName));

    return { notFound: false, rows: rows.slice(0, 5) };
  }

  render() {
    const {
      averageTeamGrade,
      medianTeamGrade,
      standardDeviation,
      teams,
      showTeamDynamicsModal,
      teamDynamicsModalTitle,
      teamDynamicsTeamsList,
      teamDynamicsCurrentTeam,
      players,
      teamDynamicsCurrentPlayer,
      setPlayerValue,
      reports,
      userScoreReports,
      surveys,
      allSurveys,
      surveysError,
      currentGame,
      currentSurvey,
      countOfCourseMembers,
      currentRound,
      isReportsLoading,
      isPlayersLoading,
      teamDynamicsHideDropdowns,
      showStudentStats,
      studentStatsLoading,
      engagementDownloadRequest,
      teamDynamicsDownloadRequest,
      performanceDownloadRequest,
      showPerformanceDowload,
      hasUserScores,
      showError,
      rounds,
      showStudentStatError,
      processResultsFailed
    } = this.state;
    const { isMenuExpanded } = this.props;

    if (this.isLoading === true) {
      return (
        <Styled.Body>
          <Loader type="ball-scale-ripple" active />
        </Styled.Body>
      );
    }

    const roundNumber = this.roundNumber;

    const emptySurveys = !allSurveys || allSurveys.length === 0;
    const activeSurvey = allSurveys ? allSurveys.find(i => i.id === currentSurvey) : null;
    const activeSurveyFinished = activeSurvey ? this.isSurveyFinished(activeSurvey) : false;

    const survey = surveys ? surveys.find(i => i.id === currentSurvey) : null;
    const currentSurveyName = survey ? survey.name : null;

    const selectedRound = rounds.find(round => round.id === currentRound);
    const selectedRoundFinished = selectedRound ? selectedRound.is_finished : false;

    return (
      <Styled.Body>
        {/* "Survey Ready to Review" nudge. Scoped to the dashboard so
            it only fires when the instructor is actually on the surface
            where insights get generated — avoids remounting (and re-
            firing surveys/?course=) on every unrelated navigation, which
            was the behavior when this lived in App.js. Reads surveys +
            self course_member summary from Dashboard props (already on
            general-info / survey-data) instead of re-fetching. */}
        <InstructorSurveyReadyNotification
          allSurveys={allSurveys}
          courseMembers={this.props.courseMembers}
        />
        {showError && (
          <ErrorAlert
            title="Round is still live!"
            message="Round has not ended yet."
            onHide={this.hideError}
          />
        )}
        {showStudentStats && (
          <StudentStatsPopup
            show={showStudentStats}
            onHide={this.hideStudentStats}
            loading={studentStatsLoading}
            setLoadingState={this.setStudentsStatsLoading}
            roundId={currentRound}
            roundNumber={roundNumber}
            gameId={currentGame}
            survey={survey}
            reports={reports ? reports : {}}
            selectPlayer={this.popupSelectPlayer}
          />
        )}
        <TeamDynamicsPopup
          show={showTeamDynamicsModal}
          onHide={this.hideTeamDynamicsModal}
          teams={teamDynamicsTeamsList}
          title={teamDynamicsModalTitle}
          currentTeam={teamDynamicsCurrentTeam}
          selectTeam={this.teamDynamicsSelectTeam}
          players={players ? players : []}
          currentPlayer={teamDynamicsCurrentPlayer}
          selectPlayer={this.popupSelectPlayer}
          setPlayerValue={setPlayerValue}
          setPlayerValueCallback={this.setPlayerValue}
          reports={reports ? reports : {}}
          userScoreReports={userScoreReports ? userScoreReports : {}}
          surveys={surveys ? surveys : []}
          allTeams={teams}
          isSurveyFinished={this.isSurveyFinished}
          currentSurvey={currentSurvey}
          isReportsLoading={isReportsLoading}
          isPlayersLoading={isPlayersLoading}
          hideDropdowns={teamDynamicsHideDropdowns}
        />
        <Styled.Title>
          Snapshot of Class Performance
        </Styled.Title>
        {/* Round selector + View Student Stats row removed per dashboard
            revamp. Selector handlers (selectRound, selectSurvey,
            showStudentStats) remain in this file so the controls can
            be re-mounted later if needed. */}

        {/* Top KPI row — mirrors the design of the KPI tiles on
            decisions/product-marketing. Three equal blocks: Team
            Scores (mean + median), Team Count, Student Count. */}
        {(() => {
          // Team Scores KPI reads BE-provided aggregates from
          // /app/team-grading/ (`overall_stats.average_team_grade` /
          // `.median_team_grade`) via state. This lets the dashboard
          // scope drop `gradings[]` from the payload without breaking
          // the tile. `teamScoreStats` (which iterates gradings rows)
          // is kept for potential reuse elsewhere.
          const meanFromState = this.state.averageTeamGrade;
          const medianFromState = this.state.medianTeamGrade;
          const mean = Number.isFinite(meanFromState) && meanFromState !== 0
            ? meanFromState : null;
          const median = Number.isFinite(medianFromState) && medianFromState !== 0
            ? medianFromState : null;
          const studentCount = this.state.countOfCourseMembers || 0;
          const tCount = this.teamCount;
          const hasFinishedRound = (rounds || []).some(r => r.is_finished === true);
          const teamScoresTip = (rounds || []).length === 0
            ? "Team scores will be available after the first round ends."
            : "Team scores will be available once the current round ends.";
          return (
            <Styled.KpiRow>
              <Styled.KpiCard>
                <Styled.KpiHeader>
                  <Styled.KpiLabel>Team Scores</Styled.KpiLabel>
                  {hasFinishedRound ? (
                    <Styled.KpiLink href="/instructor/final-scores">
                      view details
                    </Styled.KpiLink>
                  ) : (
                    <Styled.KpiLinkDisabledWrap>
                      <Styled.KpiLinkDisabled>view details</Styled.KpiLinkDisabled>
                      <Styled.KpiLinkDisabledTip>{teamScoresTip}</Styled.KpiLinkDisabledTip>
                    </Styled.KpiLinkDisabledWrap>
                  )}
                </Styled.KpiHeader>
                <Styled.KpiValueRow>
                  <Styled.KpiMicroPair>
                    <Styled.KpiMicroLabel>Mean</Styled.KpiMicroLabel>
                    <Styled.KpiMicroValue>{this.formatScorePct(mean)}</Styled.KpiMicroValue>
                  </Styled.KpiMicroPair>
                  <Styled.KpiMicroPair>
                    <Styled.KpiMicroLabel>Median</Styled.KpiMicroLabel>
                    <Styled.KpiMicroValue>{this.formatScorePct(median)}</Styled.KpiMicroValue>
                  </Styled.KpiMicroPair>
                </Styled.KpiValueRow>
              </Styled.KpiCard>
              <Styled.KpiCard>
                <Styled.KpiHeader>
                  <Styled.KpiLabel>Class Roster</Styled.KpiLabel>
                </Styled.KpiHeader>
                <Styled.KpiValueRow>
                  <Styled.KpiMicroPair>
                    <Styled.KpiMicroLabel>Students</Styled.KpiMicroLabel>
                    <Styled.KpiMicroValue>{studentCount}</Styled.KpiMicroValue>
                  </Styled.KpiMicroPair>
                  <Styled.KpiMicroPair>
                    <Styled.KpiMicroLabel>Teams</Styled.KpiMicroLabel>
                    <Styled.KpiMicroValue>{tCount}</Styled.KpiMicroValue>
                  </Styled.KpiMicroPair>
                </Styled.KpiValueRow>
                {this.state.notRegisteredCount > 0 && (
                  <NotRegisteredLink
                    type="button"
                    onClick={this.openNotRegisteredPopup}
                    aria-label={`View ${this.state.notRegisteredCount} students not yet registered`}
                  >
                    {this.state.notRegisteredCount} student{this.state.notRegisteredCount === 1 ? '' : 's'} not registered yet
                  </NotRegisteredLink>
                )}
              </Styled.KpiCard>
              <Styled.KpiCard>
                <Styled.KpiHeader>
                  <Styled.KpiLabel>Decision Submission Status</Styled.KpiLabel>
                </Styled.KpiHeader>
                <Styled.KpiValue>{this.state.pendingDecisionsCount}</Styled.KpiValue>
                {this.state.pendingDecisionsCount > 0 && (() => {
                  // Resolve whether the round the KPI is counting against
                  // is still active or has already ended. props.currentRound
                  // is the round NUMBER (the in-progress round the dashboard
                  // tracks); look it up in rounds[] to read its is_finished
                  // flag. If the round can't be resolved (edge case on first
                  // mount), default to the "round active" copy — matches the
                  // historical behavior where the live-round KPI drove this
                  // card.
                  const liveRoundNumber = Number(this.props.currentRound) || 0;
                  const liveRound = (rounds || []).find(
                    r => r && Number(r.number) === liveRoundNumber
                  );
                  const roundEnded = !!(liveRound && liveRound.is_finished);
                  const count = this.state.pendingDecisionsCount;
                  const isOne = count === 1;
                  const text = roundEnded
                    ? (isOne
                      ? `${count} team did not submit their decisions.`
                      : `${count} teams did not submit their decisions.`)
                    : (isOne
                      ? `${count} team has not submitted their decisions yet.`
                      : `${count} teams have not submitted their decisions yet.`);
                  return (
                    <NotRegisteredLink
                      type="button"
                      onClick={this.openPendingDecisionsPopup}
                      aria-label={text}
                    >
                      {text}
                    </NotRegisteredLink>
                  );
                })()}
              </Styled.KpiCard>
              {(() => {
                const isMac = typeof window !== "undefined"
                  && /Mac|iPhone|iPad/.test(window.navigator.platform);
                const cmdLabel = isMac ? "⌘" : "Ctrl";
                return (
                  <Styled.TeamLookup.Card
                    type="button"
                    aria-keyshortcuts="Meta+K Control+K"
                    aria-label="Look up a team"
                    onClick={() => this.openTeamLookup()}
                  >
                    <Styled.TeamLookup.CardHeader>
                      <Styled.TeamLookup.CardIcon aria-hidden>🔍</Styled.TeamLookup.CardIcon>
                      Look up a team
                      <Styled.TeamLookup.CardShortcut>{cmdLabel} K</Styled.TeamLookup.CardShortcut>
                    </Styled.TeamLookup.CardHeader>
                    <Styled.TeamLookup.CardInputMock>Search by team name…</Styled.TeamLookup.CardInputMock>
                    <Styled.TeamLookup.CardHelper>
                      Access audit trail or team report
                    </Styled.TeamLookup.CardHelper>
                  </Styled.TeamLookup.Card>
                );
              })()}
            </Styled.KpiRow>
          );
        })()}

        <Styled.Flex isMenuExpanded={isMenuExpanded}>
          <div>
            <Styled.Row>
              {/* Engagement block repurposed as "Critical Thinking
                  Insights". Title + subtitle in the card header;
                  download link hidden but the handler stays imported
                  so we can re-enable later. Body is a search box plus
                  a 2-column split — numbered list on the left, hover
                  detail on the right. */}
              <Styled.Block isMenuExpanded={isMenuExpanded} fullWidth>
                <Styled.BlockTitle>
                  <Styled.PerfFeedbackTitleWrap>
                    <Styled.PerfFeedbackTitle>Critical Thinking Insights</Styled.PerfFeedbackTitle>
                    <Styled.PerfFeedbackSubtitle>Why Teams Didn't Hit Their Targets</Styled.PerfFeedbackSubtitle>
                  </Styled.PerfFeedbackTitleWrap>
                  <Styled.PerfFeedbackSearch
                    type="text"
                    placeholder="Search by team name"
                    value={this.state.perfFeedbackSearch}
                    onChange={e => this.setState({ perfFeedbackSearch: e.target.value })}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        this.setState({
                          perfFeedbackSearchSubmitted: this.state.perfFeedbackSearch,
                        });
                      }
                    }}
                  />
                  {false && (
                    <Styled.DownloadLink href="#" onClick={this.downloadEngagementData}>
                      {engagementDownloadRequest ? 'Download Data' : 'Download Request'}
                    </Styled.DownloadLink>
                  )}
                </Styled.BlockTitle>
                <Styled.BlockContent>
                  <Styled.PerfFeedbackBody>
                    {(() => {
                      // Top list driven by performancefeedbacks/game
                      // API + the round's targets. Each row is a theme
                      // (catalog pages with the same displayName
                      // collapse into one) and links to its primary
                      // page. Hovering or clicking a row sets it as
                      // the active row and its long-form explanation
                      // text appears in the right column.
                      //
                      // When the instructor submits a team-name
                      // search, the getter either returns
                      // notFound:true (no team matches) — we render
                      // an inline error in that case — or filters
                      // rows to themes whose team set includes that
                      // specific team.
                      const { notFound, rows: top } = this.topPerformanceFeedbackItems;
                      if (notFound) {
                        return (
                          <Styled.PerfFeedbackNotFound>
                            This team does not exist. Please double check team name.
                          </Styled.PerfFeedbackNotFound>
                        );
                      }
                      const isSearching = (this.state.perfFeedbackSearchSubmitted || "").trim().length > 0;
                      const total = 5;
                      const filler = isSearching ? 0 : Math.max(0, total - top.length);
                      const activeRound = this.roundNumber;
                      const activeIndex = this.state.perfFeedbackActiveIndex;
                      const activeRow = (activeIndex != null && top[activeIndex]) || null;
                      return (
                        <Styled.PerfFeedbackSplit>
                          <Styled.PerfFeedbackList>
                            {top.map((row, idx) => {
                              const to = activeRound
                                ? `${row.path}?round=${activeRound}`
                                : row.path;
                              const isActive = activeIndex === idx;
                              return (
                                <Styled.PerfFeedbackItem
                                  key={`pf-${idx}`}
                                  onMouseEnter={() => this.setState({ perfFeedbackActiveIndex: idx })}
                                  onClick={() => this.setState({ perfFeedbackActiveIndex: idx })}
                                  style={{
                                    cursor: "pointer",
                                    background: isActive ? "rgba(92, 145, 245, 0.06)" : undefined,
                                    borderRadius: 4,
                                  }}
                                >
                                  <Styled.PerfFeedbackNumber>{idx + 1}.</Styled.PerfFeedbackNumber>
                                  <Link to={to}>{row.displayName}</Link>
                                </Styled.PerfFeedbackItem>
                              );
                            })}
                            {Array.from({ length: filler }, (_, i) => {
                              const n = top.length + i + 1;
                              return (
                                <Styled.PerfFeedbackItem key={`pf-empty-${n}`}>
                                  <Styled.PerfFeedbackNumber>{n}.</Styled.PerfFeedbackNumber>
                                  <span>—</span>
                                </Styled.PerfFeedbackItem>
                              );
                            })}
                          </Styled.PerfFeedbackList>
                          <Styled.PerfFeedbackDetailColumn>
                            {activeRow && activeRow.hoverMessage ? (
                              <div>{activeRow.hoverMessage}</div>
                            ) : (
                              <Styled.PerfFeedbackDetailPlaceholder>
                                Hover or click an item to see why teams hit this issue.
                              </Styled.PerfFeedbackDetailPlaceholder>
                            )}
                          </Styled.PerfFeedbackDetailColumn>
                        </Styled.PerfFeedbackSplit>
                      );
                    })()}
                  </Styled.PerfFeedbackBody>
                </Styled.BlockContent>
              </Styled.Block>
              {/* Performance block removed per dashboard revamp.
                  Performance popup logic and download wiring remain
                  intact higher up in this file so they can be re-mounted
                  later if needed. */}
              {!surveysError && (
                <Styled.Block
                  isMenuExpanded={isMenuExpanded}
                  fullWidth
                  style={{ height: 270 }}
                >
                  <Styled.BlockTitle>
                    <Styled.PerfFeedbackTitleWrap>
                      <Styled.PerfFeedbackTitle>
                        Collaboration Insights
                      </Styled.PerfFeedbackTitle>
                    </Styled.PerfFeedbackTitleWrap>
                    <Styled.PerfFeedbackSearch
                      type="text"
                      placeholder="Search by student or team name…"
                      value={this.state.collabInsightsSearch}
                      onChange={e => this.setState({ collabInsightsSearch: e.target.value })}
                    />
                  </Styled.BlockTitle>
                  <Styled.BlockContent>
                    {/* Survey name is shown in the hover popup's
                        subtitle and implied by the Eval toggle —
                        no need for the absolute-positioned hint
                        here, where it overlaps the donut. */}
                    {!emptySurveys && (() => {
                      const compact = this.renderCollaborationCharts();
                      return (
                        <TeamDynamics.ChartHoverArea
                          onMouseEnter={this.openCollabPopup}
                          onMouseLeave={this.scheduleCollabPopupClose}
                        >
                          <div style={{ flex: '0 0 220px' }}>{compact.donut}</div>
                          <div style={{ flex: '1 1 auto', minWidth: 0 }}>{compact.bubbles}</div>
                        </TeamDynamics.ChartHoverArea>
                      );
                    })()}
                    {emptySurveys && (
                      <TeamDynamics.LabelCentered>
                        There are no peer evaluations created for this course.
                      </TeamDynamics.LabelCentered>
                    )}
                  </Styled.BlockContent>
                </Styled.Block>
              )}
            </Styled.Row>
          </div>

          {/* "Student Spotlight" — Most Valuable + Least Valuable
              teammates side-by-side inside a single card, with a
              subtle vertical divider between the two lists and one
              shared Update link in the header. The wrapping <div>
              is the second child of Styled.Flex, which targets it
              with `display: grid; grid-template-columns: 1fr 1fr;`
              to size the Spotlight at the right width — without
              this wrapper, SpotlightBlock lands directly inside
              Styled.Flex and collapses to a narrow strip. */}
          <div>
            <Styled.SpotlightBlock>
              <Styled.SpotlightHeader>
                <Styled.SpotlightHeaderLeft>
                  <span>Student Spotlight</span>
                  {(() => {
                    const evalSurveys = this.findEvalSurveys();
                    if (!evalSurveys.initial || !evalSurveys.final) return null;
                    const cur = this.state.currentSurvey;
                    return (
                      <TeamDynamics.EvalToggleRow>
                        Eval:
                        <TeamDynamics.EvalToggleOption
                          active={cur === evalSurveys.initial.id}
                          onClick={e => {
                            e.preventDefault();
                            this.selectSurveyById(evalSurveys.initial.id);
                          }}
                        >
                          Initial
                        </TeamDynamics.EvalToggleOption>
                        <TeamDynamics.EvalToggleSeparator>,</TeamDynamics.EvalToggleSeparator>
                        <TeamDynamics.EvalToggleOption
                          active={cur === evalSurveys.final.id}
                          onClick={e => {
                            e.preventDefault();
                            this.selectSurveyById(evalSurveys.final.id);
                          }}
                        >
                          Final
                        </TeamDynamics.EvalToggleOption>
                      </TeamDynamics.EvalToggleRow>
                    );
                  })()}
                </Styled.SpotlightHeaderLeft>
                {this.renderSpotlightTeamFilter()}
              </Styled.SpotlightHeader>

              {/* Description sits between the header and the body
                  only when the Generate Insights button is visible
                  (survey exists, no insights yet, no generate notice).
                  Helps the instructor understand what the lists will
                  show once insights are generated. */}
              {(this.state.allSurveys || []).length > 0
                && !this.state.studentSpotlightGenerateMessage
                && this.state.studentSpotlightHasInsights === false && (
                  <Styled.SpotlightDescription>
                    This section displays a list of the most and least valuable teammates in the simulation exercise.
                  </Styled.SpotlightDescription>
                )}

              {(() => {
                // Spotlight body state machine.
                //   A. No survey on this game           → "needs peer eval" message
                //   B. Generate-insights message active → blue notice banner
                //   C. Survey + no insights generated   → Generate Insights button
                //        (active iff the survey end date has passed)
                //   D. Survey + insights confirmed OR  → render the two lists
                //        still checking
                const surveys = this.state.allSurveys || [];
                const hasInsights = this.state.studentSpotlightHasInsights;
                const generateMsg = this.state.studentSpotlightGenerateMessage;
                const currentSurveyObj = surveys.find(
                  s => s.id === this.state.currentSurvey
                );
                const closed = currentSurveyObj
                  ? this.isSurveyReallyFinished(currentSurveyObj)
                  : false;

                if (surveys.length === 0) {
                  return (
                    <Styled.SpotlightColumns>
                      <Styled.SpotlightEmptyState>
                        <Styled.SpotlightEmptyMessage>
                          A peer evaluation needs to be created and completed before student insights can be generated.
                        </Styled.SpotlightEmptyMessage>
                      </Styled.SpotlightEmptyState>
                    </Styled.SpotlightColumns>
                  );
                }

                if (generateMsg) {
                  return (
                    <Styled.SpotlightColumns>
                      <Styled.SpotlightEmptyState>
                        <Styled.SpotlightNotice>{generateMsg}</Styled.SpotlightNotice>
                      </Styled.SpotlightEmptyState>
                    </Styled.SpotlightColumns>
                  );
                }

                if (hasInsights === false) {
                  const canGenerate = this.canGenerateInsights();
                  let tooltip = "";
                  if (!canGenerate) {
                    tooltip = "Only lead instructors assigned to every section in this course can generate insights.";
                  } else if (!closed) {
                    tooltip = "Peer evaluation has not closed yet";
                  }
                  return (
                    <Styled.SpotlightColumns>
                      <Styled.SpotlightEmptyState>
                        <Styled.SpotlightEmptyMessage>
                          Insights haven't been generated for this peer evaluation yet.
                        </Styled.SpotlightEmptyMessage>
                        <Styled.SpotlightGenerateButton
                          type="button"
                          disabled={!canGenerate || !closed || this.state.studentSpotlightGenerating}
                          title={tooltip}
                          onClick={this.openGenerateConfirm}
                        >
                          {this.state.studentSpotlightGenerating ? "Starting…" : "Generate Insights"}
                        </Styled.SpotlightGenerateButton>
                      </Styled.SpotlightEmptyState>
                    </Styled.SpotlightColumns>
                  );
                }

                // Default: render the two lists (insights present, or
                // check still in flight). Sort by team_name asc first,
                // then apply the instructor's team filter. Row numbers
                // reflect the visible list so filtering reindexes 1..N.
                const mvpsVisible = this.filterSpotlightByTeam(
                  this.sortSpotlightByTeam(this.state.mostValuablePlayers || [])
                );
                const lvpsVisible = this.filterSpotlightByTeam(
                  this.sortSpotlightByTeam(this.state.leastValuablePlayers || [])
                );
                return (
                  <Styled.SpotlightColumns>
                    {/* Left: Most Valuable Teammate */}
                    <Styled.SpotlightColumn>
                      <Styled.SpotlightSubheader>Most Valuable Teammate</Styled.SpotlightSubheader>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(0, 27, 59, 0.08)',
                        gap: 12,
                      }}>
                        <Styled.PerformersHeaderRow style={{ border: 'none', flex: '1 1 auto', padding: '6px 2px' }}>
                          <Styled.PerformersCell>No.</Styled.PerformersCell>
                          <Styled.PerformersCell>Team</Styled.PerformersCell>
                          <Styled.PerformersCell>Student</Styled.PerformersCell>
                        </Styled.PerformersHeaderRow>
                        {this.renderShareWithStudentsButton()}
                      </div>
                      <Styled.PerformersScroll>
                        {mvpsVisible.map((mvp, idx) => {
                          const name = this.trimSpotlightName(mvp.first_name, mvp.last_name)
                            || mvp.username
                            || (mvp.user_id != null ? `User ${mvp.user_id}` : '');
                          const linkLabel = MVP_LINK_LABEL[mvp.category] || 'Most Valuable';
                          return (
                            <Styled.PerformersBodyRow key={`top-${mvp.user_id || idx}`}>
                              <Styled.PerformersCell>{idx + 1}</Styled.PerformersCell>
                              <Styled.PerformersCell title={mvp.team_name || ''}>
                                <Styled.SpotlightTeamBadge>
                                  {this.spotlightTeamBadge(mvp.team_name)}
                                </Styled.SpotlightTeamBadge>
                              </Styled.PerformersCell>
                              <Styled.PerformersCell title={name}>
                                {name}
                                {mvp.user_id != null && (
                                  <Styled.SkillsCertificateLink
                                    href="#"
                                    onClick={e => {
                                      e.preventDefault();
                                      this.openCertificate(mvp.user_id, mvp.category, mvp);
                                    }}
                                  >
                                    ({linkLabel})
                                  </Styled.SkillsCertificateLink>
                                )}
                              </Styled.PerformersCell>
                            </Styled.PerformersBodyRow>
                          );
                        })}
                        {mvpsVisible.length === 0 &&
                          [1, 2, 3, 4, 5].map(n => (
                            <Styled.PerformersBodyRow key={`top-empty-${n}`}>
                              <Styled.PerformersCell>{n}</Styled.PerformersCell>
                              <Styled.PerformersCell>—</Styled.PerformersCell>
                              <Styled.PerformersCell>—</Styled.PerformersCell>
                            </Styled.PerformersBodyRow>
                          ))}
                      </Styled.PerformersScroll>
                    </Styled.SpotlightColumn>

                    <Styled.SpotlightDivider aria-hidden />

                    {/* Right: Least Valuable Teammate. */}
                    <Styled.SpotlightColumn>
                      <Styled.SpotlightSubheader>Least Valuable Teammate</Styled.SpotlightSubheader>
                      <Styled.PerformersHeaderRow>
                        <Styled.PerformersCell>No.</Styled.PerformersCell>
                        <Styled.PerformersCell>Team</Styled.PerformersCell>
                        <Styled.PerformersCell>Student</Styled.PerformersCell>
                      </Styled.PerformersHeaderRow>
                      <Styled.PerformersScroll>
                        {lvpsVisible.map((lvp, idx) => {
                          const name = this.trimSpotlightName(lvp.first_name, lvp.last_name)
                            || lvp.username
                            || (lvp.user_id != null ? `User ${lvp.user_id}` : '');
                          return (
                            <Styled.PerformersBodyRow key={`bottom-${lvp.user_id || idx}`}>
                              <Styled.PerformersCell>{idx + 1}</Styled.PerformersCell>
                              <Styled.PerformersCell title={lvp.team_name || ''}>
                                <Styled.SpotlightTeamBadge>
                                  {this.spotlightTeamBadge(lvp.team_name)}
                                </Styled.SpotlightTeamBadge>
                              </Styled.PerformersCell>
                              <Styled.PerformersCell title={name}>
                                {name}
                                {lvp.user_id != null && (
                                  <Styled.SkillsCertificateLink
                                    href="#"
                                    onClick={e => {
                                      e.preventDefault();
                                      this.openInsights(lvp.user_id, lvp);
                                    }}
                                  >
                                    (Insights)
                                  </Styled.SkillsCertificateLink>
                                )}
                              </Styled.PerformersCell>
                            </Styled.PerformersBodyRow>
                          );
                        })}
                        {lvpsVisible.length === 0 &&
                          [1, 2, 3, 4, 5].map(n => (
                            <Styled.PerformersBodyRow key={`bottom-empty-${n}`}>
                              <Styled.PerformersCell>{n}</Styled.PerformersCell>
                              <Styled.PerformersCell>—</Styled.PerformersCell>
                              <Styled.PerformersCell>—</Styled.PerformersCell>
                            </Styled.PerformersBodyRow>
                          ))}
                      </Styled.PerformersScroll>
                    </Styled.SpotlightColumn>
                  </Styled.SpotlightColumns>
                );
              })()}
            </Styled.SpotlightBlock>
          </div>
        </Styled.Flex>

        {/* Hover-triggered full-size view of the Collaboration
            Insights charts. Mounts only while the user's mouse is
            over the in-block chart area, so it doesn't pop up when
            interacting with Update/Search/Download in the header. */}
        {this.state.collabPopupOpen && !emptySurveys && (() => {
          const large = this.renderCollaborationCharts("large");
          return (
            <TeamDynamics.PopupBackdrop>
              <TeamDynamics.PopupCard
                onMouseEnter={this.openCollabPopup}
                onMouseLeave={this.scheduleCollabPopupClose}
              >
                <TeamDynamics.PopupTitle>Class Collaboration Overview</TeamDynamics.PopupTitle>
                <TeamDynamics.PopupSubtitle>
                  {currentSurveyName || "Peer evaluation"}
                  {this.collabTeamCount > 0 && ` · ${this.collabTeamCount} teams`}
                </TeamDynamics.PopupSubtitle>
                <TeamDynamics.PopupColumns>
                  <TeamDynamics.PopupSection>
                    <TeamDynamics.PopupSectionLabel>Team Dynamics</TeamDynamics.PopupSectionLabel>
                    <TeamDynamics.PopupSectionHint>How teams worked together</TeamDynamics.PopupSectionHint>
                    {large.donut}
                    {large.donutLegend}
                  </TeamDynamics.PopupSection>
                  <TeamDynamics.PopupSection>
                    <TeamDynamics.PopupSectionLabel>Skills Demonstrated</TeamDynamics.PopupSectionLabel>
                    <TeamDynamics.PopupSectionHint>Bubble size = % of students showing each skill</TeamDynamics.PopupSectionHint>
                    {large.bubbles}
                  </TeamDynamics.PopupSection>
                </TeamDynamics.PopupColumns>
              </TeamDynamics.PopupCard>
            </TeamDynamics.PopupBackdrop>
          );
        })()}

        {this.renderCertificate()}
        {this.renderInsights()}
        {this.renderTeamLookup()}
        {this.renderNotRegisteredPopup()}
        {this.renderPendingDecisionsPopup()}
        {this.renderBadgesIntro()}
        {this.renderBadgesTourBackdrop()}
        {this.renderBadgesTour()}
        {this.renderBadgesReview()}
        {this.renderBadgesEmailPreview()}

        {this.state.studentSpotlightConfirmOpen && (
          <Styled.SpotlightConfirm.Backdrop onClick={this.closeGenerateConfirm}>
            <Styled.SpotlightConfirm.Card onClick={e => e.stopPropagation()}>
              <Styled.SpotlightConfirm.Title>Generate insights now?</Styled.SpotlightConfirm.Title>
              <Styled.SpotlightConfirm.Body>
                Insights can only be generated once per peer evaluation. You won't be able to regenerate them, even if you re-open the evaluation for students.
              </Styled.SpotlightConfirm.Body>
              <Styled.SpotlightConfirm.Actions>
                <Styled.SpotlightConfirm.CancelButton
                  type="button"
                  onClick={this.closeGenerateConfirm}
                >
                  Cancel
                </Styled.SpotlightConfirm.CancelButton>
                <Styled.SpotlightConfirm.ConfirmButton
                  type="button"
                  onClick={this.confirmGenerateSpotlightInsights}
                >
                  Generate Insights
                </Styled.SpotlightConfirm.ConfirmButton>
              </Styled.SpotlightConfirm.Actions>
            </Styled.SpotlightConfirm.Card>
          </Styled.SpotlightConfirm.Backdrop>
        )}
      </Styled.Body>
    );
  }
}

export default Dashboard;



// WEBPACK FOOTER //
// ./src/instructor/containers/dashboard.js